import {
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  BookOpen,
  Check,
  ChevronDown,
  Copy,
  Database,
  Globe2,
  GraduationCap,
  LayoutGrid,
  LoaderCircle,
  MessageCircle,
  Send,
  Shield,
  Sparkles,
  Target,
  Trophy,
  Users,
  Wallet,
  X,
} from 'lucide-react';

type Billing = 'monthly' | 'annual';
type StorePlatform = 'ios' | 'android' | 'general';
type SubmitState = 'idle' | 'sending' | 'success' | 'error';

const WAITLIST_TO_EMAIL = 'info@klynd.trade';

const WaitlistContext = createContext<(source?: StorePlatform) => void>(() => {});

function useWaitlistModal() {
  return useContext(WaitlistContext);
}

const PageContext = createContext<string>('home');

function usePageKind() {
  return useContext(PageContext);
}

function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function buildMailtoFallback(email: string, source: StorePlatform) {
  const subject = encodeURIComponent(`Klynd waitlist signup: ${email}`);
  const body = encodeURIComponent(
    `Please add me to the Klynd waitlist.\n\nEmail: ${email}\nSource: ${source}\nPage: ${window.location.href}`
  );
  return `mailto:${WAITLIST_TO_EMAIL}?subject=${subject}&body=${body}`;
}

class WaitlistError extends Error {
  mailto: string;
  constructor(message: string, mailto: string) {
    super(message);
    this.mailto = mailto;
  }
}

async function submitWaitlistEmail(email: string, source: StorePlatform) {
  const cleanEmail = email.trim().toLowerCase();
  if (!validateEmail(cleanEmail)) {
    throw new Error('Enter a valid email address.');
  }

  const mailto = buildMailtoFallback(cleanEmail, source);

  const payload = {
    email: cleanEmail,
    source,
    page: window.location.href,
  };

  let lastError: unknown;

  try {
    const res = await fetch('/api/waitlist', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const contentType = res.headers.get('content-type') ?? '';
    const data = contentType.includes('application/json')
      ? await res.json().catch(() => null)
      : null;

    if (!contentType.includes('application/json')) {
      throw new Error('The signup service is not available in this preview. Tap below to email us directly.');
    }

    if (res.ok && data?.ok === true) {
      return;
    }

    lastError = new Error(data?.error || `Waitlist request failed (${res.status})`);
  } catch (error) {
    lastError = error;
  }

  const message =
    lastError instanceof TypeError
      ? "We couldn't reach our signup service. Tap below to email us directly."
      : lastError instanceof Error
        ? lastError.message
        : 'Could not add you right now. Try again in a moment.';

  throw new WaitlistError(message, mailto);
}

// ─── AI conversation data ──────────────────────────────────────────────────────

type AiWidget =
  | {
      type: 'curve';
      label: string;
      value: string;
      tone: 'green' | 'red' | 'blue';
      path: string;
      area: string;
    }
  | { type: 'bars'; label: string; value: string; items: { label: string; pct: number; color: 'green' | 'red' | 'blue' | 'dim' }[] }
  | { type: 'cones'; label: string; value: string; bands: { d: string; opacity: number }[]; line: string }
  | { type: 'heatmap'; label: string; value: string; items: { label: string; pct: number; color: 'green' | 'red' | 'blue' | 'dim' }[] };

const aiConvo: { q: string; a: string; widget: AiWidget }[] = [
  {
    q: 'Why did I lose on Tuesday?',
    a: 'Losses clustered after the first red trade. Risk expanded while setup quality dropped, turning a normal pullback into a -2.7R day.',
    widget: {
      type: 'curve',
      label: 'Tuesday drift',
      value: '-2.7R',
      tone: 'red',
      path: 'M6 22C24 18 33 20 46 18C62 16 70 26 84 30C100 35 113 48 132 50C151 53 166 57 184 66',
      area: 'M6 22C24 18 33 20 46 18C62 16 70 26 84 30C100 35 113 48 132 50C151 53 166 57 184 66V78H6Z',
    },
  },
  {
    q: "What's my strongest setup?",
    a: 'London open breakouts are still your edge: highest win rate, lowest drawdown, and the cleanest follow-through after entry.',
    widget: {
      type: 'bars',
      label: 'Setup score',
      value: '86',
      items: [
        { label: 'LO', pct: 86, color: 'green' },
        { label: 'NY', pct: 58, color: 'blue' },
        { label: 'Range', pct: 39, color: 'dim' },
        { label: 'Late', pct: 22, color: 'red' },
      ],
    },
  },
  {
    q: 'When do I overtrade?',
    a: 'Your risk cone widens after the third trade. The fourth trade has twice the variance and almost no expectancy.',
    widget: {
      type: 'cones',
      label: 'Risk cone',
      value: 'Trade 4',
      bands: [
        { d: 'M8 56C44 43 76 31 126 18C151 12 172 10 190 9V70C166 67 147 65 126 62C76 55 43 54 8 56Z', opacity: 0.13 },
        { d: 'M8 56C45 49 78 41 126 33C150 29 171 28 190 27V59C167 58 149 57 126 54C78 49 45 52 8 56Z', opacity: 0.22 },
      ],
      line: 'M8 56C43 53 74 48 104 43C137 37 162 35 190 34',
    },
  },
  {
    q: 'Show me my best trades.',
    a: 'Your best trades share the same shape: patient entry, no chase candle, and exit after momentum confirms.',
    widget: {
      type: 'heatmap',
      label: 'Quality map',
      value: '3 repeatable signals',
      items: [
        { label: 'Patience', pct: 92, color: 'green' },
        { label: 'Entry', pct: 78, color: 'blue' },
        { label: 'Exit', pct: 66, color: 'green' },
        { label: 'Size', pct: 41, color: 'dim' },
        { label: 'Tilt', pct: 18, color: 'red' },
        { label: 'News', pct: 35, color: 'dim' },
      ],
    },
  },
];

// ─── feature story data ───────────────────────────────────────────────────────

const features = [
  {
    id: 'journal',
    num: '01',
    label: 'Journal',
    headline: 'Log every trade in seconds.',
    body: 'Symbol, side, size, entry, exit, fees, notes, tags, screenshots. Manual entry takes seconds. Everything stays yours.',
  },
  {
    id: 'calendar',
    num: '02',
    label: 'P&L Calendar',
    headline: 'See your month the way your account does.',
    body: 'Green and red days turn a month of trades into a pattern you can read, and actually act on.',
  },
  {
    id: 'analytics',
    num: '03',
    label: 'Performance',
    headline: 'The stats your broker report skips.',
    body: 'Win rate, profit factor, expectancy, avg R, equity curve, streak data. One view. Always current.',
  },
  {
    id: 'ai',
    num: '04',
    label: 'AI Review',
    headline: 'Ask your data anything.',
    body: 'Weekly reports, trading plans, and natural-language chat surface the patterns worth acting on, without digging through rows.',
  },
  {
    id: 'import',
    num: '05',
    label: 'Connections',
    headline: 'Your trades, already there.',
    body: 'Sync from Binance and Bybit with your own read-only API keys - Klynd reads your fills, never your funds. Plus one-tap import from MetaTrader 5, NinjaTrader, and any standard export.',
  },
];

// ─── pricing ──────────────────────────────────────────────────────────────────

const pricingTiers = [
  {
    name: 'Free',
    summary: 'For traders building the habit.',
    monthly: '$0', annual: '$0',
    monthlyNote: 'forever', annualNote: 'forever',
    featured: false,
    items: ['Manual trade logging','P&L calendar','Core performance stats','Recent trade history','Basic filters'],
  },
  {
    name: 'Pro',
    summary: 'For traders who review seriously.',
    monthly: '$4.99', annual: '$39.99',
    monthlyNote: 'per month', annualNote: 'per year',
    featured: true,
    items: ['Unlimited trade history','Advanced analytics','Heatmaps and breakdowns','CSV and broker imports','Trade tagging','PDF reports','Multiple accounts'],
  },
  {
    name: 'AI Pro',
    summary: 'For traders who want the full loop.',
    monthly: '$9.99', annual: '$79.99',
    monthlyNote: 'per month', annualNote: 'per year',
    featured: false,
    items: ['Everything in Pro','Weekly AI reports','AI trading plan','Ask your trades','Pattern detection','Smart journal prompts','Risk and behavior alerts'],
  },
];

const propPricingTiers = [
  {
    name: 'Free',
    summary: 'For your first challenge.',
    monthly: '$0', annual: '$0',
    monthlyNote: 'forever', annualNote: 'forever',
    featured: false,
    items: ['Track up to 2 accounts','Live rule dashboard','Manual trade logging','Daily-limit alert','Prop firm database'],
  },
  {
    name: 'Pro',
    summary: 'For running multiple accounts.',
    monthly: '$4.99', annual: '$49.99',
    monthlyNote: 'per month', annualNote: 'per year',
    featured: true,
    items: ['Unlimited accounts','All smart alerts','Firm database filtering','CSV import','Challenge history & analytics','Payout tracker','Multi-account risk overview','Community firm reviews'],
  },
  {
    name: 'AI',
    summary: 'For the full coaching loop.',
    monthly: '$9.99', annual: '$99.99',
    monthlyNote: 'per month', annualNote: 'per year',
    featured: false,
    items: ['Everything in Pro','Weekly AI review','Rule-violation predictions','Challenge pattern analysis','Firm recommendations'],
  },
];

// ─── products (the Klynd suite) ─────────────────────────────────────────────────

type ProductKey = 'journal' | 'prop' | 'academy';
type PricedProductKey = Exclude<ProductKey, 'academy'>;

type Product = {
  key: ProductKey;
  name: string;
  logo: string;
  tagline: string;
  blurb: string;
  href: string;
  points: string[];
};

const PRODUCTS: Record<ProductKey, Product> = {
  journal: {
    key: 'journal',
    name: 'Klynd Journal',
    logo: '/logo-journal.svg',
    tagline: 'AI trading journal',
    blurb: 'Log or sync every trade, then let AI find the leaks, the patterns, and the setups that move your P&L.',
    href: '/journal',
    points: ['Auto-sync brokers & exchanges', 'Win rate, profit factor, expectancy', 'Weekly AI reports & trade chat'],
  },
  prop: {
    key: 'prop',
    name: 'Klynd Prop',
    logo: '/logo-prop.svg',
    tagline: 'Prop firm command center',
    blurb: 'Every challenge and funded account on one screen, with firm rules tracked live and warnings first.',
    href: '/prop',
    points: ['Live drawdown & loss headroom', 'Alerts before you breach a rule', 'AI coach that finds why you fail'],
  },
  academy: {
    key: 'academy',
    name: 'Klynd Academy',
    logo: '/logo-academy.svg',
    tagline: 'Free trading education',
    blurb: 'Interactive lessons from first chart to funded discipline, with quizzes, XP, streaks, and community.',
    href: '/academy',
    points: ['12 tracks, 53 lessons - all free', 'Interactive lessons, quizzes & XP', 'English, Spanish, Arabic + more'],
  },
};

const PRODUCT_ORDER: ProductKey[] = ['journal', 'prop', 'academy'];

// ─── recommended prop firms (affiliate) ─────────────────────────────────────────
// MIRROR of the Klynd Academy app's firm registry (app/src/content/firms.ts and
// docs/prop-firms.txt). The website is a SEPARATE repo from the app, so this list
// is a MANUAL copy — if you add, remove, re-order, or change a firm in Supabase /
// the app, update this array to match.
//
// Compliance (mirrors the app's posture):
//  • Names only, no firm logos (nominative use — avoids trademark issues).
//  • NO prices / profit splits / targets / drawdown / payout numbers. Those go
//    stale and misrepresenting a firm's terms breaches most affiliate agreements.
//    Each firm's own site is the single source of truth; we just link out.
//  • Every CTA sits with a conspicuous affiliate disclosure + a risk note.
//  • FTMO: only ever point CTAs at the cookieless tracking link below — other
//    FTMO pages only attribute the referral if the user accepts marketing cookies.

type RecommendedFirm = {
  slug: string;
  name: string;
  /** Neon accent — drives the card's breathing border + radial glow. */
  accent: string;
  /** One line, qualitative. No numbers. */
  tagline: string;
  markets: string[];
  /** Tracked affiliate sign-up link. */
  affiliateUrl: string;
  /** Personal checkout code (tap-to-copy). May also discount the buyer. */
  couponCode?: string;
};

const RECOMMENDED_FIRMS: RecommendedFirm[] = [
  {
    slug: 'ftmo',
    name: 'FTMO',
    accent: '#4F8BFF',
    tagline: 'The most established forex prop firm.',
    markets: ['Forex', 'Futures', 'Stocks', 'Crypto'],
    affiliateUrl: 'https://trader.ftmo.com/?affiliates=TWimZjcTKijBFOovLWHd',
  },
  {
    slug: 'the5ers',
    name: 'The5ers',
    accent: '#B57BFF',
    tagline: 'Funded forex trading with a scaling plan and salary tiers.',
    markets: ['Forex', 'Metals', 'Indices'],
    affiliateUrl: 'https://www.the5ers.com/?afmc=1c64',
    couponCode: '84519EULM',
  },
  {
    slug: 'e8',
    name: 'E8 Markets',
    accent: '#22D3EE',
    tagline: 'Straightforward 2-step evaluation with quick payouts.',
    markets: ['Forex', 'Metals', 'Indices', 'Crypto'],
    affiliateUrl: 'https://e8markets.com/d/B7E309D6',
  },
  {
    slug: 'alpha',
    name: 'Alpha Capital Group',
    accent: '#34D399',
    tagline: 'UK-based prop firm with a clean 2-step evaluation.',
    markets: ['Forex', 'Metals', 'Indices'],
    affiliateUrl: 'https://app.alphacapitalgroup.uk/signup/3RDGG',
    couponCode: '3RDGG',
  },
  {
    slug: 'goat',
    name: 'Goat Funded Trader',
    accent: '#FBBF24',
    tagline: 'Fast-growing prop firm with generous payouts.',
    markets: ['Forex', 'Metals', 'Indices', 'Crypto'],
    affiliateUrl: 'https://checkout.goatfundedtrader.com/aff/abdullahnaveed/',
  },
  {
    slug: 'earn2trade',
    name: 'Earn2Trade',
    accent: '#FB7185',
    tagline: 'Education-first funded futures program.',
    markets: ['Futures'],
    // CTA points at the Trader Career Path (their flagship). The KlyndTrade
    // coupon below also tracks commission + gives the buyer a discount, and
    // works across Earn2Trade products (incl. the Gauntlet Mini).
    affiliateUrl: 'https://www.earn2trade.com/trader-career-path?a_pid=KlyndTrade&a_bid=8d7b4b9e',
    couponCode: 'KlyndTrade',
  },
];

const propFirmsFaqs: { q: string; a: string }[] = [
  {
    q: 'Does signing up through Klynd cost me more?',
    a: 'No. These are affiliate links, so Klynd may earn a commission if you start a challenge — but you pay the firm exactly the same price you would going direct. Where a firm gives us a checkout code, it can actually save you money.',
  },
  {
    q: 'How does Klynd make money from this page?',
    a: 'When you sign up with a firm through one of our links, that firm pays Klynd a referral commission. It costs you nothing extra and it helps keep Klynd Academy free. We only list firms we would happily recommend regardless.',
  },
  {
    q: 'Is Klynd partnered with or endorsed by these firms?',
    a: 'No. Klynd is an independent affiliate. We are not partnered with, endorsed by, owned by, or an official representative of any prop firm. The "Klynd Pick" label reflects our own opinion, not the firm endorsing Klynd.',
  },
  {
    q: 'How does Klynd choose which firms to recommend?',
    a: 'We weigh track record, payout reliability, rule transparency, platform quality, and trader feedback, and only list firms we are comfortable pointing our own community to. We do not rank a firm higher just because it pays a bigger commission.',
  },
  {
    q: 'Which prop firm should I pick?',
    a: 'It depends on the markets you trade and the rules you are comfortable with. Compare the markets on each card, then read the firm’s current evaluation rules, pricing, and payout terms on their own site before buying. Prop trading carries real risk — never risk money you cannot afford to lose.',
  },
  {
    q: 'Are the prices and rules accurate here?',
    a: 'We deliberately do not publish prices, profit splits, targets, or payout figures, because firms change them often and stale numbers would mislead you. Always confirm the current terms on the firm’s own website — that is the only source of truth.',
  },
];

const suiteFaqs: { q: string; a: string }[] = [
  {
    q: 'What is Klynd.Trade?',
    a: 'Klynd.Trade is a suite of three mobile apps for traders: Klynd Journal, an AI trading journal that analyzes your own trade history; Klynd Prop, a prop firm tracker that watches every challenge and funded-account rule in real time; and Klynd Academy, a free interactive trading education app.',
  },
  {
    q: 'Are the Klynd apps free?',
    a: 'Klynd Academy is completely free. Klynd Journal and Klynd Prop both have generous free plans, with paid tiers that unlock advanced analytics, broker sync, unlimited accounts, and AI coaching.',
  },
  {
    q: 'Which Klynd app should I start with?',
    a: 'New to trading? Start with Klynd Academy and learn the fundamentals free. Trading prop firm challenges? Klynd Prop keeps you inside every rule. Already trading your own capital? Klynd Journal turns your history into an edge. They are built to work together.',
  },
  {
    q: 'Which platforms are Klynd apps available on?',
    a: 'All three apps - Klynd Journal, Klynd Prop, and Klynd Academy - are available free on both the iOS App Store and Google Play. Use the Download link in the menu to grab any app on your platform.',
  },
];

// ─── integrations data ────────────────────────────────────────────────────────

type IntItem = { name: string; icon: ReactNode };

function mkCryptoItems(): IntItem[] {
  return [
    { name: 'Binance', icon: <LogoBinance size={34} /> },
    { name: 'Bybit',   icon: <LogoBybit   size={34} /> },
  ];
}

function mkBrokerItems(): IntItem[] {
  return [
    { name: 'MetaTrader 5', icon: <LogoMT5   size={34} /> },
    { name: 'NinjaTrader',  icon: <LogoNinja size={34} /> },
    { name: 'CSV import',   icon: <LogoCSV   size={34} /> },
  ];
}

// ─── per-route SEO (title, description, canonical, robots, social) ──────────────

const SITE_URL = 'https://klynd.trade';

type PageInfo = ReturnType<typeof getPage>;

type SeoMeta = {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  keywords?: string;
  jsonLd?: object;
};

const INDEXABLE = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
const NOINDEX = 'noindex, nofollow';

// ─── structured data (JSON-LD) building blocks ──────────────────────────────────

const ORG_LD = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Klynd',
  legalName: 'ANSY GAMES (SMC-PRIVATE) LIMITED',
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.svg`, contentUrl: `${SITE_URL}/logo.svg` },
  email: 'support@klynd.trade',
  sameAs: ['https://twitter.com/klyndtrade', 'https://instagram.com/klynd.trade'],
  contactPoint: { '@type': 'ContactPoint', email: 'support@klynd.trade', contactType: 'customer support', availableLanguage: 'English' },
};

const WEBSITE_LD = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'Klynd.Trade',
  description: 'AI-powered trading tools for serious traders',
  inLanguage: 'en-US',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const JOURNAL_APP_LD = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#journal`,
  name: 'Klynd Journal',
  alternateName: ['Klynd AI Trading Journal', 'Klynd Trade Tracker'],
  description:
    'Klynd Journal is an AI-first trading journal for active traders. Log trades manually or sync your broker and exchange accounts, track performance across multiple accounts, and get AI-powered weekly reports, pattern detection, and trade analysis, all from your own data.',
  url: `${SITE_URL}/journal`,
  applicationCategory: 'FinanceApplication',
  applicationSubCategory: 'Trading Journal',
  operatingSystem: 'iOS, Android',
  inLanguage: 'en-US',
  publisher: { '@id': `${SITE_URL}/#organization` },
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: '0',
    highPrice: '9.99',
    offerCount: 3,
    offers: [
      { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', description: 'Full trading journal with manual logging and core analytics' },
      { '@type': 'Offer', name: 'Pro', price: '4.99', priceCurrency: 'USD', description: 'Advanced analytics, broker sync, and all journal features' },
      { '@type': 'Offer', name: 'AI Pro', price: '9.99', priceCurrency: 'USD', description: 'Weekly AI reports, AI chat, and pattern detection' },
    ],
  },
  featureList: [
    'AI-powered trade analysis and insights',
    'Weekly AI performance reports',
    'AI chat: ask your trades anything',
    'Broker and exchange sync',
    'Multi-account and multi-asset tracking',
    'Forex, crypto, futures, stocks, and options support',
    'Performance analytics and charts',
    'Trade tags, notes, and screenshots',
    'CSV import from any broker',
  ],
};

const PROP_APP_LD = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#prop`,
  name: 'Klynd Prop',
  alternateName: ['Klynd Prop Firm Tracker', 'Klynd Prop Challenge Tracker'],
  description:
    'Klynd Prop is a mobile command center for prop firm traders. Track every challenge and funded account in one place, monitor each firm’s rules in real time, get alerted before you breach a limit, and use AI to find the patterns behind your wins and losses.',
  url: `${SITE_URL}/prop`,
  applicationCategory: 'FinanceApplication',
  applicationSubCategory: 'Prop Firm Tracker',
  operatingSystem: 'iOS, Android',
  inLanguage: 'en-US',
  publisher: { '@id': `${SITE_URL}/#organization` },
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: '0',
    highPrice: '9.99',
    offerCount: 3,
    offers: [
      { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', description: 'Track up to 2 prop accounts with the live rule dashboard and daily-limit alerts' },
      { '@type': 'Offer', name: 'Pro', price: '4.99', priceCurrency: 'USD', description: 'Unlimited accounts, all alerts, analytics, and payout tracking' },
      { '@type': 'Offer', name: 'AI', price: '9.99', priceCurrency: 'USD', description: 'AI prop coach, weekly reviews, and pattern analysis' },
    ],
  },
  featureList: [
    'Multi-account prop firm dashboard',
    'Real-time rules engine (daily loss, drawdown, profit target, consistency)',
    'Breach and risk push alerts',
    'Prop firm rules database and comparison',
    'AI prop coach and weekly review',
    'Challenge history and pass-rate analytics',
    'Payout tracker',
    'Multi-account risk overview',
  ],
};

const ACADEMY_APP_LD = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#academy`,
  name: 'Klynd Academy',
  alternateName: ['Klynd Trading Academy', 'Klynd Learning'],
  description:
    'Klynd Academy is a free mobile-first trading education app for learning market basics, crypto, forex, risk management, trading psychology, prop firm rules, funded-account discipline, quizzes, progress, and community discussion.',
  url: `${SITE_URL}/academy`,
  applicationCategory: 'EducationApplication',
  applicationSubCategory: 'Trading Education',
  operatingSystem: 'iOS, Android',
  inLanguage: ['en-US', 'es', 'ar', 'hi', 'de', 'ur', 'it'],
  publisher: { '@id': `${SITE_URL}/#organization` },
  offers: {
    '@type': 'Offer',
    name: 'Free',
    price: '0',
    priceCurrency: 'USD',
    description: 'Free trading education curriculum, quizzes, progress, and community learning features',
  },
  featureList: [
    'Free trading education curriculum',
    'Beginner, intermediate, prop firm, and funded trader tracks',
    'Crypto, forex, chart reading, risk management, and psychology lessons',
    'Interactive quizzes and practice drills',
    'Progress tracking, XP, streaks, and completion moments',
    'Multilingual trading education',
    'Community market analysis and level-based discussion',
    'Vetted prop firm research and recommendations',
  ],
};

function faqLd(items: { q: string; a: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

function ldGraph(...nodes: object[]) {
  return { '@context': 'https://schema.org', '@graph': nodes };
}

function breadcrumbLd(items: { name: string; url: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: it.url })),
  };
}

const PRODUCT_ITEMLIST_LD = {
  '@type': 'ItemList',
  name: 'Klynd products',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Klynd Journal', url: `${SITE_URL}/journal` },
    { '@type': 'ListItem', position: 2, name: 'Klynd Prop', url: `${SITE_URL}/prop` },
    { '@type': 'ListItem', position: 3, name: 'Klynd Academy', url: `${SITE_URL}/academy` },
  ],
};

const JOURNAL_KEYWORDS =
  'trading journal, AI trading journal, trade tracker, trading analytics, forex trading journal, crypto trading journal, futures trading journal, stock trading journal, trade log app, broker sync journal, trading performance tracker, trade review app';
const PROP_KEYWORDS =
  'prop firm tracker, prop firm rules, prop challenge tracker, funded account tracker, drawdown tracker, daily loss limit tracker, FTMO tracker, Apex tracker, Topstep tracker, prop trading app, prop firm dashboard, prop firm rules database';
const ACADEMY_KEYWORDS =
  'free trading education, trading academy app, learn forex, learn crypto trading, prop firm education, funded trader course, trading lessons, trading quizzes, market analysis community, multilingual trading education';
const PROPFIRMS_KEYWORDS =
  'best prop firms, prop firms, prop trading firms, funded trading accounts, get funded, FTMO, The5ers, E8 Markets, Alpha Capital Group, Goat Funded Trader, Earn2Trade, futures funded account, prop firm challenge, forex prop firm, futures prop firm';

const PROPFIRMS_ITEMLIST_LD = {
  '@type': 'ItemList',
  name: 'Prop firms Klynd recommends',
  itemListElement: RECOMMENDED_FIRMS.map((f, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: f.name,
    description: f.tagline,
  })),
};

export function seoForPage(page: PageInfo): SeoMeta {
  switch (page.kind) {
    case 'journal':
      return {
        title: 'Klynd Journal - The AI Trading Journal That Studies You Back',
        description:
          'Klynd Journal is an AI-first trading journal for active traders. Log trades, sync your broker, track performance, and get AI-powered insights from your own trading history. Free to start.',
        canonical: `${SITE_URL}/journal`,
        robots: INDEXABLE,
        ogTitle: 'Klynd Journal - AI Trading Journal',
        ogDescription:
          "Turn your trade history into a sharper edge. Klynd Journal syncs your brokers, tracks every trade, and surfaces what's actually working.",
        ogImage: `${SITE_URL}/og-image.png?v=2`,
        keywords: JOURNAL_KEYWORDS,
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          JOURNAL_APP_LD,
          faqLd(faqs),
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Klynd Journal', url: `${SITE_URL}/journal` }]),
        ),
      };
    case 'prop':
      return {
        title: 'Klynd Prop - Prop Firm Tracker, Rules & Drawdown Alerts',
        description:
          'Klynd Prop tracks every prop firm challenge and funded account in one place, monitors each firm’s rules in real time, and warns you before you breach your daily loss or drawdown limit. Free to start.',
        canonical: `${SITE_URL}/prop`,
        robots: INDEXABLE,
        ogTitle: 'Klynd Prop - Never blow a prop challenge again',
        ogDescription:
          'The command center for prop traders: track every account, watch every firm’s rules live, and get warned before you breach.',
        ogImage: `${SITE_URL}/og-prop.png`,
        keywords: PROP_KEYWORDS,
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          PROP_APP_LD,
          faqLd(propFaqs),
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Klynd Prop', url: `${SITE_URL}/prop` }]),
        ),
      };
    case 'academy':
      return {
        title: 'Klynd Academy - Free Trading Education From Zero to Pro',
        description:
          'Klynd Academy teaches trading from the basics to funded-account discipline for free: crypto, forex, risk, psychology, prop firm rules, quizzes, community discussion, and multilingual lessons.',
        canonical: `${SITE_URL}/academy`,
        robots: INDEXABLE,
        ogTitle: 'Klynd Academy - Free trading education from zero to pro',
        ogDescription:
          'Learn crypto, forex, risk, psychology, prop firm rules, and funded-account discipline with free mobile lessons, quizzes, progress, and community market discussion.',
        ogImage: `${SITE_URL}/og-image.png?v=2`,
        keywords: ACADEMY_KEYWORDS,
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          ACADEMY_APP_LD,
          faqLd(academyFaqs),
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Klynd Academy', url: `${SITE_URL}/academy` }]),
        ),
      };
    case 'prop-firms':
      return {
        title: 'Recommended Prop Firms - Get Funded with Firms Klynd Trusts',
        description:
          'The prop trading firms the Klynd team recommends - FTMO, The5ers, E8 Markets, Alpha Capital Group, Goat Funded Trader, and Earn2Trade. Vetted for reputation, payouts, and clear rules. Start a challenge and get funded.',
        canonical: `${SITE_URL}/prop-firms`,
        robots: INDEXABLE,
        ogTitle: 'Recommended Prop Firms - Klynd',
        ogDescription:
          'A hand-picked list of prop trading firms Klynd recommends, vetted for reputation, payout reliability, and fair rules. Get funded.',
        ogImage: `${SITE_URL}/og-prop.png`,
        keywords: PROPFIRMS_KEYWORDS,
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          PROPFIRMS_ITEMLIST_LD,
          faqLd(propFirmsFaqs),
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Recommended Prop Firms', url: `${SITE_URL}/prop-firms` }]),
        ),
      };
    case 'download':
      return {
        title: 'Download Klynd - Journal, Prop & Academy Apps for iOS & Android',
        description:
          'Download the Klynd apps, free to start. Get Klynd Journal, Klynd Prop, and Klynd Academy on iPhone and Android. Pick your app and your platform.',
        canonical: `${SITE_URL}/download`,
        robots: INDEXABLE,
        ogTitle: 'Download the Klynd apps',
        ogDescription:
          'Get Klynd Journal, Klynd Prop, and Klynd Academy - free to start on iPhone and Android. One place to download whichever app you need.',
        ogImage: `${SITE_URL}/og-image.png?v=2`,
        keywords:
          'download klynd, klynd app download, trading journal app download, klynd journal android, klynd prop app, klynd academy app, google play trading journal, download trading journal',
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          JOURNAL_APP_LD,
          PROP_APP_LD,
          ACADEMY_APP_LD,
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Download', url: `${SITE_URL}/download` }]),
        ),
      };
    case 'pricing':
      return {
        title: 'Pricing - Klynd Journal & Klynd Prop',
        description:
          "Klynd pricing for Journal and Prop. Start free, then unlock advanced analytics, broker sync, and AI. Klynd Academy's trading education path is free.",
        canonical: `${SITE_URL}/pricing`,
        robots: INDEXABLE,
        keywords: 'klynd pricing, trading journal pricing, prop firm tracker pricing, trading journal free plan, AI trading journal cost',
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          breadcrumbLd([{ name: 'Klynd', url: `${SITE_URL}/` }, { name: 'Pricing', url: `${SITE_URL}/pricing` }]),
        ),
      };
    case 'privacy':
      return {
        title: 'Privacy Policy - Klynd',
        description:
          'How Klynd collects, uses, and protects your data: read-only broker access, encrypted storage, and full control over your trading journal data.',
        canonical: `${SITE_URL}/privacy`,
        robots: INDEXABLE,
      };
    case 'terms':
      return {
        title: 'Terms of Service - Klynd',
        description: 'The terms that govern your use of the Klynd AI trading journal app and website.',
        canonical: `${SITE_URL}/terms`,
        robots: INDEXABLE,
      };
    case 'confirmed':
      return { title: 'Email confirmed - Klynd', description: 'Your Klynd email address has been confirmed.', canonical: `${SITE_URL}/confirmed`, robots: NOINDEX };
    case 'academy-confirmed':
      return { title: 'Email confirmed - Klynd Academy', description: 'Your Klynd Academy email address has been confirmed.', canonical: `${SITE_URL}/academy/confirmed`, robots: NOINDEX };
    case 'prop-confirmed':
      return { title: 'Email confirmed - Klynd Prop', description: 'Your Klynd Prop email address has been confirmed.', canonical: `${SITE_URL}/prop/confirmed`, robots: NOINDEX };
    case 'delete-account':
      return { title: 'Delete your account - Klynd', description: 'Request deletion of your Klynd account and data.', canonical: `${SITE_URL}/delete-account`, robots: NOINDEX };
    case 'referral':
      return { title: 'Join Klynd - the AI trading journal', description: 'You were invited to Klynd, the AI-first trading journal for active traders.', canonical: `${SITE_URL}/`, robots: NOINDEX };
    case 'profile':
      return { title: `@${page.handle} on Klynd`, description: `View ${page.handle}'s public trading profile on Klynd.`, canonical: `${SITE_URL}/u/${page.handle}`, robots: NOINDEX };
    case 'home':
    default:
      return {
        title: 'Klynd.Trade',
        description:
          'Klynd builds the complete loop for serious traders: learn the game free with Klynd Academy, protect every prop account with Klynd Prop, and sharpen your edge with the Klynd Journal AI trading journal.',
        canonical: `${SITE_URL}/`,
        robots: INDEXABLE,
        ogTitle: 'Klynd.Trade - Serious tools for serious traders',
        ogDescription:
          'One suite, three apps: a free trading academy, a prop firm command center, and an AI trading journal that studies you back.',
        ogImage: `${SITE_URL}/og-image.png?v=2`,
        keywords:
          'klynd, trading apps, AI trading journal, prop firm tracker, free trading education, trading journal app, prop challenge tracker, trading academy, learn trading, trade tracker',
        jsonLd: ldGraph(
          WEBSITE_LD,
          ORG_LD,
          JOURNAL_APP_LD,
          PROP_APP_LD,
          ACADEMY_APP_LD,
          PRODUCT_ITEMLIST_LD,
          faqLd(suiteFaqs),
        ),
      };
  }
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function useSeo(meta: SeoMeta) {
  const ogTitle = meta.ogTitle ?? meta.title;
  const ogDescription = meta.ogDescription ?? meta.description;
  useEffect(() => {
    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'robots', meta.robots);
    upsertMeta('property', 'og:title', ogTitle);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:url', meta.canonical);
    upsertMeta('name', 'twitter:title', ogTitle);
    upsertMeta('name', 'twitter:description', ogDescription);
    if (meta.ogImage) {
      upsertMeta('property', 'og:image', meta.ogImage);
      upsertMeta('name', 'twitter:image', meta.ogImage);
    }
    if (meta.keywords) {
      upsertMeta('name', 'keywords', meta.keywords);
    }

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', meta.canonical);
  }, [meta.title, meta.description, meta.canonical, meta.robots, ogTitle, ogDescription, meta.ogImage, meta.keywords]);
}

// ─── app shell ────────────────────────────────────────────────────────────────

function App({ path }: { path?: string } = {}) {
  const page = useMemo(() => getPage(path), [path]);
  useSeo(seoForPage(page));
  const body = (() => {
    if (page.kind === 'download') return <DownloadPage />;
    if (page.kind === 'journal')  return <JournalPage />;
    if (page.kind === 'prop')     return <PropPage />;
    if (page.kind === 'prop-firms') return <PropFirmsPage />;
    if (page.kind === 'academy')  return <AcademyPage />;
    if (page.kind === 'pricing')  return <PricingPage />;
    if (page.kind === 'privacy' || page.kind === 'terms') return <LegalPage kind={page.kind} />;
    if (page.kind === 'delete-account') return <DeleteAccountPage />;
    if (page.kind === 'confirmed') return <ConfirmedPage />;
    if (page.kind === 'academy-confirmed') return <AcademyConfirmedPage />;
    if (page.kind === 'prop-confirmed') return <PropConfirmedPage />;
    if (page.kind === 'referral') return <ReferralPage code={page.code} />;
    if (page.kind === 'profile')  return <ProfilePage handle={page.handle} />;
    return <SuiteHomePage />;
  })();
  return (
    <PageContext.Provider value={page.kind}>
      <Shell>{body}</Shell>
    </PageContext.Provider>
  );
}

export function getPage(pathname?: string):
  | { kind: 'home' }
  | { kind: 'download' }
  | { kind: 'journal' }
  | { kind: 'prop' }
  | { kind: 'prop-confirmed' }
  | { kind: 'prop-firms' }
  | { kind: 'academy' }
  | { kind: 'pricing' }
  | { kind: 'privacy' }
  | { kind: 'terms' }
  | { kind: 'delete-account' }
  | { kind: 'confirmed' }
  | { kind: 'academy-confirmed' }
  | { kind: 'referral'; code: string }
  | { kind: 'profile'; handle: string } {
  const raw = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const p = raw.toLowerCase().split('/').filter(Boolean);
  if (p[0] === 'download') return { kind: 'download' };
  if (p[0] === 'journal') return { kind: 'journal' };
  if (p[0] === 'prop-firms') return { kind: 'prop-firms' };
  if (p[0] === 'prop')    return p[1] === 'confirmed' ? { kind: 'prop-confirmed' } : { kind: 'prop' };
  if (p[0] === 'academy') return p[1] === 'confirmed' ? { kind: 'academy-confirmed' } : { kind: 'academy' };
  if (p[0] === 'pricing') return { kind: 'pricing' };
  if (p[0] === 'privacy') return { kind: 'privacy' };
  if (p[0] === 'terms')   return { kind: 'terms' };
  if (p[0] === 'delete-account') return { kind: 'delete-account' };
  if (p[0] === 'confirmed') return { kind: 'confirmed' };
  if (p[0] === 'r')       return { kind: 'referral', code: p[1] ?? '' };
  if (p[0] === 'u')       return { kind: 'profile', handle: p[1] ?? '' };
  return { kind: 'home' };
}

function Shell({ children }: { children: ReactNode }) {
  const [waitlistSource, setWaitlistSource] = useState<StorePlatform | null>(null);
  const openWaitlist = (source: StorePlatform = 'general') => setWaitlistSource(source);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);

  return (
    <WaitlistContext.Provider value={openWaitlist}>
      <div className="shell">
        <Nav />
        <main>{children}</main>
        <Footer onOpenCookieSettings={() => setCookieSettingsOpen(true)} />
        <WaitlistModal source={waitlistSource} onClose={() => setWaitlistSource(null)} />
        <CookieConsent settingsOpen={cookieSettingsOpen} onCloseSettings={() => setCookieSettingsOpen(false)} />
      </div>
    </WaitlistContext.Provider>
  );
}

type CookiePrefs = { analytics: boolean; marketing: boolean };

function saveCookieConsent(prefs: CookiePrefs) {
  try {
    localStorage.setItem('klynd_cookie_consent', JSON.stringify({ necessary: true, ...prefs, ts: Date.now() }));
  } catch {}
}

function readCookiePrefs(): CookiePrefs {
  try {
    const raw = localStorage.getItem('klynd_cookie_consent');
    if (raw && raw !== '1') {
      const parsed = JSON.parse(raw);
      return { analytics: !!parsed.analytics, marketing: !!parsed.marketing };
    }
  } catch {}
  return { analytics: false, marketing: false };
}

function CookieToggle({ checked, label, onChange }: { checked: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="cookie-switch"
      onClick={() => onChange(!checked)}
    >
      <span className="cookie-switch-knob" />
    </button>
  );
}

function CookieConsent({ settingsOpen, onCloseSettings }: { settingsOpen: boolean; onCloseSettings: () => void }) {
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem('klynd_cookie_consent'); } catch { return false; }
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(readCookiePrefs);
  const showPanel = panelOpen || settingsOpen;

  const closePanel = () => {
    setPanelOpen(false);
    onCloseSettings();
  };

  const decide = (next: CookiePrefs) => {
    saveCookieConsent(next);
    setPrefs(next);
    setVisible(false);
    closePanel();
  };

  // Footer "Cookie settings" reopens the panel later - reload saved choices.
  useEffect(() => {
    if (settingsOpen) setPrefs(readCookiePrefs());
  }, [settingsOpen]);

  useEffect(() => {
    if (!showPanel) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPanel]);

  return (
    <>
      <AnimatePresence>
        {visible && !showPanel && (
          <motion.div
            className="cookie-banner"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.3, delay: 1.2 } }}
            exit={{ opacity: 0, y: 16, transition: { duration: 0.2 } }}
          >
            <div className="cookie-copy">
              <p className="cookie-title">We value your privacy</p>
              <p className="cookie-text">
                We use cookies to keep this site secure and working properly, and to remember your preferences.
                You can accept all cookies, reject non-essential ones, or customize your choices. Read our{' '}
                <a href="/privacy">Privacy Policy</a>.
              </p>
            </div>
            <div className="cookie-actions">
              <button className="cookie-btn cookie-btn--outline" type="button" onClick={() => setPanelOpen(true)}>Customize</button>
              <button className="cookie-btn cookie-btn--outline" type="button" onClick={() => decide({ analytics: false, marketing: false })}>Reject All</button>
              <button className="cookie-btn cookie-btn--solid" type="button" onClick={() => decide({ analytics: true, marketing: true })}>Accept All</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            className="waitlist-modal-backdrop"
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onMouseDown={(event) => { if (event.target === event.currentTarget) closePanel(); }}
          >
            <motion.div
              className="waitlist-modal cookie-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-panel-title"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
              <button className="modal-close" type="button" aria-label="Close cookie settings" onClick={closePanel}>
                <X size={16} />
              </button>
              <h2 id="cookie-panel-title">Customize consent preferences</h2>
              <p className="modal-copy">
                We use cookies to help you navigate efficiently and perform certain functions. Cookies categorized
                as necessary are stored on your browser as they are essential for basic site functions. You can
                choose to enable or disable the other categories, and change your choice anytime from "Cookie
                settings" in the footer.
              </p>

              <div className="cookie-category">
                <div className="cookie-category-head">
                  <span>Strictly necessary</span>
                  <span className="cookie-status cookie-status--locked">Always active</span>
                </div>
                <p>Required for core site functions such as security, load balancing, and remembering your cookie choices. These cannot be switched off.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-head">
                  <span>Analytics</span>
                  <CookieToggle checked={prefs.analytics} label="Allow analytics cookies" onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
                </div>
                <p>Help understand how visitors use the site. Klynd does not currently set any analytics cookies; your choice applies automatically if that ever changes.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-head">
                  <span>Marketing</span>
                  <CookieToggle checked={prefs.marketing} label="Allow marketing cookies" onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} />
                </div>
                <p>Used to show relevant content and measure campaigns. Klynd does not currently set any marketing cookies; your choice applies automatically if that ever changes.</p>
              </div>

              <div className="cookie-panel-actions">
                <button className="cookie-btn cookie-btn--outline" type="button" onClick={() => decide({ analytics: false, marketing: false })}>Reject All</button>
                <button className="cookie-btn cookie-btn--outline" type="button" onClick={() => decide(prefs)}>Save My Preferences</button>
                <button className="cookie-btn cookie-btn--solid" type="button" onClick={() => decide({ analytics: true, marketing: true })}>Accept All</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── nav ──────────────────────────────────────────────────────────────────────

function WaitlistModal({ source, onClose }: { source: StorePlatform | null; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [message, setMessage] = useState('');
  const [mailto, setMailto] = useState<string | null>(null);
  const open = source !== null;
  const platform = source === 'ios' ? 'iOS' : source === 'android' ? 'Android' : 'mobile';
  const alreadyJoined = useMemo(() => {
    try { return !!localStorage.getItem('klynd_waitlist_submitted'); } catch { return false; }
  }, []);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setState('idle');
    setMessage('');
    setMailto(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, source]);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [state, onClose]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState('sending');
    setMessage('');
    setMailto(null);
    try {
      await submitWaitlistEmail(email, source ?? 'general');
      setState('success');
      setMessage('You are on the list. We will send launch access when Klynd is ready.');
      try { localStorage.setItem('klynd_waitlist_submitted', '1'); } catch {}
    } catch (error) {
      setState('error');
      setMessage(error instanceof Error ? error.message : 'Could not add you right now. Try again in a moment.');
      if (error instanceof WaitlistError) setMailto(error.mailto);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="waitlist-modal-backdrop"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.div
            className="waitlist-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="waitlist-modal-title"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <button className="modal-close" type="button" aria-label="Close waitlist form" onClick={onClose}>
              <X size={16} />
            </button>
            {state === 'success' || alreadyJoined ? (
              <div className="waitlist-success">
                <div className="waitlist-success-icon"><Check size={22} strokeWidth={2.5} /></div>
                <h2 className="waitlist-success-title">You're on the list.</h2>
                <p className="waitlist-success-body">{alreadyJoined && state !== 'success' ? "You've already signed up. We'll notify you the moment Klynd is ready." : `We'll send you launch access the moment Klynd is ready for ${platform}. Check your email for a confirmation.`}</p>
              </div>
            ) : (
              <>
                <p className="eyebrow">Early access</p>
                <h2 id="waitlist-modal-title">Join the Klynd waitlist.</h2>
                <p className="modal-copy">Add your email and we will notify you when {platform} access is available.</p>
                <form className="waitlist-form modal-form" onSubmit={onSubmit}>
                  <div className="waitlist-row">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="Enter your email"
                      aria-label="Email address"
                      required
                    />
                    <button type="submit" disabled={state === 'sending'}>
                      {state === 'sending' ? <LoaderCircle size={14} className="spin" /> : <Send size={14} />}
                      Add me
                    </button>
                  </div>
                  {message && <p className={`waitlist-message waitlist-message--${state}`}>{message}</p>}
                  {state === 'error' && mailto && (
                    <a className="waitlist-mailto" href={mailto}>
                      <Send size={13} /> Email us instead
                    </a>
                  )}
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const NAV_LINKS: { label: string; href: string; kind: string }[] = [
  { label: 'Journal', href: '/journal', kind: 'journal' },
  { label: 'Prop',    href: '/prop',    kind: 'prop' },
  { label: 'Academy', href: '/academy', kind: 'academy' },
  { label: 'Pricing', href: '/pricing', kind: 'pricing' },
];

function Nav() {
  const pageKind = usePageKind();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`nav${scrolled ? ' nav--scrolled' : ''}`}>
      <div className="nav-pill">
        <a className="brand" href="/" aria-label="Klynd home">
          <img src="/logo.svg" alt="Klynd" className="brand-logo" />
          <span className="brand-name">Klynd</span>
        </a>
        <nav className="nav-links" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <a
              key={link.kind}
              href={link.href}
              className={pageKind === link.kind ? 'active' : ''}
              aria-current={pageKind === link.kind ? 'page' : undefined}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="nav-actions">
          <a className="nav-cta" href="/download">
            Download
          </a>
          <button
            type="button"
            className={`nav-burger${mobileOpen ? ' open' : ''}`}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />
    </header>
  );
}

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const pageKind = usePageKind();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // NOTE: do NOT lock body scroll here — setting overflow:hidden on <body>
    // breaks the sticky nav (it falls back to its in-flow position and vanishes
    // when the page is scrolled). The menu is a fixed dropdown, so no lock needed.
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const itemAnim = (i: number) => ({
    initial: { opacity: 0, y: -7 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -5 },
    transition: { duration: 0.22, delay: 0.04 + i * 0.045, ease: [0.22, 1, 0.36, 1] as const },
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="mobile-menu-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className="mobile-menu"
            role="menu"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mobile-menu-label">Products</p>
            {PRODUCT_ORDER.map((key, i) => {
              const p = PRODUCTS[key];
              const isCurrent = pageKind === key;
              return (
                <motion.a
                  key={key}
                  className={`mobile-product mobile-product--${key}${isCurrent ? ' is-current' : ''}`}
                  href={p.href}
                  onClick={onClose}
                  aria-current={isCurrent ? 'page' : undefined}
                  {...itemAnim(i)}
                >
                  <img src={p.logo} alt="" aria-hidden="true" />
                  <span className="mobile-product-text">
                    <strong>{p.name}</strong>
                    <em>{p.tagline}</em>
                  </span>
                  {isCurrent ? <span className="mobile-product-here">Here</span> : <ArrowRight size={15} />}
                </motion.a>
              );
            })}
            <motion.div className="mobile-menu-div" {...itemAnim(3)} />
            <motion.a
              className={`mobile-link${pageKind === 'pricing' ? ' is-current' : ''}`}
              href="/pricing"
              onClick={onClose}
              {...itemAnim(3.4)}
            >
              Pricing
            </motion.a>
            <motion.a className="mobile-waitlist" href="/download" onClick={onClose} {...itemAnim(3.8)}>
              Download
            </motion.a>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── suite home (klynd.trade root) ───────────────────────────────────────────

function SuiteHomePage() {
  return (
    <>
      <SuiteHero />
      <SuiteShowcase />
      <SuiteFlow />
      <PropFirmsCallout />
      <SuiteFaq />
      <FinalCta />
    </>
  );
}

function SuiteHero() {
  return (
    <section className="suite-hero">
      <div className="suite-hero-bg" aria-hidden="true" />
      <motion.p
        className="eyebrow suite-hero-eyebrow"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        The Klynd suite
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.08 }}
      >
        Learn it. Protect it.
        <br />
        <span className="suite-hero-accent">Master it.</span>
      </motion.h1>
      <motion.p
        className="hero-sub suite-hero-sub"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.16 }}
      >
        Three focused apps for the full arc of a trading career: a free trading academy,
        a prop firm command center, and an AI trading journal that studies you back.
      </motion.p>
      <motion.div
        className="suite-hero-cta"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.24 }}
      >
        <a className="suite-hero-explore" href="#products">Explore the apps</a>
        <span className="suite-hero-note">Free to start · iOS &amp; Android</span>
      </motion.div>
    </section>
  );
}

const TEASE_SCREENS: Record<ProductKey, ReactNode> = {
  journal: <PhoneFrame screen="dashboard" />,
  prop: <PropPhoneFrame screen="prop-dashboard" />,
  academy: <AcademyPhone shot="home" />,
};

function SuiteShowcase() {
  return (
    <section id="products" className="suite-showcase" aria-label="Klynd products">
      <div className="suite-showcase-grid">
        {PRODUCT_ORDER.map((key, i) => (
          <SuiteTeaseCard key={key} product={PRODUCTS[key]} index={i} />
        ))}
      </div>
    </section>
  );
}

function SuiteTeaseCard({ product, index }: { product: Product; index: number }) {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [3.2, -3.2]), { stiffness: 180, damping: 24 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-3.6, 3.6]), { stiffness: 180, damping: 24 });
  const glowX = useTransform(mx, (v) => `${v * 100}%`);
  const glowY = useTransform(my, (v) => `${v * 100}%`);

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'mouse') return;
    const rect = event.currentTarget.getBoundingClientRect();
    mx.set((event.clientX - rect.left) / rect.width);
    my.set((event.clientY - rect.top) / rect.height);
  };
  const onPointerLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.article
      className={`tease-card tease-card--${product.key}`}
      style={{ rotateX, rotateY }}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="tease-glow"
        aria-hidden="true"
        style={{ '--gx': glowX, '--gy': glowY } as Record<string, unknown>}
      />
      <span className="tease-tap-hint" aria-hidden="true">Tap to explore <ArrowUpRight size={11} strokeWidth={2.2} /></span>
      <div className="tease-head">
        <img src={product.logo} alt="" aria-hidden="true" className="tease-logo" />
        <div className="tease-id">
          <h2 className="tease-name">{product.name}</h2>
          <span className="tease-tag">{product.tagline}</span>
        </div>
      </div>
      <p className="tease-blurb">{product.blurb}</p>
      <div className="tease-phone" aria-hidden="true">
        <div className="tease-phone-scale">{TEASE_SCREENS[product.key]}</div>
      </div>
      <ul className="tease-points">
        {product.points.map((pt) => (
          <li key={pt}><Check size={13} strokeWidth={2.5} /><span>{pt}</span></li>
        ))}
      </ul>
      <div className="tease-actions">
        <div className="tease-badges">
          <AppStoreBadge href={APP_STORE_URLS[product.key]} />
          <PlayStoreBadge href={PLAY_URLS[product.key]} />
        </div>
        <a className="tease-link" href={product.href}>
          Learn more about {product.name}
        </a>
      </div>
    </motion.article>
  );
}

function SuiteFlow() {
  const steps = [
    { num: '01', verb: 'Learn', app: 'Klynd Academy', href: '/academy', text: 'Start free. Markets, risk, psychology, and prop firm rules - taught in minutes a day.' },
    { num: '02', verb: 'Protect', app: 'Klynd Prop', href: '/prop', text: 'Take the challenge. Every rule tracked live, every breach flagged before it happens.' },
    { num: '03', verb: 'Master', app: 'Klynd Journal', href: '/journal', text: 'Compound the edge. AI reviews every trade and tells you what is actually working.' },
  ];

  return (
    <section className="suite-flow" aria-labelledby="suite-flow-title">
      <p className="eyebrow">Built as a loop</p>
      <h2 id="suite-flow-title">One path, three stages.</h2>
      <div className="suite-flow-track">
        {steps.map((step, i) => (
          <motion.a
            key={step.num}
            className="suite-flow-step"
            href={step.href}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="suite-flow-num">{step.num}</span>
            <strong className="suite-flow-verb">{step.verb}</strong>
            <span className="suite-flow-app">{step.app}</span>
            <p>{step.text}</p>
          </motion.a>
        ))}
      </div>
    </section>
  );
}

function SuiteFaq() {
  return (
    <section className="faq-section" aria-labelledby="suite-faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="suite-faq-title">The Klynd suite, explained.</h2>
        <p className="section-sub faq-sub">What the three apps do and how they fit together.</p>
        <FaqAccordion items={suiteFaqs} />
      </div>
    </section>
  );
}

// ─── journal landing (/journal) ───────────────────────────────────────────────

function JournalPage() {
  return (
    <>
      <Hero />
      <MarketStrip />
      <FeatureStory />
      <AiSection />
      <IntegrationsSection />
      <FaqSection />
      <PropFirmsCallout />
      <SuiteBand current="journal" />
      <FinalCta product="journal" />
    </>
  );
}

// ─── hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="hero">
      <div className="hero-copy">
        <motion.div
          className="hero-logo-mark"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
        >
          <img src="/logo-journal.svg" alt="Klynd Journal" />
        </motion.div>
        <motion.p
          className="eyebrow"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.06 }}
        >
          AI-powered trading intelligence
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
        >
          The AI trading journal that studies you back.
        </motion.h1>
        <motion.p
          className="hero-sub"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Connect your broker, log every trade, and let Klynd's AI surface exactly what's working and what's costing you.
        </motion.p>
        <motion.div
          className="hero-badges"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.16 }}
        >
          <AppStoreBadge href={JOURNAL_APP_STORE_URL} />
          <PlayStoreBadge href={JOURNAL_PLAY_URL} />
        </motion.div>
      </div>

      <motion.div
        className="hero-phone"
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
      >
        <PhoneFrame screen="dashboard" />
        <ScreenDisclaimer />
      </motion.div>
    </section>
  );
}

// ─── market strip (ticker) ────────────────────────────────────────────────────

const MARKETS = ['Forex', 'Crypto', 'Futures', 'Equities', 'Prop firms', 'Indices', 'Commodities', 'Options'];

function MarketStrip() {
  const items = [...MARKETS, ...MARKETS, ...MARKETS, ...MARKETS];
  return (
    <div className="market-strip" aria-hidden="true">
      <div className="market-track">
        {items.map((m, i) => (
          <span key={i} className="market-pill">{m}</span>
        ))}
      </div>
    </div>
  );
}

// ─── feature story ────────────────────────────────────────────────────────────

const STORY_MS = 4200;

function FeatureStory() {
  const [active, setActive]     = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  const stepRefs = useRef<(HTMLElement | null)[]>([]);
  const storyRafRef = useRef<number | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    setProgress(0);
    let cancelled = false;
    const start = performance.now();

    const tick = (now: number) => {
      if (cancelled) return;
      const next = Math.min(100, ((now - start) / STORY_MS) * 100);
      setProgress(next);
      if (next >= 100) {
        setProgress(0);
        setActive(a => (a + 1) % features.length);
        return;
      }
      storyRafRef.current = window.requestAnimationFrame(tick);
    };

    storyRafRef.current = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (storyRafRef.current !== null) {
        window.cancelAnimationFrame(storyRafRef.current);
        storyRafRef.current = null;
      }
    };
  }, [active, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    const observers = features.map((_, i) => {
      const el = stepRefs.current[i];
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(i); },
        { threshold: 0.55, rootMargin: '-10% 0px -30% 0px' }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [isMobile]);

  const advance = (dir: 1 | -1) => {
    setActive(a => (a + dir + features.length) % features.length);
    setProgress(0);
  };

  return (
    <section id="features" className="story-section">

      {/* ══ MOBILE: Stories-style fullscreen ══ */}
      <div className="stories-mobile">
        <div className="stories-bars">
          {features.map((_, i) => (
            <div key={i} className="stories-bar">
              <div
                className="stories-fill"
                style={{ width: i < active ? '100%' : i === active ? `${progress}%` : '0%' }}
              />
            </div>
          ))}
        </div>
        <div className="stories-phone">
          <PhoneFrame screen={features[active].id} />
          <ScreenDisclaimer />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="stories-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <p className="step-label">{features[active].label}</p>
            <h2>{features[active].headline}</h2>
            <p className="step-body">{features[active].body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ══ DESKTOP: Sticky phone + scrolling steps ══ */}
      <div className="story-phone-col">
        <div className="story-phone-wrap">
          <PhoneFrame screen={features[active].id} />
          <div className="phone-nav-dots">
            {features.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`dot${active === i ? ' active' : ''}`}
                onClick={() => {
                  setActive(i);
                  stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                aria-label={`Go to ${features[i].label}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="story-steps-col">
        {features.map((f, i) => (
          <article
            key={f.id}
            ref={el => { stepRefs.current[i] = el; }}
            className={`story-step${active === i ? ' active' : ''}`}
          >
            <span className="step-num">{f.num}</span>
            <p className="step-label">{f.label}</p>
            <h2>{f.headline}</h2>
            <p className="step-body">{f.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── phone frame ──────────────────────────────────────────────────────────────

function PhoneFrame({ screen }: { screen: string }) {
  return (
    <div className="phone-frame" aria-label={`${screen} screen preview`}>
      <div className="phone-island" />
      <div className="phone-btn-vol-up" />
      <div className="phone-btn-vol-dn" />
      <div className="phone-btn-power" />
      <div className="phone-screen">
        <StatusBar />
        <ScreenContent key={screen} screen={screen} />
      </div>
    </div>
  );
}

function ScreenDisclaimer() {
  return (
    <p className="screen-disclaimer">Real screens from the Klynd app. Trade data shown is illustrative.</p>
  );
}

const SCREEN_SHOTS: Record<string, { src: string; alt: string }> = {
  dashboard: { src: '/screens/performance.jpg', alt: 'Klynd performance dashboard with net P&L and equity curve' },
  journal:   { src: '/screens/journal.jpg',     alt: 'Klynd trade journal listing logged trades' },
  calendar:  { src: '/screens/calendar.jpg',    alt: 'Klynd P&L calendar showing green and red trading days' },
  analytics: { src: '/screens/breakdowns.jpg',  alt: 'Klynd performance breakdowns and trading-window heatmap' },
  ai:        { src: '/screens/ai.png',          alt: 'Klynd AI review with weekly report, trading plan and insights' },
  import:    { src: '/screens/import.jpg',      alt: 'Klynd import screen for MetaTrader 5 and CSV trades' },
  'ai-chat':   { src: '/screens/ai-chat.png',   alt: 'Klynd AI coach answering why last week was bad' },
  'ai-report': { src: '/screens/ai-report.png', alt: 'Klynd weekly AI performance report' },
};

function ScreenContent({ screen }: { screen: string }) {
  const shot = SCREEN_SHOTS[screen] ?? SCREEN_SHOTS.dashboard;
  return <img className="phone-shot" src={shot.src} alt={shot.alt} loading="lazy" decoding="async" />;
}

// ─── Status bar ───────────────────────────────────────────────────────────────

function PolishedDashboardScreen() {
  return (
    <div className="screen screen-polished screen-polished--dashboard">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">Trading edge</span>
            <strong>Discipline score</strong>
          </div>
          <span className="polish-pill polish-pill--green">Last 30 trades</span>
        </div>

        <section className="balance-card reveal-item reveal-delay-1">
          <div className="balance-top">
            <span>Plan adherence</span>
            <span>Improving</span>
          </div>
          <strong>82%</strong>
          <p>Your cleanest trades came after waiting for confirmation.</p>
          <svg viewBox="0 0 230 76" className="polish-chart" aria-hidden="true">
            <defs>
              <linearGradient id="dash-fill-polished" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4fe08a" stopOpacity="0.36" />
                <stop offset="100%" stopColor="#4fe08a" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path className="polish-chart-grid" d="M0 20H230M0 44H230M0 68H230" />
            <path
              className="polish-chart-area"
              d="M0 67C22 60 33 52 52 55C73 58 78 64 98 52C119 40 129 42 145 34C164 24 174 23 193 17C207 13 218 9 230 6V76H0Z"
              fill="url(#dash-fill-polished)"
            />
            <path
              className="polish-chart-line"
              d="M0 67C22 60 33 52 52 55C73 58 78 64 98 52C119 40 129 42 145 34C164 24 174 23 193 17C207 13 218 9 230 6"
            />
            <circle cx="230" cy="6" r="4" fill="#4fe08a" />
          </svg>
        </section>

        <div className="value-grid reveal-item reveal-delay-2">
          <div className="value-card">
            <span>A+ setups</span>
            <strong>18</strong>
          </div>
          <div className="value-card value-card--blue">
            <span>Risk kept</span>
            <strong>0.8R</strong>
          </div>
        </div>

        <div className="insight-card reveal-item reveal-delay-3">
          <span><Sparkles size={11} /> AI note</span>
          <p>You cut size after losses. Keep that rule for the next session.</p>
        </div>
      </div>
    </div>
  );
}

function PolishedJournalScreen() {
  const trades = [
    { pair: 'BTC/USD', setup: 'Breakout', pnl: '+$1,140', tag: 'Clean', kind: 'win' },
    { pair: 'AAPL', setup: 'Pullback', pnl: '+$342', tag: 'Patient', kind: 'win' },
    { pair: 'EUR/USD', setup: 'Re-entry', pnl: '-$89', tag: 'Late', kind: 'loss' },
  ];
  return (
    <div className="screen screen-polished screen-polished--journal">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">Trade log</span>
            <strong>Today</strong>
          </div>
          <span className="polish-pill">3 notes</span>
        </div>

        <section className="journal-focus reveal-item reveal-delay-1">
          <span>Review score</span>
          <strong>8.7</strong>
          <p>Entries were slower, cleaner, and closer to plan.</p>
        </section>

        <div className="polish-trade-list reveal-item reveal-delay-2">
          {trades.map(t => (
            <div key={t.pair} className={`polish-trade polish-trade--${t.kind}`}>
              <div>
                <strong>{t.pair}</strong>
                <span>{t.setup}</span>
              </div>
              <div>
                <b>{t.pnl}</b>
                <em>{t.tag}</em>
              </div>
            </div>
          ))}
        </div>

        <div className="habit-card reveal-item reveal-delay-3">
          <span>Next journal prompt</span>
          <p>Did the setup appear before you entered, or after you wanted the trade?</p>
        </div>
      </div>
    </div>
  );
}

function PolishedCalendarScreen() {
  const days = [
    ['+$840','win'], ['+$320','win'], ['-$210','loss'], ['+$640','win'], ['+$190','win'],
    ['+$510','win'], ['-$380','loss'], ['+$920','hot'], ['+$280','win'], ['-$140','loss'],
    ['','flat'], ['+$1.1k','hot'], ['+$450','win'], ['+$720','hot'], ['-$290','loss'],
    ['+$880','hot'], ['+$340','win'], ['-$160','loss'], ['+$600','win'], ['+$400','win'],
  ];
  return (
    <div className="screen screen-polished screen-polished--calendar">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">P&L calendar</span>
            <strong>May 2026</strong>
          </div>
        </div>

        <section className="month-summary reveal-item reveal-delay-1">
          <div>
            <span>Net month</span>
            <strong>+$8,240</strong>
          </div>
          <div>
            <span>Green days</span>
            <strong>14 / 19</strong>
          </div>
        </section>

        <div className="calendar-board reveal-item reveal-delay-2">
          {['M','T','W','T','F'].map((d, i) => <span key={i} className="calendar-dow">{d}</span>)}
          {days.map(([val, kind], i) => (
            <div key={i} className={`calendar-tile calendar-tile--${kind}`}>
              <span>{i + 1}</span>
              {val && <b>{val}</b>}
            </div>
          ))}
        </div>

        <div className="calendar-note reveal-item reveal-delay-3">
          <span>Pattern</span>
          <p>Best days cluster after one-trade mornings.</p>
        </div>
      </div>
    </div>
  );
}

function PolishedAnalyticsScreen() {
  const bars: [string, number, string][] = [
    ['LO', 86, 'green'],
    ['NY', 52, 'blue'],
    ['Range', 39, 'amber'],
    ['Late', 22, 'red'],
  ];
  return (
    <div className="screen screen-polished screen-polished--analytics">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">Performance</span>
            <strong>Edge map</strong>
          </div>
          <span className="polish-pill polish-pill--blue">Live</span>
        </div>

        <section className="analytics-hero reveal-item reveal-delay-1">
          <span>Win rate</span>
          <strong>63.4%</strong>
          <p>Expectancy rose to +$196 when risk stayed below 1.2R.</p>
        </section>

        <div className="analytics-metrics reveal-item reveal-delay-2">
          <div><span>Avg win</span><strong>+$312</strong></div>
          <div><span>Avg loss</span><strong>-$148</strong></div>
          <div><span>Best trade</span><strong>+$1.1k</strong></div>
          <div><span>Max DD</span><strong>-$680</strong></div>
        </div>

        <div className="setup-bars reveal-item reveal-delay-3">
          <span>Setup quality</span>
          <div className="setup-bar-grid">
            {bars.map(([label, pct, tone]) => (
              <div key={label} className="setup-bar-col">
                <i className={`setup-bar setup-bar--${tone}`} style={{ height: `${pct}%` }} />
                <b>{label}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PolishedAiScreen() {
  return (
    <div className="screen screen-polished screen-polished--ai">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">Klynd AI</span>
            <strong>Review chat</strong>
          </div>
          <span className="polish-pill polish-pill--green"><Sparkles size={10} />Ready</span>
        </div>

        <div className="phone-chat reveal-item reveal-delay-1">
          <div className="phone-chat-user">Why did I give back profits?</div>
          <div className="phone-chat-bot">
            <span className="ai-badge-soft">Pattern found</span>
            <p>Three losses came after winners, all with larger size and no fresh setup.</p>
          </div>
        </div>

        <div className="ai-signal-card reveal-item reveal-delay-2">
          <div>
            <span>Trigger</span>
            <strong>Revenge size</strong>
          </div>
          <div>
            <span>Cost</span>
            <strong>-$541</strong>
          </div>
        </div>

        <div className="ai-action-row reveal-item reveal-delay-3">
          <button type="button">Show trades</button>
          <button type="button">Build rule</button>
        </div>
      </div>
    </div>
  );
}

function PolishedImportScreen() {
  return (
    <div className="screen screen-polished screen-polished--import">
      <StatusBar />
      <div className="polish-pad">
        <div className="polish-head reveal-item">
          <div>
            <span className="polish-kicker">Connections</span>
            <strong>Live sync</strong>
          </div>
          <span className="polish-pill polish-pill--green"><span className="live-dot" />Live</span>
        </div>

        <section className="sync-hero reveal-item reveal-delay-1">
          <div>
            <span>Connected sources</span>
            <strong>4</strong>
          </div>
          <div className="sync-logo-row" aria-hidden="true">
            <LogoBinance size={22} />
            <LogoBybit size={22} />
            <LogoMT5 size={22} />
          </div>
          <p>Last sync 2m ago. Every fill is ready for review.</p>
        </section>

        <section className="source-list reveal-item reveal-delay-2">
          <div className="source-row">
            <div className="source-logo"><LogoBinance size={24} /></div>
            <div><strong>Binance</strong><span>847 trades</span></div>
            <i>Read only</i>
          </div>
          <div className="source-row">
            <div className="source-logo"><LogoMT5 size={30} /></div>
            <div><strong>MetaTrader 5</strong><span>324 fills</span></div>
            <i>Synced</i>
          </div>
          <div className="source-row">
            <div className="source-logo"><LogoNinja size={30} /></div>
            <div><strong>NinjaTrader</strong><span>CSV route</span></div>
            <i>Ready</i>
          </div>
        </section>

        <div className="csv-card csv-card--wide reveal-item reveal-delay-3">
          <LogoCSV size={28} />
          <div>
            <strong>CSV import</strong>
            <span>Clean fallback for every broker.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LogoBinance({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <rect width="40" height="40" rx="10" fill="#F0B90B" />
      <g transform="translate(8 8)">
        <path
          fill="#1E2026"
          d="M16.624 13.9202l2.7175 2.7154-7.353 7.353-7.353-7.352 2.7175-2.7164 4.6355 4.6595 4.6356-4.6595zm4.6366-4.6366L24 12l-2.7154 2.7164L18.5682 12l2.6924-2.7164zm-9.272.001l2.7163 2.6914-2.7164 2.7174v-.001L9.2721 12l2.7164-2.7154zm-9.2722-.001L5.4088 12l-2.6914 2.6924L0 12l2.7164-2.7164zM11.9885.0115l7.353 7.329-2.7174 2.7154-4.6356-4.6356-4.6355 4.6595-2.7174-2.7154 7.353-7.353z"
        />
      </g>
    </svg>
  );
}

function LogoBybit({ size = 40 }: { size?: number }) {
  return (
    <svg width={Math.round(size * 2.25)} height={size} viewBox="0 0 90 40" fill="none" className="logo-wordmark" aria-hidden="true">
      <rect width="90" height="40" rx="10" fill="#fbfbfc" />
      <svg x="7" y="9" width="76" height="25" viewBox="0 0 13547 4513">
        <polygon fill="#F6A500" points="9655,3480 9655,-1 10355,-1 10355,3480" />
        <path fill="#15182A" d="M1500 4514l-1500 0 0 -3481 1440 0c700,0 1107,381 1107,978 0,386 -262,636 -443,719 216,98 493,318 493,782 0,650 -458,1002 -1097,1002zm-116 -2875l0 0 -685 0 0 802 685 0c297,0 463,-161 463,-401 0,-239 -166,-401 -463,-401zm45 1413l0 0 -730 0 0 856 730 0c317,0 468,-195 468,-430 0,-235 -151,-425 -468,-425z" />
        <polygon fill="#15182A" points="4732,3086 4732,4514 4037,4514 4037,3086 2960,1033 3720,1033 4389,2436 5049,1033 5809,1033" />
        <path fill="#15182A" d="M7793 4514l-1500 0 0 -3481 1440 0c700,0 1107,381 1107,978 0,386 -262,636 -443,719 216,98 493,318 493,782 0,650 -458,1002 -1097,1002zm-116 -2875l0 0 -685 0 0 802 685 0c297,0 463,-161 463,-401 0,-239 -166,-401 -463,-401zm45 1413l0 0 -730 0 0 856 730 0c317,0 468,-195 468,-430 0,-235 -151,-425 -468,-425z" />
        <polygon fill="#15182A" points="12610,1639 12610,4514 11911,4514 11911,1639 10974,1639 10974,1033 13547,1033 13547,1639" />
      </svg>
    </svg>
  );
}

function LogoMT5({ size = 40 }: { size?: number }) {
  return <img className="logo-img logo-img--mt5" src="/logos/metatrader5-sign.svg" alt="" aria-hidden="true" style={{ width: size, height: size }} />;
}

function LogoNinja({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 30 30" fill="none" aria-hidden="true">
      <rect width="30" height="30" rx="15" fill="#FF4200" />
      <path d="M20.314 20.28v-7.037h3.543V9.72H13.23v3.523h3.547v3.514L9.686 9.72H6.143v3.523l3.543 3.518H6.143v3.52h3.543v-3.52l3.539 3.52h7.09z" fill="#fff" />
    </svg>
  );
}

function LogoCSV({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <rect width="40" height="40" rx="10" fill="#1c2430" />
      <path d="M13 9h9l6 6v16a2 2 0 01-2 2H13a2 2 0 01-2-2V11a2 2 0 012-2z" fill="#f4f7fb" />
      <path d="M22 9v7h6" fill="#d7deea" />
      <path d="M15 21h10M15 25h8" stroke="#627089" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function StatusBar() {
  return (
    <div className="screen-status">
      <span>9:41</span>
      <div className="status-icons">
        <svg width="15" height="10" viewBox="0 0 15 10" fill="none" aria-hidden="true">
          <rect x="0" y="6" width="2.4" height="4" rx="0.8" fill="currentColor" />
          <rect x="4.1" y="4" width="2.4" height="6" rx="0.8" fill="currentColor" />
          <rect x="8.2" y="2" width="2.4" height="8" rx="0.8" fill="currentColor" />
          <rect x="12.3" y="0" width="2.4" height="10" rx="0.8" fill="currentColor" />
        </svg>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
          <path d="M1.4 3.5C4.7 0.8 9.3 0.8 12.6 3.5" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
          <path d="M3.9 6C5.7 4.6 8.3 4.6 10.1 6" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
          <path d="M6.5 8.3C6.8 8 7.2 8 7.5 8.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <svg width="22" height="10" viewBox="0 0 22 10" fill="none" aria-hidden="true">
          <rect x="0.5" y="0.5" width="18" height="9" rx="2.4" stroke="currentColor" strokeWidth="1" />
          <rect x="2" y="2" width="15" height="6" rx="1.6" fill="currentColor" />
          <path d="M20 3.6v2.8c0.8-0.1 1.4-0.7 1.4-1.4S20.8 3.7 20 3.6Z" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}

// ─── Dashboard screen ─────────────────────────────────────────────────────────

function DashboardScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">

        <div className="ds-header">
          <div>
            <p className="ds-label">Portfolio</p>
            <p className="ds-month">May 2026</p>
          </div>
          <div className="ds-ai-chip"><Sparkles size={10} />AI</div>
        </div>

        <div className="ds-pnl">
          <p className="ds-big ds-big--green">+$8,240</p>
          <p className="ds-meta">↑ 12.4% this month &nbsp;·&nbsp; 42 trades</p>
        </div>

        {/* Equity chart - green with glow */}
        <div className="ds-chart">
          <svg viewBox="0 0 260 72" fill="none" className="equity-svg">
            <defs>
              <linearGradient id="eq-grad-dash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#30d158" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#30d158" stopOpacity="0"   />
              </linearGradient>
              <filter id="eq-glow">
                <feGaussianBlur stdDeviation="2.5" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            <path className="grid-h" d="M0 18 H260 M0 36 H260 M0 54 H260" />
            <path
              className="equity-area"
              d="M0 65 C20 61 35 53 55 57 C75 61 82 67 105 59 C128 51 135 39 158 35 C178 32 190 25 210 21 C228 18 244 14 260 11 L260 72 L0 72 Z"
              fill="url(#eq-grad-dash)"
            />
            <path
              className="equity-line equity-line--green"
              d="M0 65 C20 61 35 53 55 57 C75 61 82 67 105 59 C128 51 135 39 158 35 C178 32 190 25 210 21 C228 18 244 14 260 11"
              filter="url(#eq-glow)"
            />
            <circle cx="260" cy="11" r="3.5" fill="#30d158" className="equity-dot" />
          </svg>
        </div>

        {/* Stats cards */}
        <div className="ds-stats-grid">
          <div className="ds-stat-card">
            <p className="ds-stat-val">63.4%</p>
            <p className="ds-label">Win rate</p>
          </div>
          <div className="ds-stat-card">
            <p className="ds-stat-val">2.11</p>
            <p className="ds-label">P.Factor</p>
          </div>
          <div className="ds-stat-card">
            <p className="ds-stat-val ds-stat-val--green">+$196</p>
            <p className="ds-label">Expect.</p>
          </div>
        </div>

        {/* Last trade */}
        <div className="ds-last-trade">
          <div>
            <p className="ds-lt-pair">BTC/USD &nbsp;<span className="ds-lt-side-long">Long</span></p>
            <p className="ds-label">Last trade · 1h ago</p>
          </div>
          <div className="ds-lt-right">
            <p className="ds-lt-pnl">+$342</p>
            <p className="ds-label">2.4R</p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Journal screen ───────────────────────────────────────────────────────────

function JournalScreen() {
  const trades = [
    { pair: 'BTC/USD', side: 'Long',  pnl: '+$1,140', r: '3.8R', win: true,  time: '11:00' },
    { pair: 'AAPL',    side: 'Long',  pnl: '+$342',   r: '2.4R', win: true,  time: '09:32' },
    { pair: 'GBP/JPY', side: 'Short', pnl: '+$228',   r: '1.5R', win: true,  time: '08:15' },
    { pair: 'EUR/USD', side: 'Short', pnl: '-$89',    r: '0.6R', win: false, time: '14:05' },
    { pair: 'NQ',      side: 'Long',  pnl: '-$312',   r: '1.0R', win: false, time: '15:10' },
  ];
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 6 }}>
          <div>
            <p className="ds-label">Trade log</p>
            <p className="ds-month">Journal</p>
          </div>
          <p className="ds-label" style={{ marginTop: 6 }}>May 2026</p>
        </div>
        <div className="trade-list">
          {trades.map(t => (
            <div
              key={t.pair + t.time}
              className={`trade-row ${t.win ? 'trade-row--win' : 'trade-row--loss'}`}
            >
              <div className="trade-left">
                <p className="trade-pair">{t.pair}</p>
                <p className="trade-time">{t.time}</p>
              </div>
              <span className={`trade-side ${t.win ? 'side-long' : 'side-short'}`}>{t.side}</span>
              <div className="trade-right">
                <p className={`trade-pnl ${t.win ? 'pnl-win' : 'pnl-loss'}`}>{t.pnl}</p>
                <p className="trade-r">{t.r}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar screen ──────────────────────────────────────────────────────────

function CalendarScreen() {
  const days: { kind: 'w' | 'l' | 'f'; val?: string }[] = [
    { kind: 'w', val: '+840'  }, { kind: 'w', val: '+320'  }, { kind: 'l', val: '-210' }, { kind: 'w', val: '+640' }, { kind: 'w', val: '+190' },
    { kind: 'w', val: '+510'  }, { kind: 'l', val: '-380'  }, { kind: 'w', val: '+920' }, { kind: 'w', val: '+280' }, { kind: 'l', val: '-140' },
    { kind: 'f'               }, { kind: 'w', val: '+1.1k' }, { kind: 'w', val: '+450' }, { kind: 'w', val: '+720' }, { kind: 'l', val: '-290' },
    { kind: 'w', val: '+880'  }, { kind: 'w', val: '+340'  }, { kind: 'l', val: '-160' }, { kind: 'w', val: '+600' }, { kind: 'w', val: '+400' },
  ];
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 6 }}>
          <div>
            <p className="ds-label">P&amp;L Calendar</p>
            <p className="ds-month">May 2026</p>
          </div>
        </div>
        <div className="cal-dow">
          {['M','T','W','T','F'].map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="cal-grid">
          {days.map((d, i) => (
            <div key={i} className={`cal-day cal-${d.kind}`}>
              <span className="cal-day-num">{i + 1}</span>
              {d.val && <span className="cal-day-val">{d.val}</span>}
            </div>
          ))}
        </div>
        <div className="cal-summary">
          <div className="cal-stat">
            <p className="ds-stat-val cal-green">+$8,240</p>
            <p className="ds-label">Net P&amp;L</p>
          </div>
          <div className="ds-stat-div" />
          <div className="cal-stat">
            <p className="ds-stat-val cal-green">14W</p>
            <p className="ds-label">Win days</p>
          </div>
          <div className="ds-stat-div" />
          <div className="cal-stat">
            <p className="ds-stat-val cal-red">5L</p>
            <p className="ds-label">Loss days</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Analytics screen ─────────────────────────────────────────────────────────

function AnalyticsScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 6 }}>
          <div>
            <p className="ds-label">Analytics</p>
            <p className="ds-month">Performance</p>
          </div>
        </div>

        {/* Hero metric */}
        <div className="perf-hero">
          <p className="perf-hero-val">63.4%</p>
          <p className="ds-label">Win rate · 42 trades</p>
        </div>

        <div className="perf-grid">
          {[
            { label: 'P.Factor',   value: '2.11',     accent: false, neg: false },
            { label: 'Expectancy', value: '+$196',    accent: true,  neg: false },
            { label: 'Avg win',    value: '+$312',    accent: true,  neg: false },
            { label: 'Avg loss',   value: '-$148',    accent: false, neg: true  },
            { label: 'Best trade', value: '+$1,140',  accent: true,  neg: false },
            { label: 'Max DD',     value: '-$680',    accent: false, neg: true  },
          ].map(s => (
            <div key={s.label} className="perf-card">
              <p className={`perf-val${s.accent ? ' perf-val--green' : s.neg ? ' perf-val--red' : ''}`}>{s.value}</p>
              <p className="ds-label">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="ds-chart" style={{ marginTop: 8 }}>
          <p className="ds-label" style={{ marginBottom: 4 }}>Equity curve</p>
          <svg viewBox="0 0 260 56" fill="none" className="equity-svg">
            <defs>
              <linearGradient id="eq-grad-perf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#30d158" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#30d158" stopOpacity="0"    />
              </linearGradient>
              <filter id="eq-glow-perf">
                <feGaussianBlur stdDeviation="1.8" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            <path className="grid-h" d="M0 14 H260 M0 28 H260 M0 42 H260" />
            <path
              className="equity-area"
              d="M0 50 C20 46 35 40 55 43 C75 47 82 51 105 44 C128 37 135 27 158 22 C178 19 190 14 210 10 C228 7 244 4 260 2 L260 56 L0 56 Z"
              fill="url(#eq-grad-perf)"
            />
            <path
              className="equity-line equity-line--green"
              d="M0 50 C20 46 35 40 55 43 C75 47 82 51 105 44 C128 37 135 27 158 22 C178 19 190 14 210 10 C228 7 244 4 260 2"
              filter="url(#eq-glow-perf)"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ─── AI screen (phone) ────────────────────────────────────────────────────────

function AiScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 10 }}>
          <div>
            <p className="ds-label">AI Assistant</p>
            <p className="ds-month">Klynd AI</p>
          </div>
          <div className="ds-ai-chip"><Sparkles size={10} />Live</div>
        </div>

        <div className="ai-screen-chat">
          <div className="ai-chat-user">Why was last week bad?</div>

          <div className="ai-chat-bot">
            <div className="ai-pattern-badge">
              <Sparkles size={8} />Pattern found
            </div>
            <p className="ai-chat-bot-body">
              You traded through two losing sessions without cutting size. 73% of losses followed a prior loss.
            </p>
          </div>

          <div className="ai-insight-row">
            <div className="ai-insight-item">
              <p className="ds-label">Worst day</p>
              <p className="ai-insight-val">Wednesday</p>
            </div>
            <div className="ai-insight-item">
              <p className="ds-label">Pattern</p>
              <p className="ai-insight-val ai-insight-val--red">Revenge</p>
            </div>
          </div>

          <div className="ai-followup-chip">Show me those trades →</div>
        </div>
      </div>
    </div>
  );
}

// ─── Import screen ────────────────────────────────────────────────────────────

function ImportScreen() {
  const connections = [
    { name: 'Binance',      icon: <BrokerBinance size={32} />, trades: '1,204 trades', time: '8m ago' },
    { name: 'Bybit',        icon: <BrokerBybit   size={32} />, trades: '392 trades',   time: '1h ago' },
    { name: 'MetaTrader 5', icon: <BrokerMT5     size={32} />, trades: '324 fills',    time: '12m ago' },
  ];
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 10 }}>
          <div>
            <p className="ds-label">Live accounts</p>
            <p className="ds-month">Connections</p>
          </div>
        </div>
        <div className="import-connections">
          {connections.map(c => (
            <div key={c.name} className="import-row">
              <div className="import-icon">{c.icon}</div>
              <div className="import-info">
                <p className="import-name">{c.name}</p>
                <p className="ds-label">{c.trades}</p>
              </div>
              <div className="import-status-col">
                <div className="import-live"><span className="live-dot" />Live</div>
                <p className="import-sync-time">{c.time}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="import-divider"><span>or import</span></div>
        <div className="import-csv-btn">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v8M3.5 5.5L7 9l3.5-3.5M2 11h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Upload CSV file
        </div>
      </div>
    </div>
  );
}

// ─── AI Section ───────────────────────────────────────────────────────────────

type AiPhase = 'typing-q' | 'thinking' | 'showing-a' | 'hold';

function AiSection() {
  return (
    <section id="ai" className="ai-section">
      <div className="ai-inner">

        {/* Copy */}
        <div className="ai-copy">
          <p className="eyebrow">AI review</p>
          <h2>Ask your trades anything.</h2>
          <p className="section-sub">
            Weekly reports, trading plans, and real-time AI chat, powered by your actual data, not generic advice.
          </p>
        </div>

        {/* Two real AI screens, overlapping */}
        <div className="ai-duo">
          <div className="ai-duo-phone ai-duo-back">
            <PhoneFrame screen="ai-chat" />
          </div>
          <div className="ai-duo-phone ai-duo-front">
            <PhoneFrame screen="ai-report" />
          </div>
        </div>

      </div>
    </section>
  );
}

function AiInsightWidget({ widget }: { widget: AiWidget }) {
  return (
    <div className={`ai-chart-shell ai-chart-shell--${widget.type}`}>
      <div className="ai-widget-head">
        <span>{widget.label}</span>
        <strong>{widget.value}</strong>
      </div>

      {widget.type === 'curve' && (
        <svg className={`ai-curve-svg ai-curve-svg--${widget.tone}`} viewBox="0 0 220 116" aria-hidden="true">
          <defs>
            <linearGradient id={`ai-curve-grad-${widget.tone}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.42" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path className="ai-chart-grid" d="M22 16H210M22 38H210M22 60H210M22 82H210" />
          <text className="ai-svg-label" x="20" y="18">+2R</text>
          <text className="ai-svg-label" x="20" y="40">+1R</text>
          <text className="ai-svg-label" x="20" y="62">0</text>
          <text className="ai-svg-label" x="20" y="84">-1R</text>
          <path className="ai-curve-zero" d="M22 60H210" />
          <text className="ai-svg-label" x="22" y="108">Open</text>
          <text className="ai-svg-label ai-svg-label--right" x="210" y="108">Close</text>
          <g transform="translate(22 12)">
            <path className="ai-curve-area" d={widget.area} fill={`url(#ai-curve-grad-${widget.tone})`} />
            <path className="ai-curve-line" d={widget.path} />
            <circle className="ai-curve-dot" cx="184" cy="66" r="4" />
            <circle className="ai-curve-dot ai-curve-dot--ping" cx="184" cy="66" r="8" />
            <g transform="translate(150 50)">
              <rect className="ai-curve-callout-bg" x="0" y="0" rx="5" ry="5" width="48" height="16" />
              <text className="ai-curve-callout-tx" x="24" y="11" textAnchor="middle">{widget.value}</text>
            </g>
          </g>
        </svg>
      )}

      {widget.type === 'bars' && (
        <div className="ai-bar-chart">
          <div className="ai-bar-scale"><span>100</span><span>75</span><span>50</span><span>25</span><span>0</span></div>
          <div className="ai-widget-bars">
            {widget.items.map(b => (
              <div key={b.label} className="ai-wb-col">
                <div className="ai-wb-track">
                  <span className={`ai-wb-val ai-wb-val--${b.color}`} style={{ bottom: `${Math.max(10, b.pct * 0.92)}px` }}>{b.pct}</span>
                  <div
                    className={`ai-wb-bar ai-wb-bar--${b.color}`}
                    style={{ height: `${Math.max(10, b.pct * 0.92)}px` }}
                  />
                </div>
                <span className="ai-wb-label">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {widget.type === 'cones' && (
        <svg className="ai-cone-svg" viewBox="0 0 220 116" aria-hidden="true">
          <defs>
            <linearGradient id="ai-cone-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#6E8BFF" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.32" />
            </linearGradient>
          </defs>
          <path className="ai-chart-grid" d="M22 16H210M22 38H210M22 60H210M22 82H210" />
          <text className="ai-svg-label" x="20" y="18">±2R</text>
          <text className="ai-svg-label" x="20" y="40">±1R</text>
          <text className="ai-svg-label" x="20" y="62">0</text>
          <text className="ai-svg-label" x="22" y="108">Trade 1</text>
          <text className="ai-svg-label" x="100" y="108">Trade 2</text>
          <text className="ai-svg-label ai-svg-label--right" x="210" y="108">Trade 4</text>
          <g transform="translate(22 12)">
            {widget.bands.map((band, i) => (
              <path key={i} d={band.d} className="ai-cone-band" fill="url(#ai-cone-grad)" style={{ opacity: band.opacity + 0.18 }} />
            ))}
            <path d={widget.line} className="ai-cone-line" />
            <circle className="ai-cone-dot" cx="190" cy="34" r="4" />
            <circle className="ai-cone-dot ai-curve-dot--ping" cx="190" cy="34" r="8" />
            <g transform="translate(150 12)">
              <rect className="ai-curve-callout-bg" x="0" y="0" rx="5" ry="5" width="52" height="16" />
              <text className="ai-curve-callout-tx" x="26" y="11" textAnchor="middle">tilt zone</text>
            </g>
          </g>
        </svg>
      )}

      {widget.type === 'heatmap' && (
        <div className="ai-heatmap-grid">
          {widget.items.map(cell => (
            <div key={cell.label} className={`ai-heat-cell ai-heat-cell--${cell.color}`}>
              <i style={{ transform: `scaleY(${Math.max(0.16, cell.pct / 100)})` }} />
              <div className="ai-heat-spark" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <em key={i} style={{ height: `${Math.max(14, Math.min(100, cell.pct - 8 + i * 4))}%` }} />
                ))}
              </div>
              <span>{cell.label}</span>
              <strong>{cell.pct}%</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Integrations (marquee) ───────────────────────────────────────────────────

function MarqueeRow({ seed, reverse = false }: { seed: IntItem[]; reverse?: boolean }) {
  // Repeat enough that one copy (the per-loop travel) overflows any laptop/desktop
  // width, so the row never shows a gap. The track animates by exactly 1/copies
  // (via --marquee-shift), keeping the loop seamless and the speed constant.
  const copies = seed.length <= 2 ? 10 : 8;
  const items = Array.from({ length: copies }, () => seed).flat();
  const shift = { '--marquee-shift': `-${(100 / copies).toFixed(4)}%` } as CSSProperties;
  return (
    <div className="marquee-wrap">
      <div className={`marquee-track${reverse ? ' marquee-track--rev' : ''}`} style={shift}>
        {items.map((item, i) => (
          <div key={i} className="marquee-card">
            <div className="marquee-icon">{item.icon}</div>
            <p className="marquee-name">{item.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntegrationsSection() {
  const cryptoItems = useMemo(mkCryptoItems, []);
  const brokerItems = useMemo(mkBrokerItems, []);
  return (
    <section className="integrations-section">
      <div className="integrations-inner">
        <p className="eyebrow">Connect your platforms</p>
        <h2>Connected to your world.</h2>
        <p className="section-sub int-sub">
          Connect your exchange with your own read-only API keys, or import from any platform - so every trade is already there when you review.
        </p>
      </div>
      <div className="integrations-carousels">
        <MarqueeRow seed={cryptoItems} />
        <MarqueeRow seed={brokerItems} reverse />
      </div>
    </section>
  );
}

// ─── faq (visible - mirrors the FAQPage JSON-LD in index.html) ──────────────────

const faqs: { q: string; a: string }[] = [
  {
    q: 'What is Klynd?',
    a: 'Klynd is an AI-first trading journal app for active traders. It lets you log trades manually or sync directly with your broker or exchange, analyze performance across multiple accounts, and get AI-powered insights from your own trading history.',
  },
  {
    q: 'Does Klynd have AI features for traders?',
    a: 'Yes. Klynd is built AI-first. It analyzes your trade history, surfaces patterns in your results, generates weekly AI performance reports, and lets you chat with an AI assistant about your specific trades, all powered by your own data, not generic market signals.',
  },
  {
    q: 'Is Klynd a good trading journal for forex traders?',
    a: 'Yes. Klynd is designed for all types of active traders including forex, crypto, futures, stocks, options, and prop firm traders. You can sync broker accounts, import CSV files, and track multiple trading accounts in one place.',
  },
  {
    q: 'What brokers and exchanges does Klynd support?',
    a: 'Klynd supports connections to traditional brokers, crypto exchanges, blockchain wallet addresses, TradingView webhook alerts, and CSV imports from most platforms. You can also log trades manually.',
  },
  {
    q: 'Is Klynd free?',
    a: 'Yes. Klynd has a free plan with a complete trading journal including manual trade logging and core analytics. Paid plans unlock AI-powered features, advanced performance reports, and broker sync.',
  },
  {
    q: 'Is Klynd safe for my trading data?',
    a: 'Yes. Klynd uses encrypted storage, database-level access controls, and requires only read-only broker credentials. Klynd is a journaling and analytics tool: it cannot place trades, withdraw funds, or manage your accounts.',
  },
  {
    q: 'Can I use Klynd for prop firm account tracking?',
    a: 'Yes. Klynd supports prop firm account tracking. You can log trades from prop firm accounts, track drawdown, review performance, and use AI analysis to understand what is and is not working in your trading.',
  },
  {
    q: 'How is Klynd different from other trading journals?',
    a: 'Klynd is AI-first, not an afterthought with a chatbot bolted on. Every feature is built around helping you understand your own trading patterns through AI analysis, weekly AI reports, and an AI assistant trained on your actual trade history.',
  },
];

function FaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number>(-1);
  return (
    <div className="faq-list">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className={`faq-item${isOpen ? ' open' : ''}`}>
            <button
              type="button"
              className="faq-q"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? -1 : i)}
            >
              <span>{f.q}</span>
              <ChevronDown className="faq-chevron" size={18} aria-hidden="true" />
            </button>
            <div className="faq-a-wrap">
              <div className="faq-a"><p>{f.a}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FaqSection() {
  return (
    <section id="faq" className="faq-section" aria-labelledby="faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="faq-title">Trading journal questions, answered.</h2>
        <p className="section-sub faq-sub">Everything you need to know about using Klynd as your AI trading journal.</p>
        <FaqAccordion items={faqs} />
      </div>
    </section>
  );
}

// ─── suite band (umbrella cross-sell) ───────────────────────────────────────────

function SuiteBand({ current }: { current: ProductKey }) {
  return (
    <section className="suite-band" aria-labelledby="suite-title">
      <div className="suite-inner">
        <p className="eyebrow">The Klynd suite</p>
        <h2 id="suite-title">One brand. Three trader tools.</h2>
        <p className="section-sub suite-sub">
          Klynd builds the education, tracking, and review loop for serious traders. Learn the game, protect the account, then study every trade.
        </p>
        <div className="suite-cards">
          {PRODUCT_ORDER.map((key) => (
            <ProductCard key={key} product={PRODUCTS[key]} current={current} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product, current }: { product: Product; current: ProductKey }) {
  const isCurrent = product.key === current;
  return (
    <div className={`suite-card suite-card--${product.key}${isCurrent ? ' is-current' : ''}`}>
      <div className="suite-card-head">
        <img src={product.logo} alt="" aria-hidden="true" className="suite-card-logo" />
        <div className="suite-card-id">
          <strong className="suite-card-name">{product.name}</strong>
          <span className="suite-card-tag">{product.tagline}</span>
        </div>
      </div>
      <p className="suite-card-blurb">{product.blurb}</p>
      <ul className="suite-card-points">
        {product.points.map((pt) => (
          <li key={pt}><Check size={13} strokeWidth={2.5} /><span>{pt}</span></li>
        ))}
      </ul>
      {isCurrent ? (
        <span className="suite-card-cta suite-card-cta--current">Currently viewing</span>
      ) : (
        <a className="suite-card-cta" href={product.href}>
          Explore {product.name} <ArrowRight size={14} />
        </a>
      )}
    </div>
  );
}

// ─── final cta ────────────────────────────────────────────────────────────────

// Final call-to-action. With a `product` it becomes a download CTA for that
// app (live store badges); without one it's the suite-level CTA that points to
// the /download hub. The apps are live, so this is no longer a waitlist.
function FinalCta({ product }: { product?: ProductKey } = {}) {
  if (product) {
    const p = PRODUCTS[product];
    return (
      <section className="final-cta">
        <div className="cta-inner">
          <p className="eyebrow">Get the app</p>
          <h2>Get {p.name}.</h2>
          <p className="section-sub">Free to start. Download {p.name} and set it up in minutes.</p>
          <div className="cta-badges">
            <AppStoreBadge href={APP_STORE_URLS[product]} />
            <PlayStoreBadge href={PLAY_URLS[product]} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="final-cta">
      <div className="cta-inner">
        <p className="eyebrow">Get started</p>
        <h2>Build your edge with Klynd.</h2>
        <p className="section-sub">Three free apps to learn the game, protect your account, and study every trade. Pick the one you need and download in minutes.</p>
        <a className="btn-primary-lg" href="/download">Download the apps</a>
      </div>
    </section>
  );
}

// ─── store badges ─────────────────────────────────────────────────────────────

// Live store listings. All three apps are now live on both Google Play and the
// App Store, so every store badge is a real link. (AppStoreBadge still supports
// a no-href waitlist fallback, but nothing uses it anymore.)
const JOURNAL_PLAY_URL = 'https://play.google.com/store/apps/details?id=trade.klynd';
const ACADEMY_PLAY_URL = 'https://play.google.com/store/apps/details?id=trade.klynd.academy';
const PROP_PLAY_URL = 'https://play.google.com/store/apps/details?id=trade.klynd.prop';

const JOURNAL_APP_STORE_URL = 'https://apps.apple.com/us/app/klynd-ai-trading-journal/id6782612195';
const ACADEMY_APP_STORE_URL = 'https://apps.apple.com/us/app/klynd-academy-learn-trading/id6783255048';
const PROP_APP_STORE_URL = 'https://apps.apple.com/us/app/klynd-prop-ai-prop-tracker/id6784974114';

const PLAY_URLS: Record<ProductKey, string> = {
  journal: JOURNAL_PLAY_URL,
  prop: PROP_PLAY_URL,
  academy: ACADEMY_PLAY_URL,
};

const APP_STORE_URLS: Record<ProductKey, string> = {
  journal: JOURNAL_APP_STORE_URL,
  prop: PROP_APP_STORE_URL,
  academy: ACADEMY_APP_STORE_URL,
};

// When `href` is set the badge is a real store link; otherwise it opens the
// waitlist (app not live yet on that platform).
function AppStoreBadge({ href }: { href?: string }) {
  const openWaitlist = useWaitlistModal();

  if (href) {
    return (
      <a className="store-badge store-badge--app" href={href} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
        <img src="/badges/app-store.svg" alt="Download on the App Store" />
      </a>
    );
  }
  return (
    <button type="button" className="store-badge store-badge--app" aria-label="Download on the App Store" onClick={() => openWaitlist('ios')}>
      <img src="/badges/app-store.svg" alt="Download on the App Store" />
    </button>
  );
}

function PlayStoreBadge({ href }: { href?: string }) {
  const openWaitlist = useWaitlistModal();

  if (href) {
    return (
      <a className="store-badge store-badge--play" href={href} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
        <img src="/badges/google-play.png" alt="Get it on Google Play" />
      </a>
    );
  }
  return (
    <button type="button" className="store-badge store-badge--play" aria-label="Get it on Google Play" onClick={() => openWaitlist('android')}>
      <img src="/badges/google-play.png" alt="Get it on Google Play" />
    </button>
  );
}

// ─── download hub (/download) ───────────────────────────────────────────────────
// One place to grab any app on any platform: a card per app with its own store
// badges. All apps are live on both stores, so every badge is a real link.

function DownloadPage() {
  return (
    <section className="download-page">
      <div className="download-head">
        <motion.p
          className="eyebrow"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.04 }}
        >
          Get the apps
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.06 }}
        >
          Download Klynd.
        </motion.h1>
        <motion.p
          className="section-sub"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Three apps, free to start. Pick whichever you need - Klynd Journal, Klynd Prop, and Klynd Academy - and grab it on your platform.
        </motion.p>
      </div>

      <div className="download-stack">
        {PRODUCT_ORDER.map((key, i) => {
          const product = PRODUCTS[key];
          return (
            <motion.article
              key={key}
              className={`download-card download-card--${key}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="download-card-id">
                <img src={product.logo} alt="" aria-hidden="true" className="download-card-logo" />
                <div className="download-card-name">
                  <h2>{product.name}</h2>
                  <span>{product.tagline}</span>
                </div>
              </div>
              <p className="download-card-blurb">{product.blurb}</p>
              <div className="download-card-badges">
                <AppStoreBadge href={APP_STORE_URLS[key]} />
                <PlayStoreBadge href={PLAY_URLS[key]} />
              </div>
              <a className="download-card-link" href={product.href}>
                Learn more about {product.name} <ArrowRight size={13} />
              </a>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

// ─── Klynd Prop landing ─────────────────────────────────────────────────────────

// Klynd Academy landing

const academyShots = {
  home: {
    src: '/screens/academy/home.png',
    alt: 'Klynd Academy home screen showing a beginner lesson and practice arena',
  },
  catalog1: {
    src: '/screens/academy/catalog-1.png',
    alt: 'Klynd Academy course catalog with beginner, markets, intermediate, chart patterns, and analysis tracks',
  },
  catalog2: {
    src: '/screens/academy/catalog-2.png',
    alt: 'Klynd Academy course catalog with strategy, prop firm ready, and funded trader tracks',
  },
  course1: {
    src: '/screens/academy/course-1.png',
    alt: 'Klynd Academy lesson screen explaining funded account risk',
  },
  course2: {
    src: '/screens/academy/course-2.png',
    alt: 'Klynd Academy lesson screen explaining funded trader discipline',
  },
  course3: {
    src: '/screens/academy/course-3.png',
    alt: 'Klynd Academy short lesson screen with educational trading content',
  },
  quiz: {
    src: '/screens/academy/quiz.png',
    alt: 'Klynd Academy quiz screen with answer feedback',
  },
  forexProgress: {
    src: '/screens/academy/forex-progress.png',
    alt: 'Klynd Academy forex course progress screen',
  },
  fundedProgress: {
    src: '/screens/academy/funded-progress.png',
    alt: 'Klynd Academy funded trader course progress screen',
  },
  streak: {
    src: '/screens/academy/streak.png',
    alt: 'Klynd Academy completion screen showing XP and a learning streak',
  },
  confetti: {
    src: '/screens/academy/confetti.png',
    alt: 'Klynd Academy completion screen with confetti and XP earned',
  },
  languages: {
    src: '/screens/academy/languages.png',
    alt: 'Klynd Academy profile screen with language choices',
  },
} as const;

type AcademyShotKey = keyof typeof academyShots;

function AcademyPhone({ shot, className = '', eager = false }: { shot: AcademyShotKey; className?: string; eager?: boolean }) {
  const img = academyShots[shot];
  return (
    <figure className={`academy-phone ${className}`} aria-label={img.alt}>
      <div className="phone-frame academy-phone-frame">
        <div className="phone-island" />
        <div className="phone-btn-vol-up" />
        <div className="phone-btn-vol-dn" />
        <div className="phone-btn-power" />
        <div className="phone-screen">
          <StatusBar />
          <img
            className="phone-shot academy-phone-shot"
            src={img.src}
            alt=""
            width="1080"
            height="2200"
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
          />
        </div>
      </div>
    </figure>
  );
}

// The real curriculum, straight from the app's course catalog screens.
const academyTrackData: {
  name: string;
  lessons: number;
  color: string;
  desc: string;
  sample?: string[];
  shot: AcademyShotKey;
}[] = [
  {
    name: 'Complete Beginner', lessons: 4, color: '#30d158',
    desc: 'What trading is and how markets actually work.',
    sample: ['What is trading?'],
    shot: 'catalog1',
  },
  {
    name: 'Know Your Markets', lessons: 5, color: '#42c8ff',
    desc: 'Forex, gold, indices and crypto - and which fits you.',
    sample: ['Forex - the currency market', 'Gold & commodities', 'Stock indices', 'Crypto', 'Choosing your market'],
    shot: 'catalog1',
  },
  {
    name: 'Intermediate', lessons: 4, color: '#a78bfa',
    desc: 'Risk, technical analysis, psychology, and a real plan.',
    shot: 'catalog1',
  },
  {
    name: 'Chart Patterns', lessons: 4, color: '#ff6482',
    desc: 'Read reversals and continuations on any chart.',
    shot: 'catalog1',
  },
  {
    name: 'Indicators & Analysis', lessons: 3, color: '#c084fc',
    desc: 'Moving averages, RSI, and multi-timeframe analysis.',
    shot: 'catalog1',
  },
  {
    name: 'Price Action & Liquidity', lessons: 5, color: '#facc15',
    desc: 'Read raw price, levels, stop hunts, wicks, and real breakouts.',
    shot: 'catalog2',
  },
  {
    name: 'Strategies', lessons: 3, color: '#2dd4bf',
    desc: 'Three complete setups - entry, stop, target, then practice.',
    shot: 'catalog2',
  },
  {
    name: 'Trade Management', lessons: 5, color: '#22d3ee',
    desc: 'Orders, stops, partials, and managing the live trade.',
    shot: 'catalog2',
  },
  {
    name: 'Risk Mastery', lessons: 5, color: '#f87171',
    desc: 'R-units, sizing math, expectancy, drawdowns, and the kill switch.',
    shot: 'catalog2',
  },
  {
    name: 'Trading Psychology', lessons: 5, color: '#e879f9',
    desc: 'Tilt, FOMO, discipline, routines, and process over outcome.',
    shot: 'catalog2',
  },
  {
    name: 'Prop Firm Ready', lessons: 6, color: '#6d86ff',
    desc: 'Understand challenges, rules, and how to pass.',
    shot: 'catalog2',
  },
  {
    name: 'Funded Trader', lessons: 4, color: '#fbbf24',
    desc: 'Manage a funded account, get paid, scale, and last.',
    sample: ['Managing a funded account', 'Payouts & scaling', 'Consistency & longevity', 'Tax & business basics'],
    shot: 'catalog2',
  },
];

// Sticky-phone scroll story (same mechanic as the Journal page).
const academyStory: { id: string; num: string; label: string; headline: string; body: string; shot: AcademyShotKey }[] = [
  {
    id: 'tracks', num: '01', label: 'The path',
    headline: 'Twelve tracks. One path: zero to funded.',
    body: 'From "What is trading?" to payouts, scaling, and tax basics - 53 bite-size lessons, 6 to 8 minutes each, one idea at a time.',
    shot: 'catalog1',
  },
  {
    id: 'lessons', num: '02', label: 'Interactive lessons',
    headline: 'Lessons you interact with, not just read.',
    body: 'Every concept is a living visual you can replay. Watch an 85% profit split turn a 4% month on a $100K account into $3,400 in your pocket.',
    shot: 'course2',
  },
  {
    id: 'quiz', num: '03', label: 'Quizzes',
    headline: 'Prove it before you risk it.',
    body: 'Every lesson ends with a check. Answer, get instant feedback, and learn why the right answer is right - before real money is on the line.',
    shot: 'quiz',
  },
  {
    id: 'progress', num: '04', label: 'Progression',
    headline: 'One lesson unlocks the next.',
    body: 'A clear path instead of a content dump. XP on every lesson, from your first chart to funded-account discipline.',
    shot: 'fundedProgress',
  },
  {
    id: 'streaks', num: '05', label: 'Streaks & XP',
    headline: 'Momentum you can feel.',
    body: 'Confetti when a course falls, XP when a lesson lands, and a daily streak that makes day two easier than day one.',
    shot: 'confetti',
  },
  {
    id: 'languages', num: '06', label: 'Languages',
    headline: 'Learn in your language.',
    body: 'English, Spanish, Arabic with full right-to-left support, and Hindi - with more on the way. Education that feels local, not translated.',
    shot: 'languages',
  },
];

const academyFaqs: { q: string; a: string }[] = [
  {
    q: 'Is Klynd Academy free?',
    a: 'Yes. Klynd Academy is built as a free education layer for traders, with lessons, quizzes, progress, completion moments, and community learning built around the Klynd ecosystem.',
  },
  {
    q: 'What can I learn in Klynd Academy?',
    a: 'The curriculum starts with trading basics and moves through crypto, forex, chart reading, risk, psychology, strategy, prop firm rules, funded-account management, payouts, scaling, and long-term discipline.',
  },
  {
    q: 'Which languages does Academy support?',
    a: 'Klynd Academy is built for multilingual learning, including English, Spanish, Arabic, Hindi, German, Urdu, Italian, and many more as the curriculum expands.',
  },
  {
    q: 'Does Klynd Academy recommend prop firms?',
    a: 'Academy can highlight vetted firms and explain what to compare, such as rules, drawdown model, pricing, payout structure, and reputation. It does not guarantee outcomes, and traders should always verify firm terms directly.',
  },
  {
    q: 'Is this financial advice?',
    a: 'No. Klynd Academy is educational content only. It teaches concepts, process, risk, and self-review; it does not provide financial advice, trade signals, profit guarantees, or a promise that any trader will pass a challenge.',
  },
];

function AcademyPage() {
  return (
    <div className="academy-page" data-product="academy">
      <AcademyHero />
      <AcademyStats />
      <AcademyStory />
      <AcademyCurriculum />
      <AcademyTryLesson />
      <AcademyGallery />
      <AcademyCommunity />
      <AcademyFaq />
      <AcademyDisclaimer />
      <PropFirmsCallout />
      <SuiteBand current="academy" />
      <FinalCta product="academy" />
    </div>
  );
}

function AcademyHero() {
  return (
    <section className="hero academy-hero">
      <div className="hero-copy academy-hero-copy">
        <motion.div
          className="hero-logo-mark academy-logo-mark"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
        >
          <img src="/logo-academy.svg" alt="Klynd Academy" />
        </motion.div>
        <motion.p className="eyebrow" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
          Free trading education
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}>
          Learn trading from zero to funded. Free.
        </motion.h1>
        <motion.p className="hero-sub" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          Short, interactive lessons in markets, risk, psychology, and prop-firm discipline - with quizzes, XP, streaks, and a Practice Arena that keeps you sharp.
        </motion.p>
        <motion.div className="hero-badges" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}>
          <AppStoreBadge href={ACADEMY_APP_STORE_URL} />
          <PlayStoreBadge href={ACADEMY_PLAY_URL} />
        </motion.div>
      </div>

      <motion.div
        className="hero-phone academy-hero-phone"
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
      >
        <AcademyPhone shot="home" eager />
        <p className="screen-disclaimer">Real Klynd Academy app screens. Educational content only.</p>
      </motion.div>
    </section>
  );
}

/* SSR renders the final value; the count-up only plays client-side on scroll into view. */
function CountUp({ to, suffix = '' }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(to);
  const played = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || played.current) return;
        played.current = true;
        const t0 = performance.now();
        const duration = 1100;
        const tick = (now: number) => {
          const p = Math.min(1, (now - t0) / duration);
          setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to]);

  return <span ref={ref}>{val}{suffix}</span>;
}

function AcademyStats() {
  const stats: { value: ReactNode; label: string; num: boolean }[] = [
    { value: 'Free', label: 'Every lesson included', num: false },
    { value: <CountUp to={12} />, label: 'Tracks, beginner to funded', num: true },
    { value: <CountUp to={53} />, label: 'Bite-size interactive lessons', num: true },
    { value: <CountUp to={4} suffix="+" />, label: 'Languages - EN, ES, AR, HI', num: true },
  ];

  return (
    <section className="academy-stats" aria-label="Klynd Academy highlights">
      {stats.map((stat, i) => (
        <motion.div
          key={stat.label}
          className={`academy-stat${stat.num ? ' academy-stat--num' : ''}`}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.45, delay: i * 0.07 }}
        >
          <strong>{stat.value}</strong>
          <span>{stat.label}</span>
        </motion.div>
      ))}
    </section>
  );
}

// ─── academy story (sticky phone + scroll steps, Journal-style) ────────────────

function AcademyStory() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  const stepRefs = useRef<(HTMLElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    setProgress(0);
    let cancelled = false;
    const start = performance.now();

    const tick = (now: number) => {
      if (cancelled) return;
      const next = Math.min(100, ((now - start) / STORY_MS) * 100);
      setProgress(next);
      if (next >= 100) {
        setProgress(0);
        setActive(a => (a + 1) % academyStory.length);
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [active, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    const observers = academyStory.map((_, i) => {
      const el = stepRefs.current[i];
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(i); },
        { threshold: 0.55, rootMargin: '-10% 0px -30% 0px' }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [isMobile]);

  const step = academyStory[active];

  return (
    <section id="academy-story" className="story-section academy-story">

      {/* ══ MOBILE: Stories-style fullscreen ══ */}
      <div className="stories-mobile">
        <div className="stories-bars">
          {academyStory.map((_, i) => (
            <div key={i} className="stories-bar">
              <div
                className="stories-fill"
                style={{ width: i < active ? '100%' : i === active ? `${progress}%` : '0%' }}
              />
            </div>
          ))}
        </div>
        <div className="stories-phone">
          <AcademyPhone key={step.shot} shot={step.shot} />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="stories-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <p className="step-label">{step.label}</p>
            <h2>{step.headline}</h2>
            <p className="step-body">{step.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ══ DESKTOP: Sticky phone + scrolling steps ══ */}
      <div className="story-phone-col">
        <div className="story-phone-wrap">
          <AcademyPhone key={step.shot} shot={step.shot} />
          <div className="phone-nav-dots">
            {academyStory.map((s, i) => (
              <button
                key={i}
                type="button"
                className={`dot${active === i ? ' active' : ''}`}
                onClick={() => {
                  setActive(i);
                  stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                aria-label={`Go to ${s.label}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="story-steps-col">
        {academyStory.map((s, i) => (
          <article
            key={s.id}
            ref={el => { stepRefs.current[i] = el; }}
            className={`story-step${active === i ? ' active' : ''}`}
          >
            <span className="step-num">{s.num}</span>
            <p className="step-label">{s.label}</p>
            <h2>{s.headline}</h2>
            <p className="step-body">{s.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── academy curriculum (the real catalog, shown as the real screens) ──────────

function AcademyCurriculum() {
  const totalLessons = academyTrackData.reduce((sum, t) => sum + t.lessons, 0);

  return (
    <section className="academy-section academy-curriculum" id="academy-curriculum">
      <div className="academy-section-head">
        <p className="eyebrow">Curriculum</p>
        <h2>Everything a trader should have been taught first.</h2>
        <p className="section-sub">The real course catalog, straight from the app.</p>
      </div>

      <div className="academy-duo" aria-label="Academy course catalog screens">
        <motion.div
          className="academy-duo-phone academy-duo-phone--a"
          initial={{ opacity: 0, y: 34, rotate: -6 }}
          whileInView={{ opacity: 1, y: 0, rotate: -4 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <AcademyPhone shot="catalog1" />
        </motion.div>
        <motion.div
          className="academy-duo-phone academy-duo-phone--b"
          initial={{ opacity: 0, y: 34, rotate: 6 }}
          whileInView={{ opacity: 1, y: 0, rotate: 4 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          <AcademyPhone shot="catalog2" />
        </motion.div>
      </div>

      <p className="academy-track-total">
        {academyTrackData.length} tracks · {totalLessons} lessons · every one free
      </p>
    </section>
  );
}

// ─── academy gallery (the rest of the real screens, captioned) ─────────────────

const academyGallery: { shot: AcademyShotKey; caption: string }[] = [
  { shot: 'course1', caption: 'Funded-account risk, animated' },
  { shot: 'course3', caption: 'Tap-to-reveal flashcards' },
  { shot: 'forexProgress', caption: 'Know Your Markets track' },
  { shot: 'streak', caption: 'XP and daily streaks' },
];

function AcademyGallery() {
  return (
    <section className="academy-gallery-wrap" aria-label="More Klynd Academy screens">
      <p className="academy-gallery-hint" aria-hidden="true">Slide to explore <ArrowRight size={12} /></p>
      <div className="academy-gallery">
        {academyGallery.map((item, i) => (
          <motion.figure
            key={item.shot}
            className="academy-gallery-item"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="academy-gallery-phone" style={{ '--tilt': `${i % 2 === 0 ? -2.5 : 2.5}deg` } as CSSProperties}>
              <AcademyPhone shot={item.shot} />
            </div>
            <figcaption>{item.caption}</figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

// ─── academy try-a-lesson (interactive demo straight from the app) ─────────────

const TRY_QUIZ = {
  question: 'What should change about your risk once you’re funded?',
  options: [
    { text: 'Nothing - keep the same process', correct: true },
    { text: 'Risk much more to earn faster', correct: false },
    { text: 'Drop the stop-loss', correct: false },
  ],
  explain: 'Keep the process identical. Funded just means the profits - and the consequences - are now real.',
};

function AcademyTryLesson() {
  const [step, setStep] = useState(0);          // 0 slider · 1 flashcard · 2 quiz · 3 done
  const [gain, setGain] = useState(4);          // % month on a $100K funded account
  const [revealed, setRevealed] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);

  const profit = gain * 1000;
  const keep = Math.round(profit * 0.85);
  const firm = profit - keep;
  const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;
  const answered = picked !== null;
  const pickedCorrect = picked !== null && TRY_QUIZ.options[picked].correct;

  const restart = () => { setStep(0); setGain(4); setRevealed(false); setPicked(null); };

  return (
    <section className="academy-try" id="academy-try">
      <div className="academy-try-copy">
        <p className="eyebrow">Try it now</p>
        <h2>Take a 30-second lesson. Right here.</h2>
        <p className="section-sub">
          This is how Academy teaches: you drag it, tap it, and answer for it - so the idea sticks before real money is involved.
        </p>
      </div>

      <div className="academy-try-card" data-step={step}>
        <div className="try-progress" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className={`try-progress-seg${step > i ? ' done' : step === i ? ' active' : ''}`} />
          ))}
        </div>
        <p className="try-step-count">{step < 3 ? `${step + 1} of 3` : 'Complete'}</p>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="split" className="try-body" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
              <h3>Your share of the profit</h3>
              <p className="try-context">{gain}% month on a $100K funded account</p>
              <p className="try-big">{fmt(keep)}</p>
              <p className="try-caption">your share after the split</p>
              <div className="try-split-bar" aria-hidden="true">
                <span className="try-split-you" style={{ width: '85%' }}>You 85%</span>
                <span className="try-split-firm">15%</span>
              </div>
              <div className="try-split-legend">
                <span className="try-legend-you">● You keep {fmt(keep)}</span>
                <span className="try-legend-firm">● Firm {fmt(firm)}</span>
              </div>
              <input
                className="try-slider"
                type="range"
                min={1}
                max={8}
                step={1}
                value={gain}
                onChange={(e) => setGain(Number(e.target.value))}
                aria-label="Monthly gain percentage"
              />
              <p className="try-hint">‹ drag the month ›</p>
              <button className="try-continue" type="button" onClick={() => setStep(1)}>Continue</button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="card" className="try-body" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
              <h3>Flashcard</h3>
              <button
                type="button"
                className={`try-flashcard${revealed ? ' revealed' : ''}`}
                onClick={() => setRevealed(true)}
              >
                {revealed ? (
                  <>
                    <span className="try-flash-kicker">Answer</span>
                    <strong>A lot is a position-size unit.</strong>
                    <span className="try-flash-body">One standard lot is 100,000 units of the base currency. Minis (10K) and micros (1K) let you size risk precisely.</span>
                  </>
                ) : (
                  <>
                    <span className="try-flash-kicker">Tap to reveal</span>
                    <strong>What's a "lot" in forex?</strong>
                    <span className="try-flash-body">It's about size.</span>
                  </>
                )}
              </button>
              <button
                className="try-continue"
                type="button"
                onClick={() => (revealed ? setStep(2) : setRevealed(true))}
              >
                {revealed ? 'Continue' : 'Reveal answer'}
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="quiz" className="try-body" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
              <h3>{TRY_QUIZ.question}</h3>
              <div className="try-options">
                {TRY_QUIZ.options.map((opt, i) => {
                  const state = !answered ? '' : opt.correct ? ' correct' : i === picked ? ' wrong' : ' dim';
                  return (
                    <button
                      key={opt.text}
                      type="button"
                      className={`try-option${state}`}
                      disabled={answered}
                      onClick={() => setPicked(i)}
                    >
                      {opt.text}
                      {answered && opt.correct && <Check size={15} strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
              {answered && (
                <motion.div className="try-explain" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <strong className={pickedCorrect ? 'try-correct' : 'try-wrong'}>
                    {pickedCorrect ? 'Correct!' : 'Not quite.'}
                  </strong>
                  <p>{TRY_QUIZ.explain}</p>
                </motion.div>
              )}
              <button className="try-continue" type="button" disabled={!answered} onClick={() => setStep(3)}>Continue</button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="done" className="try-body try-done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.28 }}>
              <div className="try-confetti" aria-hidden="true">
                {Array.from({ length: 18 }).map((_, i) => <i key={i} />)}
              </div>
              <div className="try-xp">
                <strong>+210</strong>
                <span>XP earned</span>
              </div>
              <h3>That was one lesson.</h3>
              <p className="try-context">There are 53 more in the app - every one free.</p>
              <div className="try-done-badges">
                <AppStoreBadge href={ACADEMY_APP_STORE_URL} />
                <PlayStoreBadge href={ACADEMY_PLAY_URL} />
              </div>
              <button className="try-restart" type="button" onClick={restart}>Replay the lesson</button>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="try-disclaimer">Educational content only. This is not financial advice.</p>
      </div>
    </section>
  );
}

function AcademyCommunity() {
  const pillars = [
    {
      icon: <Users size={17} />,
      label: 'Level-based community',
      text: 'Discuss the market with traders at your stage - beginner to funded.',
    },
    {
      icon: <MessageCircle size={17} />,
      label: 'Market analysis',
      text: 'Daily context and discussion, without the signal-group noise.',
    },
    {
      icon: <Shield size={17} />,
      label: 'Vetted firm guidance',
      text: 'Learn what to compare before paying for a prop challenge.',
    },
    {
      icon: <Target size={17} />,
      label: 'Practice Arena',
      text: 'Quick drills between lessons to sharpen your edge.',
    },
  ];

  return (
    <section className="academy-section academy-community-band">
      <div className="academy-section-head">
        <p className="eyebrow">Beyond the lessons</p>
        <h2>A place to grow, not just a course.</h2>
      </div>
      <div className="academy-pillar-grid">
        {pillars.map((pillar, i) => (
          <motion.article
            key={pillar.label}
            className="academy-pillar"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
          >
            <div>{pillar.icon}</div>
            <strong>{pillar.label}</strong>
            <p>{pillar.text}</p>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function AcademyFaq() {
  return (
    <section id="academy-faq" className="faq-section" aria-labelledby="academy-faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="academy-faq-title">Academy questions, answered.</h2>
        <p className="section-sub faq-sub">Everything about learning with Klynd Academy for free.</p>
        <FaqAccordion items={academyFaqs} />
      </div>
    </section>
  );
}

function AcademyDisclaimer() {
  return (
    <div className="academy-disclaimer-wrap">
      <p className="academy-disclaimer">
        Klynd Academy is educational content only and does not provide financial advice, investment advice, trade signals, prop firm guarantees, or profit promises. Any prop firm information or recommendations are for research and comparison only; always verify current rules and terms directly with the firm.
      </p>
    </div>
  );
}

const PROP_FIRMS = [
  'FTMO', 'Apex', 'Topstep', 'MyFundedFutures', 'FundingPips',
  'The Funded Trader', 'Take Profit Trader', 'Alpha Futures', 'E8 Markets', 'Tradeify',
];

type PropStatus = 'safe' | 'caution' | 'stop';
function propLevel(pct: number): PropStatus { return pct >= 90 ? 'stop' : pct >= 70 ? 'caution' : 'safe'; }

const propFeatures = [
  { icon: <LayoutGrid size={18} />, label: 'Account dashboard', headline: 'Every account at a glance.', body: 'All your challenges and funded accounts on one screen - balance, today’s P&L, and live rule headroom with SAFE / CAUTION / STOP status.' },
  { icon: <Shield size={18} />, label: 'Rules engine', headline: 'Know your exact headroom.', body: 'Built-in rules for the top prop firms. Klynd Prop tracks daily loss, max drawdown, profit target, minimum days, and consistency in real time.' },
  { icon: <Bell size={18} />, label: 'Smart alerts', headline: 'Warned before you breach.', body: 'Push alerts at 80% of your daily limit, drawdown warnings, consistency flags, payout windows, and an 8am briefing across every account.' },
  { icon: <Database size={18} />, label: 'Prop firm database', headline: 'Every firm’s rules, in one place.', body: 'Search and compare firms by drawdown type, price, payout speed, and platform - kept current so you always trade the right rules.' },
  { icon: <Sparkles size={18} />, label: 'AI coach', headline: 'Find the pattern behind your fails.', body: 'Weekly AI reviews and pattern analysis over your own history: where you tilt, which firms suit your style, and what keeps blowing your challenges.' },
  { icon: <Wallet size={18} />, label: 'History & payouts', headline: 'Are you actually profitable?', body: 'Lifetime pass rate, total fees spent, payouts received, and net profit - the honest answer every serious prop trader needs.' },
];

const propFaqs: { q: string; a: string }[] = [
  { q: 'What is Klynd Prop?', a: 'Klynd Prop is a mobile command center for prop firm traders. It tracks every challenge and funded account in one place, monitors each firm’s rules in real time, alerts you before you breach a limit, and uses AI to surface the patterns behind your wins and losses.' },
  { q: 'Which prop firms does Klynd Prop support?', a: 'Klynd Prop includes a built-in, regularly updated rules database for the major prop firms including FTMO, Apex, Topstep, MyFundedFutures, FundingPips, and The Funded Trader, among others. You can also add any firm manually with custom rules.' },
  { q: 'Does it alert me before I break a rule?', a: 'Yes. Klynd Prop sends real-time push alerts at 80% of your daily loss limit, plus drawdown warnings, consistency-rule flags, minimum-trading-day reminders, payout-window notices, and a daily morning briefing across all your accounts.' },
  { q: 'Is Klynd Prop affiliated with FTMO, Apex, or other prop firms?', a: 'No. Klynd Prop is an independent personal tracking tool. Firm names are used only to identify which firm an account belongs to. Klynd Prop is not affiliated with, endorsed by, or partnered with any prop firm, and firm rules can change at any time - always verify with your firm.' },
  { q: 'Can Klynd Prop place trades on my accounts?', a: 'No. Klynd Prop is a journaling and rule-tracking tool only. It cannot place trades, move funds, or manage your accounts. You log trades manually or import them, and Klynd Prop tracks your rule headroom.' },
  { q: 'Is Klynd Prop free?', a: 'Yes. Klynd Prop has a free plan that tracks up to two accounts with the live rule dashboard and daily-limit alerts. Paid plans unlock unlimited accounts, all smart alerts, analytics, payout tracking, and AI coaching.' },
  { q: 'Does Klynd Prop work for futures and forex prop firms?', a: 'Yes. Klynd Prop supports futures, forex, and other prop firm models, including both trailing and static drawdown firms across the platforms traders use most.' },
];

// - prop phone screens (illustrative, React-rendered) -

function PropStatusBadge({ status }: { status: PropStatus }) {
  const label = status === 'safe' ? 'SAFE' : status === 'caution' ? 'CAUTION' : 'STOP';
  return <span className={`prop-badge prop-badge--${status}`}>{label}</span>;
}

function PropBar({ label, pct }: { label: string; pct: number }) {
  const status = propLevel(pct);
  return (
    <div className="prop-bar-row">
      <span className="prop-bar-label">{label}</span>
      <div className="prop-bar"><i className={`prop-bar-fill prop-bar-fill--${status}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      <span className="prop-bar-pct">{pct}%</span>
    </div>
  );
}

const PROP_ACCTS = [
  { mono: 'FT', color: '#2f6bff', name: 'FTMO',            size: '$100K · Phase 2', pnl: '+$1,420', up: true,  daily: 24,  dd: 38, status: 'safe' as PropStatus },
  { mono: 'AX', color: '#ff7a1a', name: 'Apex',            size: '$50K · Funded',   pnl: '+$210',   up: true,  daily: 76,  dd: 52, status: 'caution' as PropStatus },
  { mono: 'MF', color: '#0ab85e', name: 'MyFundedFutures', size: '$100K · Phase 1', pnl: '-$2,980', up: false, daily: 100, dd: 84, status: 'stop' as PropStatus },
];

function PropAcctCard({ a }: { a: (typeof PROP_ACCTS)[number] }) {
  return (
    <div className="prop-acct">
      <div className="prop-acct-head">
        <span className="prop-mono" style={{ background: a.color }}>{a.mono}</span>
        <div className="prop-acct-id">
          <strong>{a.name}</strong>
          <span>{a.size} · <b className={a.up ? 'prop-green' : 'prop-red'}>{a.pnl}</b></span>
        </div>
        <PropStatusBadge status={a.status} />
      </div>
      <PropBar label="Daily loss" pct={a.daily} />
      <PropBar label="Max drawdown" pct={a.dd} />
    </div>
  );
}

function PropDashboardScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 10 }}>
          <div>
            <p className="ds-label">All accounts</p>
            <p className="ds-month">Today</p>
          </div>
          <div className="ds-ai-chip"><Sparkles size={10} />Live</div>
        </div>
        <div className="prop-sum">
          <div className="prop-sum-item"><span className="ds-label">Combined</span><strong className="prop-red">-$1,350</strong></div>
          <div className="prop-sum-item"><span className="ds-label">Active</span><strong>4</strong></div>
          <div className="prop-sum-item"><span className="ds-label">Payout</span><strong>3d</strong></div>
        </div>
        <div className="prop-acct-list">
          {PROP_ACCTS.map((a) => <PropAcctCard key={a.name} a={a} />)}
        </div>
      </div>
    </div>
  );
}

function PropRulesScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 8 }}>
          <div>
            <p className="ds-label">FTMO · $100K</p>
            <p className="ds-month">Rule headroom</p>
          </div>
          <PropStatusBadge status="caution" />
        </div>
        <div className="prop-rule-hero">
          <span className="ds-label">Daily loss remaining</span>
          <strong className="prop-rule-big">$1,180</strong>
          <p>of $5,000 - one average loss from your limit.</p>
        </div>
        <div className="prop-rule-grid">
          <div><span className="ds-label">Max DD left</span><strong>$3,420</strong></div>
          <div><span className="ds-label">Profit target</span><strong>62%</strong></div>
          <div><span className="ds-label">Min days</span><strong>3 / 4</strong></div>
          <div><span className="ds-label">Consistency</span><strong className="prop-green">OK</strong></div>
        </div>
        <div className="prop-rule-bars">
          <PropBar label="Daily loss" pct={76} />
          <PropBar label="Max drawdown" pct={32} />
          <PropBar label="Profit target" pct={62} />
        </div>
      </div>
    </div>
  );
}

function PropAlertsScreen() {
  const alerts = [
    { tone: 'caution' as PropStatus, title: 'Apex $50K - 80% of daily limit used', time: '2m' },
    { tone: 'stop' as PropStatus,    title: 'MyFundedFutures - daily limit hit. Stop now.', time: '14m' },
    { tone: 'safe' as PropStatus,    title: 'Topstep - payout window open today', time: '1h' },
    { tone: 'safe' as PropStatus,    title: 'FundingPips - no trades logged in 3 days', time: '5h' },
  ];
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 10 }}>
          <div>
            <p className="ds-label">Protecting 4 accounts</p>
            <p className="ds-month">Alerts</p>
          </div>
          <div className="ds-ai-chip"><Bell size={10} />On</div>
        </div>
        <div className="prop-alert-list">
          {alerts.map((al, i) => (
            <div key={i} className={`prop-alert prop-alert--${al.tone}`}>
              <span className="prop-alert-dot" />
              <p className="prop-alert-title">{al.title}</p>
              <span className="prop-alert-time">{al.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PropAiPhoneScreen() {
  return (
    <div className="screen">
      <StatusBar />
      <div className="screen-pad">
        <div className="ds-header" style={{ marginBottom: 10 }}>
          <div>
            <p className="ds-label">Klynd AI</p>
            <p className="ds-month">Prop coach</p>
          </div>
          <div className="ds-ai-chip"><Sparkles size={10} />Live</div>
        </div>
        <div className="ai-screen-chat">
          <div className="ai-chat-user">Safe to keep trading FTMO today?</div>
          <div className="ai-chat-bot">
            <div className="ai-pattern-badge"><Sparkles size={8} />Risk check</div>
            <p className="ai-chat-bot-body">One more average losing trade (-$1,180) breaches your daily limit. Two of your last three fails started right here. Consider stopping for the day.</p>
          </div>
          <div className="ai-insight-row">
            <div className="ai-insight-item">
              <p className="ds-label">Headroom</p>
              <p className="ai-insight-val prop-amber">$1,180</p>
            </div>
            <div className="ai-insight-item">
              <p className="ds-label">Pattern</p>
              <p className="ai-insight-val prop-red">Mid-challenge tilt</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PROP_SCREENS: Record<string, () => ReactNode> = {
  'prop-dashboard': PropDashboardScreen,
  'prop-rules': PropRulesScreen,
  'prop-alerts': PropAlertsScreen,
  'prop-ai': PropAiPhoneScreen,
};

function PropPhoneFrame({ screen }: { screen: string }) {
  const Screen = PROP_SCREENS[screen] ?? PropDashboardScreen;
  return (
    <div className="phone-frame" aria-label={`${screen} screen preview`}>
      <div className="phone-island" />
      <div className="phone-btn-vol-up" />
      <div className="phone-btn-vol-dn" />
      <div className="phone-btn-power" />
      <div className="phone-screen">
        <Screen />
      </div>
    </div>
  );
}

function PropScreenNote() {
  return <p className="screen-disclaimer">Illustrative preview of Klynd Prop. Account data shown is for demonstration.</p>;
}

// - prop page sections -

function PropPage() {
  return (
    <div className="prop-page" data-product="prop">
      <PropHero />
      <PropFirmStrip />
      <PropFeatures />
      <PropAiSection />
      <PropFaq />
      <PropDisclaimer />
      <PropFirmsCallout />
      <SuiteBand current="prop" />
      <FinalCta product="prop" />
    </div>
  );
}

function PropHero() {
  return (
    <section className="hero">
      <div className="hero-copy">
        <motion.div
          className="hero-logo-mark"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
        >
          <img src="/logo-prop.svg" alt="Klynd Prop" />
        </motion.div>
        <motion.p className="eyebrow" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
          Prop firm command center
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}>
          Never blow a prop challenge again.
        </motion.h1>
        <motion.p className="hero-sub" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          Track every challenge and funded account in one place. Klynd Prop watches each firm’s rules in real time and warns you before you breach.
        </motion.p>
        <motion.div className="hero-badges" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}>
          <AppStoreBadge href={PROP_APP_STORE_URL} />
          <PlayStoreBadge href={PROP_PLAY_URL} />
        </motion.div>
      </div>
      <motion.div className="hero-phone" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
        <PropPhoneFrame screen="prop-dashboard" />
        <PropScreenNote />
      </motion.div>
    </section>
  );
}

function PropFirmStrip() {
  const items = [...PROP_FIRMS, ...PROP_FIRMS, ...PROP_FIRMS, ...PROP_FIRMS];
  return (
    <div className="prop-firmstrip-wrap">
      <div className="market-strip" aria-hidden="true">
        <div className="market-track">
          {items.map((m, i) => <span key={i} className="market-pill">{m}</span>)}
        </div>
      </div>
      <p className="prop-firmstrip-note">Rules tracked for the major prop firms · not affiliated with any firm</p>
    </div>
  );
}

function BentoDashboardPanel() {
  return (
    <div className="bento-panel">
      <div className="bento-sum">
        <div><span>Combined today</span><strong className="prop-red">-$1,350</strong></div>
        <div><span>Active</span><strong>4</strong></div>
        <div><span>Payout</span><strong>3d</strong></div>
      </div>
      {PROP_ACCTS.map((a) => (
        <div key={a.name} className="bento-acct">
          <div className="bento-acct-top">
            <span className="prop-mono" style={{ background: a.color }}>{a.mono}</span>
            <div className="bento-acct-id"><strong>{a.name}</strong><span>{a.size} · <b className={a.up ? 'prop-green' : 'prop-red'}>{a.pnl}</b></span></div>
            <PropStatusBadge status={a.status} />
          </div>
          <PropBar label="Daily" pct={a.daily} />
          <PropBar label="Drawdown" pct={a.dd} />
        </div>
      ))}
    </div>
  );
}

function PropFeatures() {
  const hero = propFeatures[0];
  const rest = propFeatures.slice(1);
  return (
    <section className="prop-features" id="prop-features">
      <div className="prop-features-head">
        <p className="eyebrow">What it does</p>
        <h2>The whole operation, under control.</h2>
        <p className="section-sub">Most prop traders run 3-10 accounts across different firms with different rules. Klynd Prop keeps all of it straight - and stops you breaching.</p>
      </div>
      <div className="prop-bento">
        <article className="bento-cell bento-hero">
          <div className="bento-hero-text">
            <div className="prop-feature-icon">{hero.icon}</div>
            <p className="prop-feature-label">{hero.label}</p>
            <h3>Every account on one screen.</h3>
            <p className="prop-feature-body">Balance, today’s P&amp;L, and live rule headroom for every challenge and funded account - each with a SAFE / CAUTION / STOP badge so you always know exactly where you stand.</p>
          </div>
          <BentoDashboardPanel />
        </article>
        {rest.map((f) => (
          <article key={f.label} className="bento-cell">
            <div className="prop-feature-icon">{f.icon}</div>
            <p className="prop-feature-label">{f.label}</p>
            <h3>{f.headline}</h3>
            <p className="prop-feature-body">{f.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function PropAiSection() {
  return (
    <section className="prop-ai-section" id="prop-ai">
      <div className="prop-ai-head">
        <p className="eyebrow">AI coach</p>
        <h2>It learns why you fail.</h2>
        <p className="section-sub">
          Klynd Prop’s AI reviews your own history across every account - predicting breaches before they happen and naming the patterns that keep ending your challenges.
        </p>
      </div>
      <div className="prop-ai-stage">
        <div className="prop-ai-phone prop-ai-phone--alerts">
          <PropPhoneFrame screen="prop-alerts" />
        </div>
        <div className="prop-ai-phone prop-ai-phone--ai">
          <PropPhoneFrame screen="prop-ai" />
        </div>
      </div>
    </section>
  );
}

function PropFaq() {
  return (
    <section id="prop-faq" className="faq-section" aria-labelledby="prop-faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="prop-faq-title">Prop firm tracking, answered.</h2>
        <p className="section-sub faq-sub">Everything about using Klynd Prop to protect your challenges and funded accounts.</p>
        <FaqAccordion items={propFaqs} />
      </div>
    </section>
  );
}

function PropDisclaimer() {
  return (
    <div className="prop-disclaimer-wrap">
      <p className="prop-disclaimer">
        Prop firm rules can change at any time - always verify the current rules directly with your firm. Klynd Prop is a personal tracking and analytics tool, not a broker or prop firm. Firm names are used for identification only; Klynd Prop is not affiliated with, endorsed by, or partnered with any prop firm.
      </p>
    </div>
  );
}

// ─── recommended prop firms page (/prop-firms) ──────────────────────────────────

function PropFirmsPage() {
  return (
    <div className="propfirms-page" data-product="prop-firms">
      <PropFirmsHero />
      <PropFirmsGrid />
      <PropFirmsHowWeChoose />
      <PropFirmsDisclaimer />
      <PropFirmsFaq />
      <FinalCta />
    </div>
  );
}

function PropFirmsHero() {
  return (
    <section className="propfirms-hero">
      <div className="propfirms-hero-bg" aria-hidden="true" />
      <motion.p
        className="eyebrow propfirms-hero-eyebrow"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        Recommended prop firms
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.08 }}
      >
        Get funded with the firms
        <br />
        <span className="propfirms-hero-accent">we recommend.</span>
      </motion.h1>
      <motion.p
        className="hero-sub propfirms-hero-sub"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.16 }}
      >
        A short, hand-picked list of the prop trading firms the Klynd team would point a trader to,
        vetted for reputation, payout reliability, and clear rules. Pass the challenge, get funded,
        trade their capital.
      </motion.p>
      <motion.div
        className="propfirms-disclosure"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.24 }}
      >
        <Shield size={14} strokeWidth={2} aria-hidden="true" />
        <span>
          Affiliate disclosure: Klynd earns a commission if you sign up through these links, at no
          extra cost to you. We only list firms we would recommend anyway.
        </span>
      </motion.div>
    </section>
  );
}

function PropFirmsGrid() {
  return (
    <section className="propfirms-grid-section" aria-label="Recommended prop firms">
      <div className="propfirms-grid">
        {RECOMMENDED_FIRMS.map((firm, i) => (
          <FirmCard key={firm.slug} firm={firm} index={i} />
        ))}
      </div>
    </section>
  );
}

function FirmCard({ firm, index }: { firm: RecommendedFirm; index: number }) {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [3.2, -3.2]), { stiffness: 180, damping: 24 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-3.6, 3.6]), { stiffness: 180, damping: 24 });
  const glowX = useTransform(mx, (v) => `${v * 100}%`);
  const glowY = useTransform(my, (v) => `${v * 100}%`);

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'mouse') return;
    const rect = event.currentTarget.getBoundingClientRect();
    mx.set((event.clientX - rect.left) / rect.width);
    my.set((event.clientY - rect.top) / rect.height);
  };
  const onPointerLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.article
      className="firm-card"
      style={{ rotateX, rotateY, '--firm-accent': firm.accent } as Record<string, unknown>}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="firm-glow"
        aria-hidden="true"
        style={{ '--gx': glowX, '--gy': glowY } as Record<string, unknown>}
      />
      <div className="firm-card-top">
        <span className="firm-pick"><Shield size={12} strokeWidth={2.2} aria-hidden="true" /> Klynd Pick</span>
      </div>
      <h2 className="firm-name">{firm.name}</h2>
      <p className="firm-tagline">{firm.tagline}</p>
      <div className="firm-markets">
        {firm.markets.map((m) => <span key={m} className="firm-market">{m}</span>)}
      </div>
      {firm.couponCode && <FirmCoupon code={firm.couponCode} />}
      <a
        className="firm-cta"
        href={firm.affiliateUrl}
        target="_blank"
        rel="sponsored nofollow noopener noreferrer"
      >
        Visit {firm.name} <ArrowRight size={15} aria-hidden="true" />
      </a>
    </motion.article>
  );
}

function FirmCoupon({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — the code is still visible to copy by hand */
    }
  };
  return (
    <div className="firm-coupon-wrap">
      <button type="button" className="firm-coupon" onClick={copy} aria-label={`Copy discount code ${code}`}>
        <span className="firm-coupon-label">Code</span>
        <span className="firm-coupon-code">{code}</span>
        <span className="firm-coupon-act">
          {copied ? <Check size={13} strokeWidth={2.5} aria-hidden="true" /> : <Copy size={13} strokeWidth={2} aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </span>
      </button>
      <span className="firm-coupon-hint">Apply at checkout — using this may unlock a discount.</span>
    </div>
  );
}

function PropFirmsHowWeChoose() {
  const points = [
    { icon: <Shield size={18} />, title: 'Reputation first', body: 'Established firms with a real payout track record and a community that actually gets paid, not the newest name with the biggest bonus.' },
    { icon: <Wallet size={18} />, title: 'Payouts that land', body: 'We favor firms known for paying on time and honoring their terms, so a passed challenge actually turns into money in your account.' },
    { icon: <Target size={18} />, title: 'Clear, fair rules', body: 'Transparent evaluation rules and drawdown models you can plan around, published openly on the firm’s own site.' },
    { icon: <Globe2 size={18} />, title: 'Markets that fit you', body: 'Forex, futures, indices, metals, crypto, coverage across the firms so you can match a firm to the markets you actually trade.' },
  ];
  return (
    <section className="propfirms-choose" aria-labelledby="propfirms-choose-title">
      <div className="propfirms-choose-inner">
        <p className="eyebrow">How we choose</p>
        <h2 id="propfirms-choose-title">Why these firms make the list.</h2>
        <p className="section-sub propfirms-choose-sub">
          We are an independent affiliate, so we only put our name next to firms we would trust with a
          challenge ourselves. Here is what earns a spot.
        </p>
        <div className="propfirms-choose-grid">
          {points.map((p) => (
            <div key={p.title} className="propfirms-choose-card">
              <span className="propfirms-choose-icon">{p.icon}</span>
              <strong className="propfirms-choose-card-title">{p.title}</strong>
              <p className="propfirms-choose-card-body">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PropFirmsDisclaimer() {
  return (
    <div className="propfirms-disclaimer-wrap">
      <p className="propfirms-disclaimer">
        <strong>Affiliate disclosure &amp; risk warning.</strong> Klynd is an independent affiliate of
        the firms listed above and earns a commission when you sign up through our links, at no extra
        cost to you. Klynd is not partnered with, endorsed by, or an official representative of any prop
        firm; the &ldquo;Klynd Pick&rdquo; label reflects our own opinion only. Prop firm evaluations are
        difficult to pass and trading carries a significant risk of loss, and most challenge accounts do
        not result in a payout. Nothing here is financial advice. Firm prices, rules, profit splits, and
        payout terms change often and are not shown here, so always verify the current terms on each
        firm’s own website before purchasing.
      </p>
    </div>
  );
}

function PropFirmsFaq() {
  return (
    <section id="faq" className="faq-section" aria-labelledby="propfirms-faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="propfirms-faq-title">Recommended prop firms, explained.</h2>
        <p className="section-sub faq-sub">How Klynd picks firms, how we make money, and what to check before you buy.</p>
        <FaqAccordion items={propFirmsFaqs} />
      </div>
    </section>
  );
}

// Contextual cross-link to /prop-firms, dropped into the homepage and each product page.
function PropFirmsCallout() {
  return (
    <section className="propfirms-callout" aria-labelledby="propfirms-callout-title">
      <a className="propfirms-callout-inner" href="/prop-firms">
        <div className="propfirms-callout-text">
          <p className="eyebrow">Recommended prop firms</p>
          <h2 id="propfirms-callout-title">Ready to get funded?</h2>
          <p className="section-sub">
            See the prop trading firms the Klynd team recommends, vetted for reputation, payouts, and rules.
          </p>
        </div>
        <span className="propfirms-callout-cta">View firms <ArrowRight size={16} aria-hidden="true" /></span>
      </a>
    </section>
  );
}

// ─── utility pages ────────────────────────────────────────────────────────────

function PricingPage() {
  return (
    <section className="route-section pricing-section">
      <div className="route-head">
        <p className="eyebrow">Pricing</p>
        <h1>Start free. Scale when you're serious.</h1>
        <p className="section-sub">Klynd Journal and Klynd Prop start free, with paid tiers for deeper analytics and AI. Klynd Academy's learning path is free.</p>
      </div>
      <PricingCards />
    </section>
  );
}

function annualSavingsPct(tier: { monthly: string; annual: string }) {
  const m = parseFloat(tier.monthly.replace(/[^0-9.]/g, ''));
  const a = parseFloat(tier.annual.replace(/[^0-9.]/g, ''));
  if (!m || !a) return 0;
  return Math.round((1 - a / (m * 12)) * 100);
}

function PricingCards({ initialProduct = 'journal', showToggle = true }: { initialProduct?: PricedProductKey; showToggle?: boolean }) {
  const [billing, setBilling] = useState<Billing>('monthly');
  const [product, setProduct] = useState<PricedProductKey>(initialProduct);
  const tiers = product === 'prop' ? propPricingTiers : pricingTiers;
  return (
    <>
      {showToggle && (
        <div className="product-toggle">
          <button type="button" className={product === 'journal' ? 'active' : ''} onClick={() => setProduct('journal')}>Klynd Journal</button>
          <button type="button" className={product === 'prop' ? 'active' : ''} onClick={() => setProduct('prop')}>Klynd Prop</button>
        </div>
      )}
      <div className="billing-toggle">
        <button type="button" className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>Monthly</button>
        <button type="button" className={billing === 'annual'  ? 'active' : ''} onClick={() => setBilling('annual')}>Annual</button>
      </div>
      <div className="pricing-grid">
        {tiers.map(tier => (
          <div key={tier.name} className={`pricing-card${tier.featured ? ' featured' : ''}`}>
            {billing === 'annual' && annualSavingsPct(tier) > 0 && (
              <span className="tier-corner-save">-{annualSavingsPct(tier)}%</span>
            )}
            <div className="tier-top">
              <span className="tier-name">{tier.name}</span>
              {tier.featured && <span className="tier-badge">Popular</span>}
            </div>
            <p className="tier-summary">{tier.summary}</p>
            <div className="tier-price">
              <strong>{billing === 'monthly' ? tier.monthly : tier.annual}</strong>
              <span>{billing === 'monthly' ? tier.monthlyNote : tier.annualNote}</span>
            </div>
            <ul className="tier-list">
              {tier.items.map(item => (
                <li key={item}><Check size={13} strokeWidth={2.5} /><span>{item}</span></li>
              ))}
            </ul>
            <a className="tier-cta" href="/download">Choose {tier.name} <ArrowRight size={13} /></a>
          </div>
        ))}
      </div>
    </>
  );
}

type LegalSectionData = {
  title: string;
  body: ReactNode;
};

const privacySections: LegalSectionData[] = [
  {
    title: '1. Who we are',
    body: (
      <>
        <p>Klynd is a suite of trading tools - including <strong>Klynd Journal</strong> (a trading journal and analytics app), <strong>Klynd Prop</strong> (a prop firm account and rule tracker), and <strong>Klynd Academy</strong> (a free trading education and community app) - available at klynd.trade and through the Klynd mobile apps. Klynd is owned and operated by <strong>ANSY GAMES (SMC-PRIVATE) LIMITED</strong>, a single-member private limited company incorporated under the Companies Act, 2017 of the Islamic Republic of Pakistan ("ANSY Games", the "Company", "we", "us", "our", or the "Operator"). The Company is the data controller for personal data processed in connection with the service.</p>
        <p>For privacy requests, legal notices, or support, contact <a href="mailto:support@klynd.trade">support@klynd.trade</a>. For general operational email, contact <a href="mailto:info@klynd.trade">info@klynd.trade</a>.</p>
        <p className="legal-note">If the Company is later restructured, merged, acquired, or replaced by a successor entity, the new entity may assume the role of data controller. We will update this policy when that happens.</p>
      </>
    ),
  },
  {
    title: '2. What Klynd does',
    body: (
      <>
        <p>Klynd helps users record historical trading activity, organize notes and screenshots, import or sync past trades from selected broker or exchange integrations, view performance analytics, receive optional AI-generated summaries, and optionally publish limited account statistics through public profiles or leaderboards.</p>
        <p>Klynd Prop additionally lets users track prop firm challenge and funded accounts, monitor firm rule limits such as daily loss and maximum drawdown, receive rule-related alerts, reference a directory of prop firm information, and optionally submit reviews of prop firms. Prop firm rule data is provided for tracking convenience only, may be outdated or incorrect, and is not a substitute for the firm’s own current terms.</p>
        <p>Klynd Academy additionally provides educational course content, quizzes, progress tracking, language preferences, community discussions, market-context discussions, and prop-firm research or comparison content. Academy content and community content may be incomplete, outdated, unreviewed, user-generated, AI-assisted, or not refreshed on any schedule.</p>
        <p>Klynd is not a broker, exchange, investment adviser, financial adviser, legal adviser, tax adviser, portfolio manager, signal provider, copy-trading service, or order execution platform. Klynd does not place trades, hold client money, custody assets, manage portfolios, or tell users what to buy, sell, hold, or avoid.</p>
      </>
    ),
  },
  {
    title: '3. Personal data we collect',
    body: (
      <>
        <p>Depending on how you use the service, we may process the following categories of personal data:</p>
        <ul>
          <li><strong>Account and authentication data:</strong> email address, internal user identifier, authentication state, account creation time, login state, selected display currency, app preferences, and subscription tier. Passwords are never stored in plain text.</li>
          <li><strong>Trading journal data:</strong> trading accounts, symbols, side, open and close times, entry and exit prices, quantity, fees, profit and loss, currency, notes, tags, trade categories, imported rows, external trade IDs, and timestamps.</li>
          <li><strong>Screenshots and attachments:</strong> screenshots you attach to trades, plus private storage paths and metadata needed to display, remove, and secure those files.</li>
          <li><strong>Broker and integration data:</strong> broker or exchange connection labels, account IDs, connection status, last sync time, errors, OAuth tokens, API keys or secrets, webhook secrets, wallet addresses, and CSV import content. Broker credentials are stored encrypted and are not included in user data exports.</li>
          <li><strong>AI feature data:</strong> AI prompts, AI chat messages, compressed trade summaries sent for reports or trading plans, AI-generated outputs, token usage, model name, feature name, and cost logs.</li>
          <li><strong>Subscription and purchase data:</strong> RevenueCat customer identifiers, app store purchase events, product IDs, entitlement status, renewals, cancellations, expirations, refunds, and related timestamps. We do not receive your full payment card number from Apple, Google, or RevenueCat.</li>
          <li><strong>Referral, affiliate, and payout data:</strong> referral codes, attribution records, partner applications, channel URLs, audience information, commission events, payout requests, payout method, payout destination, and anti-abuse flags.</li>
          <li><strong>Public profile and leaderboard data:</strong> public handle, account display name, public bio, public trading stats, public trade fields you choose to publish, and leaderboard eligibility settings.</li>
          <li><strong>Prop account and firm-review data (Klynd Prop):</strong> the prop firm accounts you track, their associated firm, plan, phase, and rule limits, your rule-event and breach history, payout records you log, and any prop firm reviews you submit.</li>
          <li><strong>Education, language, and community data (Klynd Academy):</strong> course progress, selected track, lesson completion, quiz answers, XP, streaks, certificates or completion status, selected language, community posts, comments, reactions, discussion level, moderation records, and market-context or prop-firm discussion content you submit or view.</li>
          <li><strong>Device and notification data:</strong> Expo push token after you grant notification permission, notification preferences, risk-warning settings, device-local preferences, and limited technical data needed to operate the app.</li>
          <li><strong>Website and waitlist data:</strong> email address, selected platform interest, page URL, submitted message content, and basic server logs when you join the waitlist or contact us.</li>
          <li><strong>Support communications:</strong> messages you send to us, metadata needed to reply, and records of the issue until it is resolved or no longer needed.</li>
        </ul>
      </>
    ),
  },
  {
    title: '4. Data we do not collect',
    body: (
      <>
        <p>Klynd is not designed to collect precise location, advertising identifiers, biometric identifiers, government ID numbers, payment card numbers, bank login credentials, private crypto keys, seed phrases, or trading permissions that allow withdrawals or order placement.</p>
        <p>Do not submit special-category personal data, third-party confidential data, private keys, recovery phrases, bank statements, medical records, or other sensitive information in notes, screenshots, CSV files, AI prompts, or support messages. If you submit such information, you bear sole responsibility for any consequences. We will process it only to the extent necessary to operate the service and take on no special handling obligations for data you volunteer outside the scope of normal use.</p>
      </>
    ),
  },
  {
    title: '5. Why we process data and our legal bases',
    body: (
      <>
        <p>For users in the European Economic Area, United Kingdom, or Switzerland, we rely on the following legal bases under the GDPR or similar laws:</p>
        <ul>
          <li><strong>Contract:</strong> to create your account, provide the app, store your journal, sync selected integrations, track Academy progress, operate community features, generate exports, manage subscriptions, respond to support, and deliver paid features.</li>
          <li><strong>Consent:</strong> for optional push notifications, optional public profile publication, optional leaderboard participation, optional broker connections, optional AI features, and marketing or waitlist messages where consent is required.</li>
          <li><strong>Legitimate interests:</strong> to secure the service, prevent fraud and abuse, maintain logs, improve reliability, enforce terms, measure feature usage, operate referrals, and protect Klynd and users.</li>
          <li><strong>Legal obligation:</strong> to keep records required by tax, accounting, consumer protection, payment, or legal-dispute rules.</li>
          <li><strong>Legal claims:</strong> to establish, exercise, or defend legal claims, including misuse, chargebacks, affiliate abuse, security incidents, or disputes.</li>
        </ul>
      </>
    ),
  },
  {
    title: '6. AI features',
    body: (
      <>
        <p>Some Klynd features use AI models through third-party AI providers, processed via secure server-side functions. These features may include AI chat, weekly reports, pattern detection, suggested tags, trading plans, risk warnings, and similar analysis based on your own historical data.</p>
        <p><strong>Your consent comes first.</strong> AI features are optional. Before any of your data is sent to an AI provider, the app asks for your explicit consent and identifies the provider that will receive it. Nothing is sent until you agree, and you can turn AI data sharing off at any time in the app's settings.</p>
        <p>We send only the information needed for the feature. Depending on the feature, that may include your prompt, prior chat messages, symbol, side, profit and loss, time, selected notes, and compressed summaries of your trading history. We do not send broker credentials, withdrawal credentials, payment card details, or authentication passwords to AI providers.</p>
        <p>Our AI providers process this data only to return the requested result, under contractual terms that require appropriate protection of your data, and do not use it to train their models.</p>
        <p>AI output is generated content. It may be incomplete, outdated, misleading, or wrong. It is not financial advice, investment research, trading advice, a recommendation, or a substitute for independent judgment or professional advice. You assume all risk from any reliance on AI output. AI features do not make decisions with legal or similarly significant effects about you.</p>
      </>
    ),
  },
  {
    title: '7. Broker, exchange, webhook, and CSV integrations',
    body: (
      <>
        <p>If you connect a broker, exchange, wallet, webhook, or CSV import, we process the data needed to validate, store, sync, and display that integration. Supported integrations may include traditional brokers, crypto exchanges, blockchain wallet addresses, webhook sources, and CSV imports from various platforms.</p>
        <p>You should only provide read-only API credentials. Klynd is designed for journaling and analysis, not order execution. Even if a third-party platform offers broader permissions, you are responsible for restricting credentials so they cannot trade, transfer, withdraw, or manage funds.</p>
      </>
    ),
  },
  {
    title: '8. Public profiles and leaderboard',
    body: (
      <>
        <p>Public profiles and leaderboard features are optional. If you enable them, selected information such as your handle, account display name, public bio, public trading statistics, and optionally individual trade fields may be visible to anyone, including people who are not signed in.</p>
        <p>You can disable public sharing in the app. Information already viewed, copied, cached, indexed, screenshotted, or shared by others may remain outside Klynd's control.</p>
      </>
    ),
  },
  {
    title: '9. Sharing with processors and third parties',
    body: (
      <>
        <p>We use service providers only as needed to run Klynd. These may include:</p>
        <ul>
          <li><strong>Supabase:</strong> authentication, database, file storage, and account management.</li>
          <li><strong>OpenAI:</strong> optional AI processing for AI-enabled features.</li>
          <li><strong>RevenueCat, Apple, and Google:</strong> subscriptions, purchase processing, entitlement status, refunds, and app distribution.</li>
          <li><strong>Expo, Apple, and Google:</strong> push notification delivery when enabled.</li>
          <li><strong>Cloudflare:</strong> website hosting, DNS, security, and server logs.</li>
          <li><strong>Resend:</strong> waitlist and operational email notifications.</li>
          <li><strong>Selected brokers, exchanges, wallet networks, and TradingView:</strong> integration features you choose to use.</li>
          <li><strong>Payout providers:</strong> Wise, PayPal, crypto wallet networks, or other payout methods if you request affiliate payouts.</li>
          <li><strong>Professional advisers or authorities:</strong> accountants, lawyers, tax advisers, auditors, courts, regulators, or law enforcement where legally required or necessary to protect rights.</li>
        </ul>
        <p>We do not sell your personal data. We do not use third-party advertising networks. This policy will be updated if that changes materially.</p>
        <p className="legal-note">We may add, replace, or remove providers within these categories from time to time. Where a change materially affects how your personal data is processed, we will reflect it in this policy.</p>
      </>
    ),
  },
  {
    title: '10. International transfers',
    body: (
      <>
        <p>Klynd is operated from Pakistan, and our processors and infrastructure may process data in Pakistan, the European Economic Area, the United Kingdom, the United States, and other countries. Where required by applicable law, we rely on safeguards such as adequacy decisions, Standard Contractual Clauses, data processing agreements, security measures, and processor commitments.</p>
        <p>By using Klynd from outside Pakistan, you acknowledge and consent to the transfer, processing, and storage of your personal data in Pakistan and in any other country where our processors operate, including countries whose data protection laws may differ from those in your country of residence.</p>
        <p>Your hosting region, app store platform, AI provider, payment platform, and broker integrations may affect where particular data is processed.</p>
      </>
    ),
  },
  {
    title: '11. Security',
    body: (
      <>
        <p>We use technical and organizational measures intended to protect personal data, including database-level access controls, private encrypted storage, encrypted broker credentials, server-side processing, least-privilege access patterns, secure session handling, and validation of sensitive operations.</p>
        <p>No internet service is perfectly secure. You are responsible for protecting your email account, device, password, two-factor authentication, API keys, broker accounts, and app store account.</p>
        <p><strong>Breach notification.</strong> If we become aware of a personal data breach that affects your personal data, we will assess its risk and, where required by applicable law, notify the competent supervisory authority and affected users without undue delay and within the timeframes required by that law.</p>
      </>
    ),
  },
  {
    title: '12. Cookies, local storage, and similar technologies',
    body: (
      <>
        <p>The klynd.trade website does not use third-party advertising cookies, cross-site tracking cookies, or third-party analytics cookies. The website stores a small number of strictly necessary values in your browser's local storage, including a record of the cookie preferences you selected in our consent banner, and a flag recording that you already joined the waitlist (so we do not show you the form again). These values stay on your device and are not used to track you across other websites.</p>
        <p>Our hosting and security provider (Cloudflare) may set strictly necessary cookies and keep short-lived server logs (such as IP address and request metadata) for security, bot mitigation, load balancing, and abuse prevention. These are required for the website to operate safely.</p>
        <p>The Klynd mobile apps store device-local preferences (such as language, theme, notification, and risk-warning settings) on your device so the app works as configured.</p>
        <p>You can review these choices anytime using the "Cookie settings" link in the site footer, or by clearing local storage and cookies in your browser or device settings; doing so may re-display notices or forms you previously dismissed. If we ever add analytics, marketing, or other non-essential cookies, we will update this policy and request consent where applicable law requires it.</p>
      </>
    ),
  },
  {
    title: '13. Retention',
    body: (
      <>
        <p>We keep personal data only for as long as needed for the purposes described in this policy, unless a longer period is required by law or needed for legal claims.</p>
        <ul>
          <li>Account, profile, trading, screenshot, tag, broker connection, AI artifact, and preference data are generally kept until you delete them or delete your account.</li>
          <li>Subscription, refund, tax, accounting, fraud-prevention, affiliate, and payout records may be kept for statutory recordkeeping periods and legal claims.</li>
          <li>AI usage logs may be kept to enforce plan limits, calculate costs, debug abuse, and document usage.</li>
          <li>Waitlist and marketing contact data are kept until you unsubscribe, request deletion, or the purpose expires.</li>
          <li>Backups and logs may persist for a limited period after deletion before being overwritten or purged.</li>
        </ul>
      </>
    ),
  },
  {
    title: '14. Your GDPR and privacy rights',
    body: (
      <>
        <p>Subject to legal limits, you may have the right to access, correct, delete, restrict, object to processing, receive a portable copy of your data, withdraw consent, and complain to a supervisory authority. We will respond within the timeframes required by applicable law, generally within one month under GDPR.</p>
        <p>The app includes data export and account deletion controls. You can also email <a href="mailto:support@klynd.trade">support@klynd.trade</a>. We may ask for information needed to verify that the request is really from you.</p>
        <p>If you are located in the European Economic Area, the United Kingdom, or another jurisdiction with a competent data protection authority, you may have the right to complain to your local supervisory authority. Rights are exercised under, and limited by, the applicable law of your jurisdiction.</p>
      </>
    ),
  },
  {
    title: '15. US state privacy rights',
    body: (
      <>
        <p>If you are a resident of California or another US state with a comprehensive consumer privacy law (such as the CCPA/CPRA, or the privacy laws of Virginia, Colorado, Connecticut, Utah, or Texas), the following applies to you in addition to the rest of this policy.</p>
        <p><strong>No sale or sharing.</strong> We do not sell your personal information, and we do not share it for cross-context behavioral advertising, as those terms are defined under the CCPA/CPRA. We have not done so in the preceding 12 months. We do not use or disclose sensitive personal information for purposes that require a right to limit under the CCPA/CPRA, and we do not engage in profiling that produces legal or similarly significant effects.</p>
        <p><strong>Your rights.</strong> Subject to legal limits and verification, you may have the right to: know and access the personal information we hold about you; correct inaccurate personal information; delete personal information; receive a portable copy; and not be discriminated against for exercising these rights. The categories of personal information we collect, the purposes, and the recipients are described in sections 3, 5, and 9 of this policy.</p>
        <p><strong>How to exercise them.</strong> Use the in-app export and deletion controls, or email <a href="mailto:support@klynd.trade">support@klynd.trade</a>. We may ask for information needed to verify your request. You may use an authorized agent where your state's law allows it; we may require proof of the agent's authority and verification of your identity.</p>
      </>
    ),
  },
  {
    title: '16. Account deletion and export',
    body: (
      <>
        <p>Klynd's app includes an export feature intended to provide a machine-readable JSON copy of your account-related data. Broker credentials and secrets are deliberately excluded from exports. You are solely responsible for exporting your data before deleting your account or before any service shutdown.</p>
        <p>Account deletion removes your authentication credentials and cascades to delete associated data stored by the service, including your profile, trades, integrations, AI history, and related records, subject to backups and any legally required retention periods.</p>
        <p>If the service is discontinued, data may be permanently deleted. Klynd does not guarantee backups and has no obligation to restore or recover lost data. Klynd is not responsible for data loss resulting from account deletion, service shutdown, expiry of the service, or any other cause. You are responsible for keeping your own independent backups at all times.</p>
      </>
    ),
  },
  {
    title: '17. Children',
    body: (
      <p>Klynd is not intended for children. You must be at least 18 years old, or the age of legal majority in your country, to use Klynd. We do not knowingly collect personal data from children.</p>
    ),
  },
  {
    title: '18. Changes',
    body: (
      <>
        <p>We may update this policy as Klynd changes, as the law changes, or as the Company's operations change. If changes are material, we will take reasonable steps to notify users through the app, website, or email. You are responsible for reviewing the current policy from time to time.</p>
        <p>Your continued use of Klynd after an updated policy becomes effective means the updated policy applies to future processing.</p>
      </>
    ),
  },
];

const termsSections: LegalSectionData[] = [
  {
    title: '1. Acceptance and operator',
    body: (
      <>
        <p>These Terms of Service ("Terms") govern your access to and use of Klynd, including klynd.trade, the Klynd Journal, Klynd Prop, and Klynd Academy mobile apps, related APIs, waitlist forms, subscriptions, referral features, public profiles, AI features, education content, quizzes, community features, market-context discussions, prop firm tracking and reviews, and support services.</p>
        <p>Klynd is owned and operated by <strong>ANSY GAMES (SMC-PRIVATE) LIMITED</strong>, a single-member private limited company incorporated under the Companies Act, 2017 of the Islamic Republic of Pakistan ("ANSY Games", the "Company", "we", "us", "our", or the "Operator"). References to "Klynd" mean the service; references to the Operator mean the Company.</p>
        <p>By creating an account, joining the waitlist, connecting an integration, purchasing a subscription, downloading, installing, or using Klynd in any way, you confirm that you have read, understood, and unconditionally agree to be bound by these Terms and the Privacy Policy. If you do not agree, you must not use Klynd in any way.</p>
      </>
    ),
  },
  {
    title: '2. Eligibility',
    body: (
      <p>You must be at least 18 years old and legally able to enter into these terms. If you use Klynd for a business, fund, trading group, or other organization, you confirm that you have authority to bind that organization and that "you" includes both you and that organization.</p>
    ),
  },
  {
    title: '3. The service',
    body: (
      <>
        <p>Klynd is a personal journaling, recordkeeping, visualization, analytics, education, and community tool for your own historical trading data and learning activity. It is a record-keeping and education tool only - not a financial service, trading platform, investment product, advisory product, legal service, tax service, or regulated service of any kind. Features may include manual trade logging, multi-account tracking, CSV import, broker or exchange sync, wallet tracking, TradingView webhook imports, screenshots, tags, notes, calendar views, statistics, charts, AI summaries, AI chat, trading plans, weekly reports, risk-warning notifications, public profiles, leaderboards, subscriptions, education lessons, quizzes, XP, streaks, certificates, language selection, community discussions, market-context discussions, and referral or affiliate tools.</p>
        <p>Features, lessons, quizzes, course tracks, translations, market-context posts, community discussions, prop-firm information, and other content may be experimental, incomplete, unavailable, region-limited, plan-limited, delayed, inaccurate, outdated, stale, not refreshed, or changed without notice. Product previews and screenshots are illustrative only.</p>
        <p><strong>Klynd Academy, education content, and community.</strong> Klynd Academy provides general educational content and community discussion only. Course content, quiz answers, examples, translations, community posts, market commentary, and prop-firm research may be wrong, outdated, incomplete, user-generated, AI-assisted, unmoderated, or not reviewed by a qualified professional. Klynd has no duty to update, refresh, verify, monitor, or correct Academy content or community content, and you rely on it entirely at your own risk.</p>
        <p><strong>Klynd Prop, prop firm data, and reviews.</strong> Klynd Prop additionally lets you track prop firm challenge and funded accounts and monitor firm rule limits. Any prop firm rules, account sizes, prices, payout terms, or other firm data shown in Klynd are informational only, may be inaccurate or out of date, and are not the firm’s official terms - you must verify the current rules directly with the firm before relying on them. Firm names and marks are used for identification (nominative) purposes only; Klynd is not affiliated with, endorsed by, sponsored by, or partnered with any prop firm. Prop firm reviews are user-generated content submitted by other users; they are not verified, endorsed, or adopted by Klynd, and Klynd may moderate, remove, rank, or refuse any review at its discretion.</p>
      </>
    ),
  },
  {
    title: '4. No financial, legal, tax, or regulated advice',
    body: (
      <>
        <p>Klynd is not a broker, dealer, exchange, investment adviser, financial adviser, tax adviser, legal adviser, portfolio manager, asset manager, signal provider, copy-trading service, robo-adviser, education credentialing institution, or regulated investment service. Klynd does not hold any financial services licence, investment adviser registration, legal-practice licence, tax-adviser licence, education accreditation, or regulatory authorisation in any jurisdiction.</p>
        <p>Klynd does not provide investment advice, trading advice, personal recommendations, investment research, suitability assessments, portfolio management, risk management, financial planning, tax advice, legal advice, professional education advice, or advice to buy, sell, hold, short, leverage, hedge, or avoid any asset, security, derivative, currency, cryptocurrency, commodity, strategy, broker, prop firm, or trading venue.</p>
        <p>All information in Klynd, including Academy courses, quizzes, translations, community discussions, market-context posts, prop-firm research, AI output, and app copy, is for journaling, historical recordkeeping, visualization, general education, and self-review only. You alone are responsible for every trading, investment, tax, legal, financial, education, community, prop-firm, and account decision you make.</p>
      </>
    ),
  },
  {
    title: '5. Trading risk',
    body: (
      <>
        <p>Trading and investing involve substantial risk. You can lose some or all of your capital. Leveraged products, derivatives, futures, options, forex, crypto assets, margin trading, and prop-firm accounts may involve extreme volatility and rapid losses.</p>
        <p>Past performance, historical win rate, profit factor, backfilled records, journal analysis, Academy course progress, quiz results, XP, streaks, certificates, community discussion, market-context posts, AI output, charts, public leaderboard results, or other Klynd content do not predict future results. No Klynd feature guarantees profit, risk reduction, compliance with prop-firm rules, a funded account, a payout, tax accuracy, legal accuracy, improved performance, course correctness, or any trading outcome.</p>
      </>
    ),
  },
  {
    title: '6. Your account and data accuracy',
    body: (
      <>
        <p>You are responsible for your account, password, device security, API keys, broker accounts, app store account, two-factor authentication, and all activity under your credentials.</p>
        <p>You are responsible for the accuracy, completeness, legality, and appropriateness of data you enter, import, sync, publish, or submit to Klynd. Klynd may calculate statistics from your data, but those calculations can be affected by missing trades, broker errors, imported CSV formatting, fees, corporate actions, time zones, currency conversion, exchange outages, manual overrides, or software bugs.</p>
        <p>You are solely responsible for maintaining your own copies of your data. Klynd provides an export feature as a convenience but does not guarantee data availability, completeness, or long-term preservation. Do not rely on Klynd as your sole record of trading activity.</p>
        <p><strong>No backup or preservation duty.</strong> To the maximum extent permitted by law, Klynd has no obligation to store, retain, back up, restore, recover, or preserve your data, and may modify, delete, lose, or make it unavailable at any time, with or without notice. You alone are responsible for independently and regularly backing up your data, and you assume all risk of any loss, deletion, corruption, or unavailability of data however caused. Klynd is not liable for any such loss.</p>
      </>
    ),
  },
  {
    title: '7. Broker, exchange, wallet, webhook, and CSV integrations',
    body: (
      <>
        <p>Integrations are optional. If you connect an integration, you authorize Klynd to use the credentials, tokens, wallet address, webhook secret, or imported files you provide to retrieve, receive, parse, store, and display historical trade-related data.</p>
        <p>You must use read-only credentials. You must not provide credentials that allow Klynd, or anyone with access to those credentials, to trade, withdraw, transfer, deposit, manage funds, or change account settings. Klynd is not responsible for losses caused by credentials you configure incorrectly, third-party platform bugs, broker API errors, unavailable integrations, stale data, duplicated data, missing data, or unauthorized access caused by your device or account security.</p>
        <p>Third-party brokers, exchanges, wallets, webhook sources, CSV formats, and blockchain networks are not controlled by Klynd. They may change, suspend, rate-limit, reject, corrupt, delay, or remove data at any time.</p>
        <p>Klynd makes no representations whatsoever about the accuracy, completeness, timeliness, or reliability of data returned by any third-party integration. Data displayed in Klynd from integrations may be incorrect, delayed, duplicated, missing, or otherwise unreliable. You must independently verify all integration data directly with your broker, exchange, or platform before relying on it for any purpose. Klynd bears no responsibility for errors, losses, tax miscalculations, prop-firm rule violations, or consequences of any kind arising from integration data.</p>
      </>
    ),
  },
  {
    title: '8. AI features',
    body: (
      <>
        <p>AI features are optional and may be limited to paid plans. They may generate text, summaries, reports, suggested tags, risk warnings, chat responses, or trading plans based on your own submitted or historical data.</p>
        <p>AI output may be inaccurate, incomplete, biased, inconsistent, outdated, or unsuitable for your situation. AI output is not financial advice, investment advice, risk advice, a trading signal, a promise of future performance, or a substitute for a qualified professional. You must independently verify all AI output before relying on it, and you must never make trades or financial decisions solely because of AI output.</p>
      </>
    ),
  },
  {
    title: '9. Subscriptions, purchases, and refunds',
    body: (
      <>
        <p>Paid features may be offered through Apple App Store, Google Play, RevenueCat, or other payment channels. Prices, billing periods, renewals, trials, cancellations, and refunds are controlled by the payment channel shown at purchase unless we state otherwise.</p>
        <p>You are responsible for cancelling subscriptions through the relevant app store or payment platform before renewal. Deleting the app or deleting your Klynd account may not automatically cancel an app store subscription. Refund requests are handled according to the relevant store or payment provider rules.</p>
      </>
    ),
  },
  {
    title: '10. Public profiles and leaderboard',
    body: (
      <>
        <p>If you enable public profile or leaderboard features, you authorize Klynd to display the information you choose to publish, which may include your handle, bio, account display name, trading statistics, and selected trade fields. Public information can be viewed, copied, indexed, screenshotted, linked, or shared by others.</p>
        <p>Klynd may remove, hide, rank, filter, or refuse public profile or leaderboard content at any time, including for abuse, manipulation, misleading results, technical issues, or legal risk.</p>
      </>
    ),
  },
  {
    title: '11. Referrals, affiliates, and payouts',
    body: (
      <>
        <p>Referral or affiliate features are optional promotional tools, not employment, partnership, agency, franchise, or investment arrangements. Commission rates, eligibility, payout thresholds, reward windows, bonus days, fraud rules, payout methods, and partner tiers may change, be corrected, or be discontinued.</p>
        <p><strong>Referral rewards are discretionary.</strong> Any referral reward (for example, a free month of a paid tier) is a discretionary goodwill gesture, not a contractual entitlement, a purchase, or a guaranteed benefit. Klynd is under no obligation to offer, grant, or honor any referral reward, and may decline, withhold, reduce, modify, suspend, cancel, or discontinue the referral program or any individual reward at any time, for any reason or for no reason, with or without notice. No reward is earned or owed unless and until Klynd actually credits it to your account, and receiving one reward does not entitle you to any future reward.</p>
        <p>You are responsible for taxes, declarations, and legal compliance related to any affiliate or referral income. Klynd may withhold, reverse, reject, delay, or cancel referral rewards for fraud, self-referrals, chargebacks, refunds, abuse, suspicious activity, duplicate accounts, policy violations, or legal concerns.</p>
        <p><strong>Prop firm information and purchase links.</strong> Klynd Prop and Klynd Academy may display prop-firm information, comparison content, vetted-firm notes, community discussion, or links to prop firms' own pages. Some links may be affiliate links from which Klynd may earn a commission at no extra cost to you. Such links, vetting language, comparison content, community discussion, or any commission do not constitute financial advice, legal advice, an endorsement, a guarantee of payout, a guarantee that a firm will remain solvent or reputable, or a recommendation that any firm is suitable for you. Firm selection is your decision, and any firm ratings or discussion may come from users, not from Klynd.</p>
      </>
    ),
  },
  {
    title: '12. Acceptable use',
    body: (
      <>
        <p>You must not use Klynd to:</p>
        <ul>
          <li>break the law, violate third-party rights, or breach broker, exchange, app store, or platform terms;</li>
          <li>upload malware, exploit code, stolen data, private keys, seed phrases, or unlawful content;</li>
          <li>attempt to bypass security, access another user's data, overload the service, scrape at scale, reverse engineer protected systems, or interfere with infrastructure;</li>
          <li>submit false, misleading, abusive, defamatory, discriminatory, or fraudulent content;</li>
          <li>manipulate referrals, leaderboards, public profiles, metrics, subscriptions, payouts, or app store systems;</li>
          <li>use Klynd to operate a regulated advisory, signal, brokerage, copy-trading, asset-management, or order-execution service without our written permission and all required legal authorizations.</li>
        </ul>
      </>
    ),
  },
  {
    title: '13. Your content and license to operate the service',
    body: (
      <>
        <p>You keep ownership of the data, notes, screenshots, prompts, profile text, and other content you submit to Klynd. You grant Klynd a limited, worldwide, non-exclusive license to host, store, copy, process, transmit, display, and analyze that content solely to operate, secure, support, and enforce the service. Klynd does not sell your content and does not use it to train AI models.</p>
        <p>You confirm that you have the rights and permissions needed to submit the content and that your content does not violate law or third-party rights.</p>
      </>
    ),
  },
  {
    title: '14. Klynd intellectual property',
    body: (
      <>
        <p>Klynd, the Klynd name, product names, logos, icons, designs, app screens, screenshots, screen recordings, videos, UI layouts, visual assets, course content, lesson text, quiz content, website, software, source code, databases, models of presentation, documentation, copy, and related materials are owned by or licensed to ANSY GAMES (SMC-PRIVATE) LIMITED and are protected by copyright, trade dress, database rights, unfair-competition laws, and other intellectual property laws where applicable.</p>
        <p>All rights not expressly granted to you in these Terms are reserved. Except for your own content, you may not copy, scrape, screenshot for reuse, reproduce, modify, crop, remove branding from, distribute, publish, sell, lease, sublicense, frame, mirror, reverse engineer, create derivative works from, train models on, use in marketing, use in competing products, or otherwise exploit any Klynd logo, app screen, screenshot, course material, UI, website content, or other Klynd material without the Company's prior written permission.</p>
        <p>No permission is granted to use Klynd materials in app store listings, ads, websites, social media, courses, videos, pitch decks, competitor products, templates, datasets, AI training sets, or promotional materials. Any unauthorized use may result in takedown requests, account or host notices, legal claims, and any other remedies available to the Company.</p>
      </>
    ),
  },
  {
    title: '15. Content reporting and intellectual property complaints',
    body: (
      <>
        <p>Klynd hosts user-generated content, including community posts, comments, market-context discussions, prop firm reviews, public profiles, and leaderboard entries. Klynd has no general obligation to monitor user content, but you can report content you believe is unlawful, infringing, defamatory, abusive, fraudulent, or in breach of these Terms by emailing <a href="mailto:support@klynd.trade">support@klynd.trade</a> with a link to or screenshot of the content, the reason for the report, and your contact details.</p>
        <p><strong>Copyright and other IP complaints.</strong> If you believe content on Klynd infringes your copyright, trademark, or other intellectual property rights, send a notice to <a href="mailto:support@klynd.trade">support@klynd.trade</a> that includes: (a) identification of the protected work and proof of your ownership or authority to act for the owner; (b) identification and location of the allegedly infringing content; (c) your name and contact details; (d) a good-faith statement that the use is not authorized by the rights owner, its agent, or the law; and (e) a statement, under penalty of perjury where applicable, that the information in the notice is accurate.</p>
        <p>We may remove, restrict, hide, or refuse to restore reported content at our sole discretion, may forward complaints (including your contact details) to the user who posted the content where appropriate or legally required, may accept or reject any counter-statement at our discretion, and may suspend or terminate accounts of repeat infringers. Submitting a report does not create any obligation on Klynd to act, and Klynd's decision on a report is final. Knowingly false or abusive reports may themselves violate these Terms and applicable law.</p>
      </>
    ),
  },
  {
    title: '16. Third-party services',
    body: (
      <p>Klynd depends on third-party services including infrastructure and database providers, AI processing providers, payment and subscription processors, app distribution platforms, push notification services, hosting and security services, email delivery services, broker and exchange APIs, wallet networks, and payout processors. Your use of those services may be subject to their own terms and privacy policies. Klynd is not responsible for third-party services, third-party outages, third-party data, or third-party decisions.</p>
    ),
  },
  {
    title: '17. Availability, changes, and shutdown',
    body: (
      <>
        <p><strong>Klynd may modify, suspend, restrict, or permanently discontinue the service, or any part of it, at any time, with or without notice and with or without cause, and has no liability to you or any third party for doing so.</strong> This includes free features, paid features, Academy lessons, quizzes, translations, course tracks, community features, market-context posts, prop-firm comparison content, integrations, AI models, public pages, affiliate tools, accounts, and pricing. We do not guarantee uninterrupted availability, error-free operation, data freshness, broker sync reliability, AI accuracy, course freshness, community moderation, content correctness, or compatibility with every device, region, broker, exchange, file, or app store.</p>
        <p>Klynd may discontinue the service entirely at any time, for any reason, including commercial, legal, technical, or personal reasons. On full discontinuation, your data may be permanently deleted. You are responsible for exporting your data before any shutdown occurs. Where reasonably practical, Klynd will endeavour to provide advance notice of a full service shutdown, but this is not guaranteed and no liability attaches to a failure to provide such notice.</p>
        <p>Klynd is not liable for any loss of data, loss of access, or any other consequence resulting from service changes, feature removal, or full discontinuation.</p>
      </>
    ),
  },
  {
    title: '18. Disclaimers',
    body: (
      <>
        <p>To the maximum extent permitted by law, Klynd is provided "as is" and "as available" without warranties of any kind, whether express, implied, statutory, or otherwise. We disclaim warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, availability, reliability, security, and that the service will meet your requirements.</p>
        <p>Academy lessons, quizzes, translations, examples, certificates, course tracks, community posts, market-context posts, prop-firm research, and any related content are provided for general education only. They may be inaccurate, incomplete, outdated, stale, unverified, unmoderated, user-generated, AI-assisted, not refreshed, or unsuitable for your situation. Klynd does not warrant or guarantee the correctness, freshness, completeness, legality, usefulness, safety, or suitability of any Academy or community content.</p>
        <p>No communication from Klynd, including support messages, app copy, Academy content, community content, market-context posts, AI output, public profiles, pricing pages, waitlist pages, or documentation, creates financial advice, investment advice, legal advice, tax advice, a fiduciary duty, a professional-client relationship, or an education credential.</p>
      </>
    ),
  },
  {
    title: '19. Limitation of liability',
    body: (
      <>
        <p><strong>No liability for the following.</strong> To the maximum extent permitted by law, the Company and its officers, directors, employees, agents, affiliates, successors, and assigns shall have <strong>no liability whatsoever</strong> to you, anyone claiming through you, or any third party for any of the following, however caused and on any theory of liability (contract, tort, statute, strict liability, or otherwise), and whether or not the Company has been advised of the possibility of such loss:</p>
        <ul>
          <li>any trading, investment, speculative, leveraged, or financial loss of any kind, including loss of capital, lost profits, missed opportunities, slippage, drawdown, margin calls, liquidations, or tax consequences;</li>
          <li>any decision you make, or refrain from making, based on anything displayed in Klynd, including journal entries, calendars, charts, statistics, AI output, public profiles, leaderboards, suggested tags, trading plans, weekly reports, risk warnings, or notifications;</li>
          <li>any decision you make, or refrain from making, based on Academy lessons, quizzes, translations, course progress, XP, streaks, certificates, examples, community posts, market-context discussions, prop-firm research, vetted-firm notes, affiliate links, or any educational content;</li>
          <li>any outdated, stale, incorrect, incomplete, unrefreshed, unreviewed, user-generated, AI-assisted, or unmoderated course, community, market, prop-firm, legal, tax, or educational content;</li>
          <li>any inaccuracy, omission, delay, duplication, corruption, loss, mis-classification, or unavailability of trade, broker, exchange, wallet, webhook, or CSV data, whether caused by us, a third party, or you;</li>
          <li>any AI-generated output, AI hallucination, fabricated information, biased response, outdated response, or your reliance on any AI feature;</li>
          <li>any account suspension, restriction, ban, drawdown breach, rule violation, payout denial, or other action by any broker, exchange, prop firm, wallet network, app store, or third-party platform;</li>
          <li>any data loss, including loss caused by account deletion, service shutdown, backup failure, sync error, retention expiry, export omission, device failure, or your own actions;</li>
          <li>any service interruption, downtime, latency, scheduled or unscheduled maintenance, feature removal, region restriction, or pricing change;</li>
          <li>any third-party service failure, outage, breach, change in terms, withdrawal of API access, or unilateral action;</li>
          <li>any unauthorized access to your account caused by your credentials, device, email, two-factor settings, or third-party platform security;</li>
          <li>any indirect, incidental, special, consequential, exemplary, or punitive damages, loss of goodwill, loss of revenue, loss of business, or business interruption of any kind.</li>
        </ul>
        <p><strong>Aggregate cap.</strong> If, despite the foregoing, the Company is held liable to you for any matter, the Company's total aggregate liability for all claims, of any kind, however arising, will be limited to the <em>lesser</em> of: (a) the amount you actually paid to the Company in the three (3) months immediately preceding the event giving rise to the first claim; or (b) USD 50. This cap applies in aggregate to all claims by you and anyone claiming through you, regardless of how many separate incidents, claims, or causes of action arise. The Company is not required to refund subscription fees or other amounts as a result of this section.</p>
        <p><strong>Construction.</strong> The limitations and exclusions in this section: (i) apply regardless of the legal theory; (ii) apply regardless of whether the Company was advised of, or should have known of, the possibility of such damages; (iii) apply even if any remedy fails of its essential purpose; (iv) reflect a reasonable allocation of risk between you and the Company; and (v) are an essential basis of the bargain - without them, the Company would not provide Klynd at the prices offered, or at all.</p>
        <p><strong>Mandatory law floor.</strong> Nothing in these Terms excludes or limits any liability that cannot lawfully be excluded or limited under the law of your country of residence, including (where applicable) liability for fraud, fraudulent misrepresentation, death or personal injury caused by negligence, or non-excludable consumer rights. Where mandatory law renders any provision of this section unenforceable as to a particular user, that provision applies to the maximum extent permitted by that law, and the remainder of these Terms continues in full force and effect as to that user and all other users.</p>
      </>
    ),
  },
  {
    title: '20. Indemnity',
    body: (
      <>
        <p>To the maximum extent permitted by law, you agree to defend, indemnify, and hold harmless the Company and its officers, directors, employees, agents, affiliates, successors, and assigns from and against any and all claims, demands, actions, proceedings, investigations, losses, liabilities, damages, judgments, settlements, fines, penalties, costs, and expenses (including reasonable legal fees and costs of enforcement) arising out of or in connection with:</p>
        <ul>
          <li>your access to or use of Klynd, whether or not authorised;</li>
          <li>your content, including notes, screenshots, AI prompts, profile text, public profile information, and any data you submit, sync, import, publish, or display through Klynd;</li>
          <li>your Academy activity or community content, including quiz answers, comments, posts, reactions, market-context discussion, prop-firm discussion, translations, profile information, or any educational content you submit, publish, rely on, or share;</li>
          <li>your trading activity, investment decisions, tax positions, financial decisions, or use of any information shown in Klynd;</li>
          <li>your integrations, broker connections, exchange connections, wallet addresses, webhooks, or CSV imports, including credentials you configure;</li>
          <li>your participation in public profiles, leaderboards, referrals, or affiliate programs;</li>
          <li>your breach of these Terms, the Privacy Policy, the Acceptable Use rules, or any representation or warranty you make to us;</li>
          <li>your violation of any law, regulation, court order, or order of any governmental or regulatory authority; or</li>
          <li>your infringement or misappropriation of any intellectual property, privacy, publicity, contractual, or other right of any third party.</li>
        </ul>
        <p>The Company may, at its option, assume the exclusive defense and control of any matter subject to indemnification by you, in which case you agree to cooperate fully with the Company's defense.</p>
        <p>If you are a consumer ordinarily resident in a jurisdiction whose mandatory law limits or prohibits indemnification by consumers, this section applies only to the maximum extent permitted by that law.</p>
      </>
    ),
  },
  {
    title: '21. Suspension and termination',
    body: (
      <p>We may suspend or terminate access, remove content, disable integrations, revoke public profile visibility, pause payouts, or close accounts if we believe you violated these terms, created legal risk, abused the service, compromised security, manipulated referrals or leaderboards, or used Klynd in a harmful way. You may stop using Klynd at any time and may delete your account through the app where available.</p>
    ),
  },
  {
    title: '22. Governing law and disputes',
    body: (
      <>
        <p><strong>Governing law.</strong> These Terms and any dispute, claim, or matter arising out of or in connection with them or their subject matter (including non-contractual disputes and claims) are governed by, and construed in accordance with, the laws of the Islamic Republic of Pakistan, without regard to conflict-of-law rules. The United Nations Convention on Contracts for the International Sale of Goods does not apply.</p>
        <p><strong>Exclusive jurisdiction.</strong> Subject to the consumer carve-out below, you and the Company irrevocably agree that the courts of Islamabad, Pakistan have exclusive jurisdiction to settle any dispute, claim, or matter arising out of or in connection with these Terms or your use of Klynd, whether in contract, tort, statute, or otherwise. You waive any objection to such venue on the basis of inconvenient forum, lack of personal jurisdiction, or any similar ground.</p>
        <p><strong>Consumer carve-out.</strong> If you are a consumer ordinarily resident in the European Economic Area, the United Kingdom, or another jurisdiction whose mandatory consumer protection laws apply to this contract notwithstanding the choice-of-law clause above, then: (a) you retain the mandatory protections granted to you by the law of your country of residence; (b) you may bring proceedings against the Company in the courts of your country of residence in addition to, or instead of, the courts of Islamabad, Pakistan; (c) the Company may bring proceedings against you only in the courts of your country of residence; and (d) nothing in these Terms is intended to deprive you of the protection of mandatory rules of law that would apply in the absence of the choice-of-law clause above.</p>
        <p><strong>EU online dispute resolution.</strong> If you are a consumer in the European Union, the European Commission provides an Online Dispute Resolution platform at <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr/</a>. The Company is not obliged to participate in alternative dispute resolution proceedings before any consumer arbitration body and does not commit to doing so.</p>
        <p><strong>Limitation period.</strong> To the maximum extent permitted by law, any claim or cause of action against the Company arising out of or related to Klynd or these Terms must be commenced within one (1) year after the cause of action accrues, or be permanently barred. This shorter limitation period does not apply to the extent prohibited by mandatory law in your country of residence.</p>
        <p><strong>Injunctive relief.</strong> Notwithstanding anything to the contrary, the Company may seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property, confidential information, or to prevent abuse of the service.</p>
        <p><strong>Class action waiver.</strong> To the maximum extent permitted by law, you and the Company agree that any dispute will be brought in an individual capacity only and not as a plaintiff or class member in any purported class, collective, consolidated, or representative proceeding. Where this waiver is unenforceable in your jurisdiction, this paragraph does not apply to you but the remainder of this section continues to apply.</p>
      </>
    ),
  },
  {
    title: '23. Changes to these terms',
    body: (
      <p>We may update these Terms at any time as Klynd changes, as laws change, or as the Company's operations change. If changes are material, we will take reasonable steps to notify you. Continued use of Klynd after changes become effective constitutes your acceptance of the updated Terms for all future use. If you do not accept the updated Terms, you must stop using Klynd.</p>
    ),
  },
  {
    title: '24. Contact',
    body: (
      <p>Questions about these terms can be sent to <a href="mailto:support@klynd.trade">support@klynd.trade</a> or <a href="mailto:info@klynd.trade">info@klynd.trade</a>.</p>
    ),
  },
  {
    title: '25. General',
    body: (
      <>
        <p><strong>Severability:</strong> If any provision of these terms is found invalid, illegal, or unenforceable by a court of competent jurisdiction, that provision will be modified to the minimum extent necessary to make it enforceable, or severed if modification is not possible. The remaining provisions continue in full force and effect.</p>
        <p><strong>Waiver:</strong> Klynd's failure or delay in enforcing any provision of these terms does not waive its right to enforce that provision or any other provision at any future time.</p>
        <p><strong>Entire agreement:</strong> These terms, together with the Privacy Policy and any plan-specific or feature-specific terms, constitute the entire agreement between you and Klynd regarding the service and supersede all prior agreements, representations, warranties, and understandings.</p>
        <p><strong>Force majeure:</strong> Klynd is not liable for any failure or delay in performance caused by circumstances beyond its reasonable control, including natural disasters, pandemics, war, civil unrest, cyberattacks, internet or infrastructure outages, third-party service failures, government actions, or regulatory changes.</p>
        <p><strong>Assignment:</strong> You may not assign or transfer your rights or obligations under these Terms, by operation of law or otherwise, without the Company's prior written consent. The Company may assign these Terms in whole or in part to a successor entity, affiliate, or any party, including in connection with a corporate restructuring, merger, acquisition, sale of assets, or transfer of the service, without your consent and without notice beyond what is required by applicable law.</p>
      </>
    ),
  },
];

function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
  const isPrivacy = kind === 'privacy';
  const sections = isPrivacy ? privacySections : termsSections;

  return (
    <section className="legal-section">
      <div className="legal-card">
        <p className="eyebrow">{isPrivacy ? 'Privacy' : 'Terms'}</p>
        <h1>{isPrivacy ? 'Privacy Policy' : 'Terms of Service'}</h1>
        <p className="legal-date">Last updated June 11, 2026</p>
        <p className="legal-intro">
          {isPrivacy
            ? 'This policy explains how Klynd processes personal data across the Klynd suite - the website, waitlist, Klynd Journal, Klynd Prop, and Klynd Academy mobile apps - operated by ANSY GAMES (SMC-PRIVATE) LIMITED.'
            : 'These terms set the legal rules for using Klynd, including the Klynd Journal, Klynd Prop, and Klynd Academy apps. Read them carefully, especially the trading-risk and no-advice sections.'}
        </p>
        {sections.map((section) => (
          <section className="legal-copy-block" key={section.title}>
            <h2>{section.title}</h2>
            {section.body}
          </section>
        ))}
      </div>
    </section>
  );
}

function DeleteAccountPage() {
  return (
    <section className="legal-section">
      <div className="legal-card">
        <p className="eyebrow">Account</p>
        <h1>Delete your Klynd account</h1>
        <p className="legal-date">Last updated June 7, 2026</p>
        <p className="legal-intro">
          You can permanently delete your Klynd account and all associated data at any time. This page explains how to do it, what gets deleted, and what is retained.
        </p>

        <section className="legal-copy-block">
          <h2>Delete from inside the app (recommended)</h2>
          <p>This is the fastest and most secure way. Open your Klynd app - Klynd Journal, Klynd Prop, or Klynd Academy - on your phone, then:</p>
          <p>
            <strong>Settings → Account → Delete Account → Confirm</strong>
          </p>
          <p>
            Your account, profile, trades, journal entries, screenshots, AI history, broker connections, tags, weekly reports, trading plans, Academy progress, quiz answers, community activity, referrals, and all other personal data are erased immediately from our database. The action is permanent and cannot be undone.
          </p>
        </section>

        <section className="legal-copy-block">
          <h2>Delete by email (if you can't access the app)</h2>
          <p>If you've lost access to your account, email us from the address you used to sign up:</p>
          <p>
            <strong><a href="mailto:support@klynd.trade?subject=Delete%20my%20Klynd%20account">support@klynd.trade</a></strong>
          </p>
          <p>Subject line: <em>Delete my Klynd account</em></p>
          <p>
            Include the email address associated with your Klynd account. We will verify ownership, complete the deletion within 7 business days, and reply to confirm. There is no charge for deletion.
          </p>
        </section>

        <section className="legal-copy-block">
          <h2>What gets deleted</h2>
          <p>The following data is permanently and irreversibly erased:</p>
          <p>
            • Your account credentials and profile<br />
            • All logged trades, accounts, and trade tags<br />
            • Prop accounts, rule history, payouts, and firm reviews you submitted<br />
            • Academy progress, quiz answers, XP, streaks, language settings, and community activity<br />
            • Journal entries, notes, and screenshots<br />
            • Broker and exchange connection credentials<br />
            • AI usage history, weekly reports, and trading plans<br />
            • Referral codes, affiliate data, and any pending payouts<br />
            • Push notification tokens and device identifiers
          </p>
        </section>

        <section className="legal-copy-block">
          <h2>What is retained, and for how long</h2>
          <p>
            For legal, tax, fraud-prevention, and accounting reasons, we retain a minimal record of paid subscription transactions and refunds. This is limited to: the transaction ID issued by Apple or Google, the date, the amount, and the product purchased. It does not include your email, name, trade data, or any personally identifying content from inside the app.
          </p>
          <p>
            Retention period: up to 7 years from the date of the last transaction, as required by Pakistan tax law and applicable consumer-protection regulations.
          </p>
          <p>
            If you never made a paid purchase, no transactional records are retained.
          </p>
        </section>

        <section className="legal-copy-block">
          <h2>Active subscriptions</h2>
          <p>
            Deleting your Klynd account does <strong>not</strong> automatically cancel an active App Store or Google Play subscription. You must cancel the subscription separately from your device's subscription settings, or it will continue to bill.
          </p>
          <p>
            • <strong>iOS:</strong> Settings → [your name] → Subscriptions → Klynd → Cancel<br />
            • <strong>Android:</strong> Play Store → Profile → Payments &amp; subscriptions → Subscriptions → Klynd → Cancel
          </p>
        </section>

        <section className="legal-copy-block">
          <h2>Operator</h2>
          <p>
            Klynd is operated by ANSY GAMES (SMC-PRIVATE) LIMITED, registered in Pakistan. For any privacy or deletion question outside the channels above, see our <a href="/privacy">Privacy Policy</a>.
          </p>
        </section>
      </div>
    </section>
  );
}

function ConfirmedPage() {
  // Supabase has already marked the email verified server-side by the time it
  // redirects here (the /auth/v1/verify step does that before the redirect).
  // So there's nothing to exchange - we just reassure the user and offer a
  // deep link back into the app. The klynd:// scheme opens the app when it's
  // installed; if it isn't (e.g. they confirmed on a desktop), the copy tells
  // them to open the app on their phone and sign in.
  return (
    <section className="route-section">
      <div className="route-card">
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(0, 214, 143, 0.12)',
            border: '1px solid rgba(0, 214, 143, 0.4)',
          }}
        >
          <Check size={30} color="#00D68F" />
        </div>
        <p className="eyebrow">Email confirmed</p>
        <h1>You're all set.</h1>
        <p>
          Your email is verified. Head back to the Klynd app and sign in with
          your email and password.
        </p>
        <a className="btn-primary-lg" href="klynd://">
          Open Klynd <ArrowRight size={15} />
        </a>
      </div>
    </section>
  );
}

function AcademyConfirmedPage() {
  // Mirrors ConfirmedPage, but deep-links into the Klynd Academy app
  // (klyndacademy:// scheme) instead of Klynd Journal (klynd://). Supabase has
  // already verified the email server-side before redirecting here, so there's
  // nothing to exchange — we reassure the user and offer the deep link.
  return (
    <section className="route-section">
      <div className="route-card">
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(0, 214, 143, 0.12)',
            border: '1px solid rgba(0, 214, 143, 0.4)',
          }}
        >
          <Check size={30} color="#00D68F" />
        </div>
        <p className="eyebrow">Email confirmed</p>
        <h1>You're all set.</h1>
        <p>
          Your email is verified. Head back to the Klynd Academy app and sign in
          with your email and password.
        </p>
        <a className="btn-primary-lg" href="klyndacademy://">
          Open Klynd Academy <ArrowRight size={15} />
        </a>
      </div>
    </section>
  );
}

function PropConfirmedPage() {
  // Mirrors ConfirmedPage, but deep-links into the Klynd Prop app
  // (klyndprop:// scheme) instead of Klynd Journal (klynd://). Supabase has
  // already verified the email server-side before redirecting here, so there's
  // nothing to exchange — we reassure the user and offer the deep link.
  return (
    <section className="route-section">
      <div className="route-card">
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(0, 214, 143, 0.12)',
            border: '1px solid rgba(0, 214, 143, 0.4)',
          }}
        >
          <Check size={30} color="#00D68F" />
        </div>
        <p className="eyebrow">Email confirmed</p>
        <h1>You're all set.</h1>
        <p>
          Your email is verified. Head back to the Klynd Prop app and sign in
          with your email and password.
        </p>
        <a className="btn-primary-lg" href="klyndprop://">
          Open Klynd Prop <ArrowRight size={15} />
        </a>
      </div>
    </section>
  );
}

function ReferralPage({ code }: { code: string }) {
  return (
    <section className="route-section">
      <div className="route-card">
        <p className="eyebrow">Referral</p>
        <h1>{code ? `${code.toUpperCase()} invited you.` : 'You were invited to Klynd.'}</h1>
        <p>Start building your trading feedback loop today.</p>
        <a className="btn-primary-lg" href="/download">Download Klynd <ArrowRight size={15}/></a>
      </div>
    </section>
  );
}

function ProfilePage({ handle }: { handle: string }) {
  return (
    <section className="route-section">
      <div className="route-card">
        <p className="eyebrow">Profile</p>
        <h1>{handle ? `@${handle}` : 'Public profiles coming.'}</h1>
        <p>Traders will soon be able to share stats and milestones publicly.</p>
        <a className="btn-ghost-lg" href="/">← Back to Klynd</a>
      </div>
    </section>
  );
}

// ─── footer ───────────────────────────────────────────────────────────────────

function Footer({ onOpenCookieSettings }: { onOpenCookieSettings: () => void }) {
  return (
    <footer className="footer">
      <div className="footer-glow" aria-hidden="true" />
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand-block">
            <a className="brand" href="/" aria-label="Klynd home">
              <img src="/logo.svg" alt="Klynd" className="brand-logo" />
              <span className="brand-name">Klynd</span>
            </a>
            <p className="footer-tag">AI-powered tools and free education for serious traders - learn the craft, protect the account, and study every trade.</p>
            <a className="footer-cta" href="/download">
              Download the apps
            </a>
          </div>

          <div className="footer-cols">
            <div className="footer-col">
              <p className="footer-col-title">Products</p>
              <a href="/journal">Klynd Journal</a>
              <a href="/prop">Klynd Prop</a>
              <a href="/academy">Klynd Academy</a>
              <a href="/prop-firms">Prop firms</a>
              <a href="/pricing">Pricing</a>
            </div>
            <div className="footer-col">
              <p className="footer-col-title">Company</p>
              <a href="mailto:support@klynd.trade">Contact</a>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <button className="footer-link-btn" type="button" onClick={onOpenCookieSettings}>Cookie settings</button>
              <a href="/delete-account">Delete account</a>
            </div>
            <div className="footer-col">
              <p className="footer-col-title">Follow</p>
              <a
                className="footer-social"
                href="https://instagram.com/klynd.trade"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Klynd on Instagram"
              >
                <LogoInstagram size={16} />
                <span>klynd.trade</span>
              </a>
              <a
                className="footer-social"
                href="https://twitter.com/klyndtrade"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Klynd on X (Twitter)"
              >
                <LogoTwitterX size={14} />
                <span>@klyndtrade</span>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-divider" aria-hidden="true" />

        <div className="footer-bottom">
          <span className="copyright">© 2026 ANSY GAMES (SMC-PRIVATE) LIMITED. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

function LogoInstagram({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="17.6" cy="6.4" r="1.15" fill="currentColor" />
    </svg>
  );
}

function LogoTwitterX({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

// ─── broker icons - real brand SVGs ──────────────────────────────────────────

// Binance - Simple Icons (CC0), viewBox 0 0 24 24
function BrokerBinance({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="9" fill="#F0B90B"/>
      <g transform="translate(8 8)">
        <path
          fill="#1E2026"
          d="M16.624 13.9202l2.7175 2.7154-7.353 7.353-7.353-7.352 2.7175-2.7164 4.6355 4.6595 4.6355-4.6595zm4.6366-4.0534 2.7175 2.7154-2.7175 2.7154-2.7175-2.7154 2.7175-2.7154zm-9.272 0l2.7175 2.7154-2.7175 2.7154-2.7175-2.7154 2.7175-2.7154zM12 0l7.353 7.352-2.7175 2.7164L12 5.3916 7.3643 10.0684 4.6468 7.352 12 0zm-9.2715 9.8668l2.7175 2.7154-2.7175 2.7154-2.7175-2.7154 2.7175-2.7154z"
        />
      </g>
    </svg>
  );
}

// Bybit - brand color #F7A600, geometric diamond mark
function BrokerBybit({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="9" fill="#1A1A2E"/>
      {/* Diamond outline */}
      <path d="M20 8l10 12-10 12L10 20z" fill="#F7A600" opacity="0.15"/>
      <path d="M20 8l10 12-10 12L10 20z" stroke="#F7A600" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
      {/* Inner diamond */}
      <path d="M20 13l5 7-5 7-5-7z" fill="#F7A600"/>
    </svg>
  );
}

// MetaTrader 5 - MetaQuotes blue, typographic mark
function BrokerMT5({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="9" fill="#1460AC"/>
      <text
        x="20" y="25"
        textAnchor="middle"
        fill="white"
        fontSize="12"
        fontWeight="800"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif"
        letterSpacing="-0.8"
      >MT5</text>
    </svg>
  );
}

// NinjaTrader - dark navy, bold N mark
function BrokerNinja({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="9" fill="#003F6F"/>
      <path
        d="M12 27V13l16 14V13"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// CSV / generic import - clean file icon
function BrokerCSV({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="9" fill="#1c1c1e"/>
      <path d="M13 10h9l7 7v17a1 1 0 01-1 1H12a1 1 0 01-1-1V11a1 1 0 011-1z" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="rgba(255,255,255,0.04)"/>
      <path d="M22 10v7h7" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none"/>
      <path d="M15 22h10M15 26h7" stroke="rgba(255,255,255,0.22)" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

export default App;
